import "@dxos/node-std/globals";
import {
  createCollectDiagnosticsBroadcastSender
} from "./chunk-XJRPB3GA.mjs";
import {
  __export,
  __reExport
} from "./chunk-QCWEHHJW.mjs";

// src/packlets/devtools/feeds.ts
import { SubscriptionList } from "@dxos/async";
import { Stream } from "@dxos/codec-protobuf/stream";
import { FeedIterator } from "@dxos/feed-store";
import { PublicKey } from "@dxos/keys";
import { log } from "@dxos/log";
import { ComplexMap } from "@dxos/util";
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/devtools/feeds.ts";
var subscribeToFeeds = ({ feedStore, spaceManager }, { feedKeys }) => {
  return new Stream(({ next }) => {
    const subscriptions = new SubscriptionList();
    const feedMap = new ComplexMap(PublicKey.hash);
    const update = () => {
      const { feeds } = feedStore;
      feeds.filter((feed) => !feedKeys?.length || feedKeys.some((feedKey) => feedKey.equals(feed.key))).forEach((feed) => {
        if (!feedMap.has(feed.key)) {
          feedMap.set(feed.key, {
            feed
          });
          feed.on("close", update);
          subscriptions.add(() => feed.off("close", update));
        }
        if (!feedMap.get(feed.key)?.owner) {
          feedMap.get(feed.key).owner = findFeedOwner(spaceManager, feed.key);
        }
      });
      next({
        feeds: Array.from(feedMap.values()).map(({ feed, owner }) => ({
          feedKey: feed.key,
          length: feed.properties.length,
          bytes: feed.core.byteLength,
          downloaded: feed.core.bitfield?.data.toBuffer() ?? new Uint8Array(),
          owner
        }))
      });
    };
    subscriptions.add(feedStore.feedOpened.on(update));
    update();
    return () => {
      subscriptions.clear();
    };
  });
};
var findFeedOwner = (spaceManager, feedKey) => {
  const feedInfo = [
    ...spaceManager.spaces.values()
  ].flatMap((space) => [
    ...space.spaceState.feeds.values()
  ]).find((feed) => feed.key.equals(feedKey));
  log("feeds", {
    feedInfo,
    key: feedKey.truncate(),
    allSpaces: spaceManager.spaces.size
  }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 51, S: void 0 });
  if (!feedInfo) {
    return void 0;
  }
  return {
    identity: feedInfo.assertion.identityKey,
    device: feedInfo.assertion.deviceKey
  };
};
var subscribeToFeedBlocks = ({ feedStore }, { feedKey, maxBlocks = 10 }) => {
  return new Stream(({ next }) => {
    if (!feedKey) {
      return;
    }
    const subscriptions = new SubscriptionList();
    const timeout = setTimeout(async () => {
      const feed = feedStore.getFeed(feedKey);
      if (!feed) {
        return;
      }
      const update = async () => {
        if (!feed.properties.length) {
          next({
            blocks: []
          });
          return;
        }
        const iterator = new FeedIterator(feed);
        await iterator.open();
        const blocks = [];
        for await (const block of iterator) {
          blocks.push(block);
          if (blocks.length >= feed.properties.length) {
            break;
          }
        }
        next({
          blocks: blocks.slice(-maxBlocks)
        });
        await iterator.close();
      };
      feed.on("append", update);
      subscriptions.add(() => feed.off("append", update));
      feed.on("truncate", update);
      subscriptions.add(() => feed.off("truncate", update));
      await update();
    });
    return () => {
      subscriptions.clear();
      clearTimeout(timeout);
    };
  });
};

// src/packlets/devtools/network.ts
import { Stream as Stream2 } from "@dxos/codec-protobuf/stream";
import { Context } from "@dxos/context";
import { PublicKey as PublicKey2 } from "@dxos/keys";
var __dxlog_file2 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/devtools/network.ts";
var subscribeToNetworkStatus = ({ signalManager }) => new Stream2(({ next, close }) => {
  const update = () => {
    try {
      const status = signalManager.getStatus?.();
      next({
        servers: status
      });
    } catch (err) {
      close(err);
    }
  };
  signalManager.statusChanged?.on(() => update());
  update();
});
var subscribeToSignal = ({ signalManager }) => new Stream2(({ next }) => {
  const ctx = new Context(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file2, L: 22 });
  signalManager.onMessage.on(ctx, (message) => {
    next({
      message: {
        author: PublicKey2.from(message.author.peerKey).asUint8Array(),
        recipient: PublicKey2.from(message.recipient.peerKey).asUint8Array(),
        payload: message.payload
      },
      receivedAt: /* @__PURE__ */ new Date()
    });
  });
  signalManager.swarmEvent.on(ctx, (swarmEvent) => {
    next({
      swarmEvent: swarmEvent.peerAvailable ? {
        peerAvailable: {
          peer: PublicKey2.from(swarmEvent.peerAvailable.peer.peerKey).asUint8Array(),
          since: swarmEvent.peerAvailable.since
        }
      } : {
        peerLeft: {
          peer: PublicKey2.from(swarmEvent.peerLeft.peer.peerKey).asUint8Array()
        }
      },
      topic: swarmEvent.topic.asUint8Array(),
      receivedAt: /* @__PURE__ */ new Date()
    });
  });
  return () => {
    return ctx.dispose();
  };
});
var subscribeToNetworkTopics = ({ networkManager }) => new Stream2(({ next, close }) => {
  const update = () => {
    try {
      const topics = networkManager.topics;
      const labeledTopics = topics.map((topic) => ({
        topic,
        label: networkManager.getSwarm(topic)?.label ?? topic.toHex()
      }));
      next({
        topics: labeledTopics
      });
    } catch (err) {
      close(err);
    }
  };
  networkManager.topicsUpdated.on(update);
  update();
});
var subscribeToSwarmInfo = ({ networkManager }) => new Stream2(({ next }) => {
  const update = () => {
    const info = networkManager.connectionLog?.swarms;
    if (info) {
      next({
        data: info
      });
    }
  };
  networkManager.connectionLog?.update.on(update);
  update();
});
var getNetworkPeers = ({ networkManager }, request) => {
  if (!request.topic) {
    throw new Error("Expected a network topic");
  }
  const map = networkManager.getSwarmMap(PublicKey2.from(request.topic));
  return {
    peers: map?.peers.map((peer) => ({
      ...peer,
      connections: peer.connections.map((connection) => connection.asUint8Array())
    }))
  };
};

// src/packlets/devtools/spaces.ts
import { Stream as Stream3 } from "@dxos/codec-protobuf/stream";
var subscribeToSpaces = (context, { spaceKeys = [] }) => {
  return new Stream3(({ next }) => {
    let unsubscribe;
    const update = async () => {
      const spaces = [
        ...context.spaceManager.spaces.values()
      ];
      const filteredSpaces = spaces.filter((space) => !spaceKeys?.length || spaceKeys.some((spaceKey) => spaceKey.equals(space.key)));
      next({
        spaces: filteredSpaces.map((space) => {
          const spaceMetadata = context.metadataStore.spaces.find((spaceMetadata2) => spaceMetadata2.key.equals(space.key));
          return {
            key: space.key,
            isOpen: space.isOpen,
            timeframe: spaceMetadata?.dataTimeframe,
            genesisFeed: space.genesisFeedKey,
            controlFeed: space.controlFeedKey,
            dataFeed: space.dataFeedKey
          };
        })
      });
    };
    const timeout = setTimeout(async () => {
      await context.initialized.wait();
      unsubscribe = context.dataSpaceManager.updated.on(() => update());
      await update();
    });
    return () => {
      unsubscribe?.();
      clearTimeout(timeout);
    };
  });
};

// src/packlets/devtools/devtools.ts
import { Event as AsyncEvent } from "@dxos/async";
import { Stream as Stream6 } from "@dxos/codec-protobuf/stream";

// src/packlets/devtools/keys.ts
import { scheduleTask } from "@dxos/async";
import { Stream as Stream4 } from "@dxos/codec-protobuf/stream";
var subscribeToKeyringKeys = ({ keyring }) => new Stream4(({ next, ctx }) => {
  const update = async () => {
    next({
      keys: await keyring.list()
    });
  };
  keyring.keysUpdate.on(ctx, update);
  scheduleTask(ctx, update);
});

// src/packlets/devtools/metadata.ts
import { Stream as Stream5 } from "@dxos/codec-protobuf/stream";
var subscribeToMetadata = ({ context }) => new Stream5(({ next, ctx }) => {
  context.metadataStore.update.on(ctx, (data) => next({
    metadata: data
  }));
  next({
    metadata: context.metadataStore.metadata
  });
});

// src/packlets/devtools/devtools.ts
var DevtoolsHostEvents = class {
  ready = new AsyncEvent();
};
var DevtoolsServiceImpl = class {
  params;
  constructor(params) {
    this.params = params;
  }
  events(_request) {
    return new Stream6(({ next }) => {
      this.params.events.ready.on(() => {
        next({
          ready: {}
        });
      });
    });
  }
  async getConfig(_request) {
    return {
      config: JSON.stringify(this.params.config.values)
    };
  }
  async getStorageInfo() {
    const navigatorInfo = typeof navigator === "object" ? await navigator.storage.estimate() : void 0;
    return {
      type: "sqlite",
      storageUsage: navigatorInfo?.usage ?? 0,
      originUsage: navigatorInfo?.usage ?? 0,
      usageQuota: navigatorInfo?.quota ?? 0
    };
  }
  async getBlobs() {
    return {
      blobs: await this.params.context.blobStore.list()
    };
  }
  async getSnapshots() {
    return {
      snapshots: []
    };
  }
  resetStorage(_request) {
    throw new Error();
  }
  enableDebugLogging(_request) {
    throw new Error();
  }
  disableDebugLogging(_request) {
    throw new Error();
  }
  subscribeToKeyringKeys(_request) {
    return subscribeToKeyringKeys({
      keyring: this.params.context.keyring
    });
  }
  subscribeToCredentialMessages(_request) {
    throw new Error();
  }
  subscribeToSpaces(_request) {
    return subscribeToSpaces(this.params.context, _request);
  }
  subscribeToItems(_request) {
    throw new Error();
  }
  subscribeToFeeds(_request) {
    return subscribeToFeeds(this.params.context, _request);
  }
  subscribeToFeedBlocks(_request) {
    return subscribeToFeedBlocks({
      feedStore: this.params.context.feedStore
    }, _request);
  }
  getSpaceSnapshot(_request) {
    throw new Error();
  }
  saveSpaceSnapshot(_request) {
    throw new Error();
  }
  clearSnapshots(_request) {
    throw new Error();
  }
  getNetworkPeers(_request) {
    throw new Error();
  }
  subscribeToNetworkTopics(_request) {
    throw new Error();
  }
  subscribeToSignalStatus(_request) {
    return subscribeToNetworkStatus({
      signalManager: this.params.context.signalManager
    });
  }
  subscribeToSignal() {
    return subscribeToSignal({
      signalManager: this.params.context.signalManager
    });
  }
  subscribeToSwarmInfo() {
    return subscribeToSwarmInfo({
      networkManager: this.params.context.networkManager
    });
  }
  subscribeToMetadata() {
    return subscribeToMetadata({
      context: this.params.context
    });
  }
  async exportSqliteDatabase() {
    return {
      data: await this.params.exportSqliteDatabase()
    };
  }
  async runSqliteQuery(request) {
    try {
      const parsedParams = request.params ? JSON.parse(request.params) : void 0;
      if (parsedParams !== void 0 && !Array.isArray(parsedParams)) {
        throw new Error("Query params must be a JSON array.");
      }
      const rows = await this.params.runSqliteQuery(request.query, parsedParams);
      return {
        rows: JSON.stringify(rows)
      };
    } catch (err) {
      return {
        rows: "[]",
        error: err instanceof Error ? err.message : String(err)
      };
    }
  }
};

// src/packlets/diagnostics/diagnostics.ts
import { asyncTimeout } from "@dxos/async";
import { getFirstStreamValue } from "@dxos/codec-protobuf";
import { createDidFromIdentityKey, credentialTypeFilter } from "@dxos/credentials";
import { invariant } from "@dxos/invariant";
import { STORAGE_VERSION } from "@dxos/protocols";
import { SpaceMember } from "@dxos/protocols/proto/dxos/client/services";
import { TRACE_PROCESSOR } from "@dxos/tracing";

// src/version.ts
var DXOS_VERSION = "0.10.0";

// src/packlets/services/platform.ts
import { Platform } from "@dxos/protocols/proto/dxos/client/services";
var getPlatform = () => {
  if (process.browser) {
    if (typeof window !== "undefined") {
      const { userAgent } = window.navigator;
      return {
        type: Platform.PLATFORM_TYPE.BROWSER,
        userAgent,
        uptime: Math.floor((Date.now() - window.performance.timeOrigin) / 1e3)
      };
    } else if (typeof SharedWorkerGlobalScope !== "undefined") {
      return {
        type: Platform.PLATFORM_TYPE.SHARED_WORKER,
        uptime: Math.floor((Date.now() - performance.timeOrigin) / 1e3)
      };
    } else {
      return {
        type: Platform.PLATFORM_TYPE.DEDICATED_WORKER,
        uptime: Math.floor((Date.now() - performance.timeOrigin) / 1e3)
      };
    }
  } else {
    const { platform: platform2, version, arch } = process;
    return {
      type: Platform.PLATFORM_TYPE.NODE,
      platform: platform2,
      arch,
      runtime: version,
      uptime: Math.floor(process.uptime()),
      memory: process.memoryUsage()
    };
  }
};

// src/packlets/diagnostics/diagnostics.ts
var __dxlog_file3 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/diagnostics/diagnostics.ts";
var DEFAULT_TIMEOUT = 1e3;
var createDiagnostics = async (clientServices, serviceContext, config) => {
  const diagnostics = {
    created: (/* @__PURE__ */ new Date()).toISOString(),
    platform: getPlatform(),
    client: {
      version: DXOS_VERSION,
      storage: {
        version: STORAGE_VERSION
      }
    },
    trace: TRACE_PROCESSOR.getDiagnostics()
  };
  await Promise.all([
    (async () => {
      invariant(clientServices.LoggingService, "SystemService is not available.", { "~LogMeta": "~LogMeta", F: __dxlog_file3, L: 32, S: void 0, A: ["clientServices.LoggingService", "'SystemService is not available.'"] });
      diagnostics.metrics = await getFirstStreamValue(clientServices.LoggingService.queryMetrics({}), {
        timeout: DEFAULT_TIMEOUT
      }).catch(() => void 0);
    })(),
    (async () => {
      diagnostics.storage = await asyncTimeout(getStorageDiagnostics(), DEFAULT_TIMEOUT).catch(() => void 0);
    })(),
    async () => {
      const identity = serviceContext.identityManager.identity;
      if (identity) {
        diagnostics.identity = {
          did: identity.did,
          identityKey: identity.identityKey,
          spaceKey: identity.space.key,
          profile: identity.profileDocument
        };
        const { devices } = await getFirstStreamValue(clientServices.DevicesService.queryDevices(), {
          timeout: DEFAULT_TIMEOUT
        }).catch(() => void 0) ?? {};
        diagnostics.devices = devices;
        if (serviceContext.dataSpaceManager) {
          diagnostics.spaces = await Promise.all(Array.from(serviceContext.dataSpaceManager.spaces.values()).map((space) => getSpaceStats(space)) ?? []);
        }
        const { feeds = [] } = await getFirstStreamValue(clientServices.DevtoolsHost.subscribeToFeeds({}), {
          timeout: DEFAULT_TIMEOUT
        }).catch(() => void 0) ?? {};
        diagnostics.feeds = feeds.map(({ feedKey, bytes, length }) => ({
          feedKey,
          bytes,
          length
        }));
        const status = await getFirstStreamValue(clientServices.NetworkService.queryStatus(), {
          timeout: DEFAULT_TIMEOUT
        }).catch(() => void 0);
        diagnostics.networkStatus = status;
        diagnostics.swarms = serviceContext.networkManager.connectionLog?.swarms;
      }
    }
  ]);
  diagnostics.config = config.values;
  return diagnostics;
};
var getSpaceStats = async (space) => {
  const stats = {
    key: space.key,
    metrics: space.metrics,
    epochs: space.inner.spaceState.credentials.filter(credentialTypeFilter("dxos.halo.credentials.Epoch")).map((credential) => ({
      ...credential.subject.assertion,
      id: credential.id
    })),
    members: await Promise.all(Array.from(space.inner.spaceState.members.values()).map(async (member) => ({
      role: member.role,
      identity: {
        did: await createDidFromIdentityKey(member.key),
        identityKey: member.key,
        profile: {
          displayName: member.assertion.profile?.displayName
        }
      },
      presence: space.presence.getPeersOnline().filter(({ identityKey }) => identityKey.equals(member.key)).length > 0 ? SpaceMember.PresenceState.ONLINE : SpaceMember.PresenceState.OFFLINE
    }))),
    pipeline: {
      // TODO(burdon): Pick properties from credentials if needed.
      currentEpoch: space.automergeSpaceState.lastEpoch,
      appliedEpoch: space.automergeSpaceState.lastEpoch,
      controlFeeds: space.inner.controlPipeline.state.feeds.map((feed) => feed.key),
      currentControlTimeframe: space.inner.controlPipeline.state.timeframe,
      targetControlTimeframe: space.inner.controlPipeline.state.targetTimeframe,
      totalControlTimeframe: space.inner.controlPipeline.state.endTimeframe
    }
  };
  if (stats.metrics) {
    const { open, ready } = stats.metrics;
    stats.metrics.startupTime = open && ready && ready.getTime() - open.getTime();
  }
  return stats;
};
var getStorageDiagnostics = async () => {
  if (typeof navigator === "undefined" || !navigator.storage) {
    return void 0;
  }
  const map = /* @__PURE__ */ new Map();
  const dir = await navigator.storage.getDirectory();
  for await (const filename of dir?.keys()) {
    const idx = filename.indexOf("-", filename.indexOf("-") + 1);
    if (idx === -1) {
      continue;
    }
    map.set(filename.slice(0, idx), (map.get(filename.slice(0, idx)) ?? 0) + 1);
  }
  return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).map(([file, count]) => ({
    file,
    count
  }));
};

// src/packlets/diagnostics/diagnostics-collector.ts
import { ClientServicesProviderResource } from "@dxos/client-protocol";
import { ConfigResource } from "@dxos/config";
import { GetDiagnosticsRequest } from "@dxos/protocols/proto/dxos/client/services";
import { TRACE_PROCESSOR as TRACE_PROCESSOR2 } from "@dxos/tracing";
import { isNonNullable, jsonKeyReplacer } from "@dxos/util";
var GET_DIAGNOSTICS_RPC_TIMEOUT = 1e4;
var DiagnosticsCollector = class {
  static broadcastSender = createCollectDiagnosticsBroadcastSender();
  static async collect(config = findConfigs(), services = findSystemServiceProvider(), options = {}) {
    const serviceDiagnostics = await services?.services?.SystemService?.getDiagnostics({
      keys: options.humanize ? GetDiagnosticsRequest.KEY_OPTION.HUMANIZE : options.truncate ? GetDiagnosticsRequest.KEY_OPTION.TRUNCATE : void 0
    }, {
      timeout: GET_DIAGNOSTICS_RPC_TIMEOUT
    });
    const clientDiagnostics = {
      config,
      trace: TRACE_PROCESSOR2.getDiagnostics()
    };
    const diagnostics = serviceDiagnostics != null ? {
      client: clientDiagnostics,
      services: serviceDiagnostics
    } : {
      client: clientDiagnostics,
      broadcast: await this.broadcastSender.broadcastDiagnosticsRequest()
    };
    return JSON.parse(JSON.stringify(diagnostics, jsonKeyReplacer(options)));
  }
};
var findSystemServiceProvider = () => {
  const serviceProviders = TRACE_PROCESSOR2.findResourcesByAnnotation(ClientServicesProviderResource);
  const providerResource = serviceProviders.find((r) => r.instance.deref()?.services?.SystemService != null);
  return providerResource?.instance?.deref() ?? null;
};
var findConfigs = () => {
  const configs = TRACE_PROCESSOR2.findResourcesByAnnotation(ConfigResource);
  return configs.map((r) => r.instance.deref()).filter(isNonNullable);
};

// src/packlets/diagnostics/index.ts
var diagnostics_exports = {};
__export(diagnostics_exports, {
  DiagnosticsCollector: () => DiagnosticsCollector,
  createDiagnostics: () => createDiagnostics
});
__reExport(diagnostics_exports, diagnostics_broadcast_star);
import * as diagnostics_broadcast_star from "#diagnostics-broadcast";

// src/packlets/identity/authenticator.ts
import { Trigger } from "@dxos/async";
import { Context as Context2 } from "@dxos/context";
import { verifyCredential } from "@dxos/credentials";
import { log as log2 } from "@dxos/log";
import { schema } from "@dxos/protocols/proto";
var __dxlog_file4 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/identity/authenticator.ts";
var Credential = schema.getCodecForType("dxos.halo.credentials.Credential");
var createAuthProvider = (signer) => async (nonce) => {
  const credential = await signer.createCredential({
    assertion: {
      "@type": "dxos.halo.credentials.Auth"
    },
    subject: signer.getIssuer(),
    nonce
  });
  return Credential.encode(credential);
};
var TrustedKeySetAuthVerifier = class {
  _params;
  _ctx = new Context2(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file4, L: 25 });
  constructor(_params) {
    this._params = _params;
  }
  async close() {
    await this._ctx.dispose();
  }
  get verifier() {
    return async (nonce, auth) => {
      const credential = Credential.decode(auth);
      log2("authenticating...", {
        credential
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file4, L: 35, S: this });
      const result = await verifyCredential(credential);
      if (result.kind === "fail") {
        log2("Invalid credential", {
          result
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file4, L: 40, S: this });
        return false;
      }
      if (!credential.proof.nonce || !Buffer.from(nonce).equals(credential.proof.nonce)) {
        log2("Invalid nonce", {
          nonce,
          credential
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file4, L: 46, S: this });
        return false;
      }
      if (this._isTrustedKey(credential.issuer)) {
        log2("key is trusted -- auth success", {
          key: credential.issuer
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file4, L: 53, S: this });
        return true;
      }
      const trigger = new Trigger();
      this._ctx.onDispose(() => {
        trigger.wake(false);
      });
      const clear = this._params.update.on(this._ctx, () => {
        if (this._isTrustedKey(credential.issuer)) {
          log2("auth success", {
            key: credential.issuer
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file4, L: 64, S: this });
          trigger.wake(true);
        } else {
          log2("key is not currently in trusted set, waiting...", {
            key: credential.issuer,
            trusted: [
              ...this._params.trustedKeysProvider()
            ]
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file4, L: 69, S: this });
        }
      });
      try {
        return await trigger.wait({
          timeout: this._params.authTimeout
        });
      } catch {
        return false;
      } finally {
        clear();
      }
    };
  }
  _isTrustedKey(deviceKey) {
    const deviceSet = this._params.trustedKeysProvider();
    return deviceSet.has(deviceKey);
  }
};

// src/packlets/spaces/edge-feed-replicator.ts
import { decode as decodeCbor, encode as encodeCbor } from "cbor-x";
import { Event, Mutex, scheduleMicroTask } from "@dxos/async";
import { Context as Context3, Resource } from "@dxos/context";
import { EdgeConnectionClosedError, EdgeIdentityChangedError } from "@dxos/edge-client";
import { invariant as invariant2 } from "@dxos/invariant";
import { PublicKey as PublicKey3 } from "@dxos/keys";
import { log as log3, logInfo } from "@dxos/log";
import { EdgeService } from "@dxos/protocols";
import { buf } from "@dxos/protocols/buf";
import { MessageSchema as RouterMessageSchema } from "@dxos/protocols/buf/dxos/edge/messenger_pb";
import { EdgeStatus } from "@dxos/protocols/proto/dxos/client/services";
import { ComplexMap as ComplexMap2, arrayToBuffer, bufferToArray, defaultMap, rangeFromTo } from "@dxos/util";
var __dxlog_file5 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/spaces/edge-feed-replicator.ts";
function _ts_decorate(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_add_disposable_resource(env, value, async) {
  if (value !== null && value !== void 0) {
    if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
    var dispose, inner;
    if (async) {
      if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
      dispose = value[Symbol.asyncDispose];
    }
    if (dispose === void 0) {
      if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
      dispose = value[Symbol.dispose];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    env.stack.push({
      value,
      dispose,
      async
    });
  } else if (async) {
    env.stack.push({
      async: true
    });
  }
  return value;
}
function _ts_dispose_resources(env) {
  var _SuppressedError = typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
  };
  return (_ts_dispose_resources = function _ts_dispose_resources6(env2) {
    function fail(e) {
      env2.error = env2.hasError ? new _SuppressedError(e, env2.error, "An error was suppressed during disposal.") : e;
      env2.hasError = true;
    }
    var r, s = 0;
    function next() {
      while (r = env2.stack.pop()) {
        try {
          if (!r.async && s === 1) return s = 0, env2.stack.push(r), Promise.resolve().then(next);
          if (r.dispose) {
            var result = r.dispose.call(r.value);
            if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) {
              fail(e);
              return next();
            });
          } else s |= 1;
        } catch (e) {
          fail(e);
        }
      }
      if (s === 1) return env2.hasError ? Promise.reject(env2.error) : Promise.resolve();
      if (env2.hasError) throw env2.error;
    }
    return next();
  })(env);
}
var EdgeFeedReplicator = class extends Resource {
  _messenger;
  _spaceId;
  _feeds = new ComplexMap2(PublicKey3.hash);
  _connectionCtx = void 0;
  _connected = false;
  /**
  * Feed length at service.
  */
  _remoteLength = new ComplexMap2(PublicKey3.hash);
  /**
  * Protects against concurrent pushes so that remote length is updated consistently.
  */
  _pushMutex = new ComplexMap2(PublicKey3.hash);
  constructor({ messenger, spaceId }) {
    super();
    this._messenger = messenger;
    this._spaceId = spaceId;
  }
  async _open() {
    log3("open", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 105, S: this });
    this._ctx.onDispose(this._messenger.onMessage((message) => {
      if (!message.serviceId) {
        return;
      }
      const [service, ...rest] = message.serviceId.split(":");
      if (service !== EdgeService.FEED_REPLICATOR) {
        return;
      }
      const [spaceId] = rest;
      if (spaceId !== this._spaceId) {
        log3("spaceID mismatch", {
          spaceId,
          _spaceId: this._spaceId
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 117, S: this });
        return;
      }
      const payload = decodeCbor(message.payload.value);
      log3("receive", {
        from: message.source,
        feedKey: payload.feedKey,
        type: payload.type
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 124, S: this });
      this._onMessage(payload);
    }));
    this._ctx.onDispose(this._messenger.onReconnected(() => {
      scheduleMicroTask(this._ctx, () => this._handleReconnect());
    }));
  }
  async _handleReconnect() {
    await this._resetConnection();
    if (this._messenger.status.state === EdgeStatus.ConnectionState.CONNECTED) {
      this._startReplication();
    }
  }
  async _close() {
    log3("close", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 142, S: this });
    await this._resetConnection();
  }
  _startReplication() {
    this._connected = true;
    const connectionCtx = this._createConnectionContext();
    this._connectionCtx = connectionCtx;
    log3("connection context created", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 149, S: this });
    scheduleMicroTask(connectionCtx, async () => {
      for (const feed of this._feeds.values()) {
        await this._replicateFeed(connectionCtx, feed);
      }
    });
  }
  async _resetConnection() {
    log3("resetConnection", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 157, S: this });
    this._connected = false;
    await this._connectionCtx?.dispose();
    this._connectionCtx = void 0;
    this._remoteLength.clear();
  }
  async addFeed(feed) {
    log3("addFeed", {
      key: feed.key,
      connected: this._connected,
      hasConnectionCtx: !!this._connectionCtx
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 164, S: this });
    this._feeds.set(feed.key, feed);
    if (this._connected && this._connectionCtx) {
      await this._replicateFeed(this._connectionCtx, feed);
    }
  }
  _getPushMutex(key) {
    return defaultMap(this._pushMutex, key, () => new Mutex());
  }
  async _replicateFeed(ctx, feed) {
    log3("replicateFeed", {
      key: feed.key
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 178, S: this });
    await this._sendMessage(ctx, {
      type: "get-metadata",
      feedKey: feed.key.toHex()
    });
    Event.wrap(feed.core, "append").on(ctx, async () => {
      await this._pushBlocksIfNeeded(feed);
    });
  }
  async _sendMessage(ctx, message) {
    if (!this._connectionCtx) {
      log3("message dropped because connection was disposed", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 191, S: this });
      return;
    }
    if (message.type === "data") {
      log3("sending blocks", {
        feedKey: message.feedKey,
        blocks: message.blocks.map((b) => b.index)
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 195, S: this });
    }
    invariant2(message.feedKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 200, S: this, A: ["message.feedKey", ""] });
    const payloadValue = bufferToArray(encodeCbor(message));
    log3("send", {
      type: message.type
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 202, S: this });
    await this._messenger.send(ctx, buf.create(RouterMessageSchema, {
      source: {
        identityDid: this._messenger.identityDid,
        peerKey: this._messenger.peerKey
      },
      serviceId: `${EdgeService.FEED_REPLICATOR}:${this._spaceId}`,
      payload: {
        value: payloadValue
      }
    }));
  }
  _onMessage(message) {
    if (!this._connectionCtx) {
      log3.warn("received message after connection context was disposed", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 218, S: this });
      return;
    }
    scheduleMicroTask(this._connectionCtx, async () => {
      switch (message.type) {
        case "metadata": {
          const env = {
            stack: [],
            error: void 0,
            hasError: false
          };
          try {
            const feedKey = PublicKey3.fromHex(message.feedKey);
            const feed = this._feeds.get(feedKey);
            if (!feed) {
              log3.warn("Feed not found", {
                feedKey
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 234, S: this });
              return;
            }
            const _guard = _ts_add_disposable_resource(env, await this._getPushMutex(feed.key).acquire(), false);
            this._remoteLength.set(feedKey, message.length);
            const logMeta = {
              localLength: feed.length,
              remoteLength: message.length,
              feedKey
            };
            if (message.length > feed.length) {
              log3("requesting missing blocks", logMeta, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 247, S: this });
              await this._sendMessage(this._connectionCtx, {
                type: "request",
                feedKey: feedKey.toHex(),
                range: {
                  from: feed.length,
                  to: message.length
                }
              });
            } else if (message.length < feed.length) {
              log3("pushing blocks to remote", logMeta, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 257, S: this });
              await this._pushBlocks(this._connectionCtx, feed, message.length, feed.length);
            }
            break;
          } catch (e) {
            env.error = e;
            env.hasError = true;
          } finally {
            _ts_dispose_resources(env);
          }
        }
        case "data": {
          log3("received data", {
            feed: message.feedKey,
            blocks: message.blocks.map((b) => b.index)
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 270, S: this });
          const feedKey = PublicKey3.fromHex(message.feedKey);
          const feed = this._feeds.get(feedKey);
          if (!feed) {
            log3.warn("Feed not found", {
              feedKey
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 277, S: this });
            return;
          }
          await this._integrateBlocks(feed, message.blocks);
          break;
        }
        default: {
          log3.warn("Unknown message", {
            ...message
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 287, S: this });
        }
      }
    });
  }
  async _pushBlocks(ctx, feed, from, to) {
    log3("pushing blocks", {
      feed: feed.key.toHex(),
      from,
      to
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 295, S: this });
    const blocks = await Promise.all(rangeFromTo(from, to).map(async (index) => {
      const data = await feed.get(index, {
        valueEncoding: "binary"
      });
      invariant2(data instanceof Uint8Array, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 304, S: this, A: ["data instanceof Uint8Array", ""] });
      const proof = await feed.proof(index);
      return {
        index,
        data,
        nodes: proof.nodes,
        signature: proof.signature
      };
    }));
    await this._sendMessage(ctx, {
      type: "data",
      feedKey: feed.key.toHex(),
      blocks
    });
    this._remoteLength.set(feed.key, to);
  }
  async _integrateBlocks(feed, blocks) {
    log3("integrating blocks", {
      feed: feed.key.toHex(),
      blocks: blocks.length
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 321, S: this });
    for (const block of blocks) {
      if (feed.has(block.index)) {
        continue;
      }
      const blockBuffer = bufferizeBlock(block);
      await feed.putBuffer(block.index, blockBuffer.data, {
        nodes: blockBuffer.nodes,
        signature: blockBuffer.signature
      }, null);
    }
  }
  async _pushBlocksIfNeeded(feed) {
    const env = {
      stack: [],
      error: void 0,
      hasError: false
    };
    try {
      const _ = _ts_add_disposable_resource(env, await this._getPushMutex(feed.key).acquire(), false);
      if (!this._remoteLength.has(feed.key)) {
        log3("blocks not pushed because remote length is unknown", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 345, S: this });
        return;
      }
      const remoteLength = this._remoteLength.get(feed.key);
      if (remoteLength < feed.length) {
        await this._pushBlocks(this._connectionCtx, feed, remoteLength, feed.length);
      }
    } catch (e) {
      env.error = e;
      env.hasError = true;
    } finally {
      _ts_dispose_resources(env);
    }
  }
  _createConnectionContext() {
    const connectionCtx = new Context3({
      parent: this._ctx,
      onError: async (err) => {
        if (connectionCtx !== this._connectionCtx) {
          return;
        }
        if (err instanceof EdgeIdentityChangedError || err instanceof EdgeConnectionClosedError) {
          log3("resetting on reconnect", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 367, S: this });
          await this._resetConnection();
        } else {
          this._ctx.raise(err);
        }
      }
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file5, L: 360 });
    return connectionCtx;
  }
};
_ts_decorate([
  logInfo
], EdgeFeedReplicator.prototype, "_spaceId", void 0);
var bufferizeBlock = (block) => ({
  index: block.index,
  data: arrayToBuffer(block.data),
  nodes: block.nodes.map((node) => ({
    index: node.index,
    hash: arrayToBuffer(node.hash),
    size: node.size
  })),
  signature: arrayToBuffer(block.signature)
});

// src/packlets/spaces/data-space.ts
import { save } from "@automerge/automerge";
import { Event as Event7, Mutex as Mutex2, scheduleTask as scheduleTask4, sleep as sleep2, synchronized as synchronized2, trackLeaks as trackLeaks2 } from "@dxos/async";
import { AUTH_TIMEOUT as AUTH_TIMEOUT2 } from "@dxos/client-protocol";
import { Context as Context8, ContextDisposedError, cancelWithContext as cancelWithContext2 } from "@dxos/context";
import { timed, warnAfterTimeout } from "@dxos/debug";
import { createMappedFeedWriter } from "@dxos/echo-host";
import { SpaceDocVersion } from "@dxos/echo-protocol";
import { failedInvariant as failedInvariant3, invariant as invariant12 } from "@dxos/invariant";
import { PublicKey as PublicKey7 } from "@dxos/keys";
import { log as log11 } from "@dxos/log";
import { CancelledError, SystemError } from "@dxos/protocols";
import { SpaceState as SpaceState3 } from "@dxos/protocols/proto/dxos/client/services";
import { AdmittedFeed as AdmittedFeed4, MembershipPolicy as MembershipPolicy2, SpaceMember as SpaceMember5 } from "@dxos/protocols/proto/dxos/halo/credentials";
import { Timeframe as Timeframe3 } from "@dxos/timeframe";
import { trace as trace4 } from "@dxos/tracing";
import { CallbackCollection, ComplexSet as ComplexSet3 } from "@dxos/util";

// src/packlets/identity/identity.ts
import { Event as Event3 } from "@dxos/async";
import { AUTH_TIMEOUT, LOAD_CONTROL_FEEDS_TIMEOUT } from "@dxos/client-protocol";
import { DeviceStateMachine, ProfileStateMachine, createCredentialSignerWithChain, createCredentialSignerWithKey } from "@dxos/credentials";
import { writeMessages as writeMessages3 } from "@dxos/feed-store";
import { invariant as invariant7 } from "@dxos/invariant";
import { PublicKey as PublicKey5 } from "@dxos/keys";
import { log as log8 } from "@dxos/log";
import { AdmittedFeed as AdmittedFeed2 } from "@dxos/protocols/proto/dxos/halo/credentials";
import { trace as trace3 } from "@dxos/tracing";
import { ComplexSet } from "@dxos/util";

// src/packlets/spaces/data-space-manager.ts
import { interpretAsDocumentId } from "@automerge/automerge-repo";
import { Event as Event2, synchronized, trackLeaks } from "@dxos/async";
import { SpaceProperties } from "@dxos/client-protocol";
import { LifecycleState, Resource as Resource2, cancelWithContext } from "@dxos/context";
import { createAdmissionCredentials, getCredentialAssertion } from "@dxos/credentials";
import { Type } from "@dxos/echo";
import { AuthStatus, CredentialServerExtension, DatabaseRoot, findInlineObjectOfType } from "@dxos/echo-host";
import { createIdFromSpaceKey } from "@dxos/echo-protocol";
import { writeMessages } from "@dxos/feed-store";
import { assertArgument, assertState, failedInvariant, invariant as invariant3 } from "@dxos/invariant";
import { PublicKey as PublicKey4 } from "@dxos/keys";
import { log as log4 } from "@dxos/log";
import { AlreadyJoinedError } from "@dxos/protocols";
import { Invitation, SpaceState } from "@dxos/protocols/proto/dxos/client/services";
import { EdgeReplicationSetting } from "@dxos/protocols/proto/dxos/echo/metadata";
import { SpaceMember as SpaceMember3 } from "@dxos/protocols/proto/dxos/halo/credentials";
import { Gossip, Presence } from "@dxos/teleport-extension-gossip";
import { trace } from "@dxos/tracing";
import { ComplexMap as ComplexMap3, deferFunction, forEachAsync } from "@dxos/util";

// src/packlets/spaces/genesis.ts
import { createCredential } from "@dxos/credentials";
import { failUndefined } from "@dxos/debug";
import { AdmittedFeed, MembershipPolicy, SpaceMember as SpaceMember2 } from "@dxos/protocols/proto/dxos/halo/credentials";
import { Timeframe } from "@dxos/timeframe";
var spaceGenesis = async (keyring, signingContext, space, automergeRoot, tags, membershipPolicy) => {
  const credentials = [
    await createCredential({
      signer: keyring,
      issuer: space.key,
      subject: space.key,
      assertion: {
        "@type": "dxos.halo.credentials.SpaceGenesis",
        "spaceKey": space.key,
        "tags": tags ?? [],
        "membershipPolicy": membershipPolicy ?? MembershipPolicy.INVITE
      }
    }),
    await createCredential({
      signer: keyring,
      issuer: space.key,
      subject: signingContext.identityKey,
      assertion: {
        "@type": "dxos.halo.credentials.SpaceMember",
        "spaceKey": space.key,
        "role": SpaceMember2.Role.OWNER,
        "profile": signingContext.getProfile(),
        "genesisFeedKey": space.controlFeedKey ?? failUndefined(),
        "tags": tags ?? []
      }
    }),
    await signingContext.credentialSigner.createCredential({
      subject: space.controlFeedKey ?? failUndefined(),
      assertion: {
        "@type": "dxos.halo.credentials.AdmittedFeed",
        "spaceKey": space.key,
        "identityKey": signingContext.identityKey,
        "deviceKey": signingContext.deviceKey,
        "designation": AdmittedFeed.Designation.CONTROL
      }
    }),
    await signingContext.credentialSigner.createCredential({
      subject: space.dataFeedKey ?? failUndefined(),
      assertion: {
        "@type": "dxos.halo.credentials.AdmittedFeed",
        "spaceKey": space.key,
        "identityKey": signingContext.identityKey,
        "deviceKey": signingContext.deviceKey,
        "designation": AdmittedFeed.Designation.DATA
      }
    }),
    await signingContext.credentialSigner.createCredential({
      subject: space.key ?? failUndefined(),
      assertion: {
        "@type": "dxos.halo.credentials.Epoch",
        "number": 0,
        "previousId": void 0,
        "timeframe": new Timeframe(),
        "snapshotCid": void 0,
        automergeRoot
      }
    })
  ];
  for (const credential of credentials) {
    await space.controlPipeline.writer.write({
      credential: {
        credential
      }
    });
  }
  return credentials;
};

// src/packlets/spaces/data-space-manager.ts
var __dxlog_file6 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/spaces/data-space-manager.ts";
function _ts_decorate2(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var PRESENCE_ANNOUNCE_INTERVAL = 1e4;
var PRESENCE_OFFLINE_TIMEOUT = 2e4;
var DataSpaceManager = class extends Resource2 {
  updated = new Event2();
  _spaces = new ComplexMap3(PublicKey4.hash);
  _spaceManager;
  _metadataStore;
  _keyring;
  _signingContext;
  _feedStore;
  _echoHost;
  _invitationsManager;
  _edgeConnection = void 0;
  _edgeHttpClient = void 0;
  _edgeFeatures = void 0;
  _meshReplicator = void 0;
  _echoEdgeReplicator = void 0;
  _runtimeProps = void 0;
  constructor(params) {
    super();
    this._spaceManager = params.spaceManager;
    this._metadataStore = params.metadataStore;
    this._keyring = params.keyring;
    this._signingContext = params.signingContext;
    this._feedStore = params.feedStore;
    this._echoHost = params.echoHost;
    this._meshReplicator = params.meshReplicator;
    this._invitationsManager = params.invitationsManager;
    this._edgeConnection = params.edgeConnection;
    this._edgeFeatures = params.edgeFeatures;
    this._echoEdgeReplicator = params.echoEdgeReplicator;
    this._edgeHttpClient = params.edgeHttpClient;
    this._runtimeProps = params.runtimeProps;
    trace.diagnostic({
      id: "spaces",
      name: "Spaces",
      fetch: async () => {
        return Promise.all(Array.from(this._spaces.values()).map(async (space) => {
          const rootUrl = space.automergeSpaceState.rootUrl;
          const rootHandle = rootUrl ? await this._echoHost.loadDoc(this._ctx, rootUrl) : void 0;
          const rootDoc = rootHandle?.doc();
          const properties = rootDoc && findInlineObjectOfType(rootDoc, Type.getTypename(SpaceProperties));
          return {
            key: space.key.toHex(),
            state: SpaceState[space.state],
            name: properties?.[1].data.name ?? null,
            inlineObjects: rootDoc ? Object.keys(rootDoc.objects ?? {}).length : null,
            linkedObjects: rootDoc ? Object.keys(rootDoc.links ?? {}).length : null,
            credentials: space.inner.spaceState.credentials.length,
            members: space.inner.spaceState.members.size,
            rootUrl
          };
        }));
      }
    });
  }
  // TODO(burdon): Remove.
  get spaces() {
    return this._spaces;
  }
  getSpaceById(spaceId) {
    return [
      ...this._spaces.values()
    ].find((space) => space.id === spaceId);
  }
  async _open(ctx) {
    log4("open", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 98, S: this });
    log4("metadata loaded", {
      spaces: this._metadataStore.spaces.length
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 99, S: this });
    const spacesToActivate = [];
    await forEachAsync(this._metadataStore.spaces, async (spaceMetadata) => {
      try {
        if (spaceMetadata.state === SpaceState.SPACE_DELETED || this.isSpaceDeleted(spaceMetadata.key)) {
          log4("skipping deleted space", {
            spaceKey: spaceMetadata.key
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 107, S: this });
          return;
        }
        log4("load space", {
          spaceMetadata
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 112, S: this });
        const space = await this._constructSpace(ctx, spaceMetadata);
        if (this._runtimeProps?.autoActivateSpaces && spaceMetadata.state === SpaceState.SPACE_ACTIVE) {
          spacesToActivate.push(space);
        }
      } catch (err) {
        log4.error("Error loading space", {
          spaceMetadata,
          err
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 121, S: this });
      }
    });
    for (const space of spacesToActivate) {
      log4("auto-activating space", {
        spaceKey: space.key
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 129, S: this });
      space.activate(ctx).catch((err) => {
        log4.error("Error auto-activating space", {
          spaceKey: space.key,
          err
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 133, S: this });
      });
    }
    this.updated.emit();
  }
  async _close(ctx) {
    log4("close", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 142, S: this });
    for (const space of this._spaces.values()) {
      await space.close(ctx);
    }
    this._spaces.clear();
  }
  /**
  * Creates a new space writing the genesis credentials to the control feed.
  */
  async createSpace(ctx, options = {}) {
    assertArgument(!!options.rootUrl === !!options.documents, "options", "root url must be required when providing documents");
    assertState(this._lifecycleState === LifecycleState.OPEN, "Not open.");
    const tags = options.tags ? Array.from(options.tags) : [];
    const spaceKey = await this._keyring.createKey();
    const controlFeedKey = await this._keyring.createKey();
    const dataFeedKey = await this._keyring.createKey();
    const spaceId = await createIdFromSpaceKey(spaceKey);
    const metadata = {
      key: spaceKey,
      genesisFeedKey: controlFeedKey,
      controlFeedKey,
      dataFeedKey,
      state: SpaceState.SPACE_ACTIVE,
      tags
    };
    log4("creating space...", {
      spaceId,
      spaceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 166, S: this });
    const documentIdMapping = {};
    if (options.documents) {
      invariant3(Object.keys(options.documents).every((documentId) => /^[a-zA-Z0-9]+$/.test(documentId)), "Invalid document IDs", { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 173, S: this, A: ["Object.keys(options.documents).every((documentId)=>/^[a-zA-Z0-9]+$/.test(documentId))", "'Invalid document IDs'"] });
      await Promise.all(Object.entries(options.documents).map(async ([documentId, data]) => {
        log4("creating document...", {
          documentId
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 175, S: this });
        const newDoc = await this._echoHost.createDoc(data, {
          preserveHistory: true
        });
        if (newDoc.doc().access !== void 0 && newDoc.doc().access.spaceKey !== spaceKey.toHex()) {
          newDoc.change((doc) => {
            doc.access.spaceKey = spaceKey.toHex();
          });
        }
        documentIdMapping[documentId] = newDoc.documentId;
      }));
    }
    log4("opening space...", {
      spaceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 191, S: this });
    let root;
    if (options.rootUrl) {
      const newRootDocId = documentIdMapping[interpretAsDocumentId(options.rootUrl)] ?? failedInvariant();
      const rootDocHandle = await this._echoHost.loadDoc(ctx, newRootDocId);
      invariant3(rootDocHandle, "Root document must be available after import.", { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 198, S: this, A: ["rootDocHandle", "'Root document must be available after import.'"] });
      DatabaseRoot.mapLinks(rootDocHandle, documentIdMapping);
      root = await this._echoHost.openSpaceRoot(ctx, spaceId, `automerge:${newRootDocId}`);
    } else {
      root = await this._echoHost.createSpaceRoot(ctx, spaceKey);
    }
    await this._echoHost.flush(ctx);
    log4("constructing space...", {
      spaceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 205, S: this });
    const space = await this._constructSpace(ctx, metadata);
    await space.open(ctx);
    log4("adding space...", {
      spaceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 210, S: this });
    const credentials = await spaceGenesis(this._keyring, this._signingContext, space.inner, root.url, tags, options.membershipPolicy);
    await this._metadataStore.addSpace(metadata);
    const memberCredential = credentials[1];
    invariant3(getCredentialAssertion(memberCredential)["@type"] === "dxos.halo.credentials.SpaceMember", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 216, S: this, A: ["getCredentialAssertion(memberCredential)['@type'] === 'dxos.halo.credentials.SpaceMember'", ""] });
    await this._signingContext.recordCredential(memberCredential);
    await space.initializeDataPipeline(ctx);
    log4("space ready.", {
      spaceId,
      spaceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 219, S: this });
    this.updated.emit();
    return space;
  }
  /**
  * Accepts an existing space by joining its swarm and initializing the data pipeline.
  * @param ctx - Caller context for cancellation and tracing.
  * @param opts - Space keys and optional timeframes for catch-up.
  */
  // TODO(burdon): Rename join space.
  async acceptSpace(ctx, opts) {
    log4("accept space", {
      opts
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 232, S: this });
    invariant3(this._lifecycleState === LifecycleState.OPEN, "Not open.", { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 235, S: this, A: ["this._lifecycleState === LifecycleState.OPEN", "'Not open.'"] });
    invariant3(!this._spaces.has(opts.spaceKey), "Space already exists.", { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 236, S: this, A: ["!this._spaces.has(opts.spaceKey)", "'Space already exists.'"] });
    invariant3(!this.isSpaceDeleted(opts.spaceKey), "Cannot accept a deleted space.", { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 237, S: this, A: ["!this.isSpaceDeleted(opts.spaceKey)", "'Cannot accept a deleted space.'"] });
    const tags = opts.tags ? Array.from(opts.tags) : [];
    const metadata = {
      key: opts.spaceKey,
      genesisFeedKey: opts.genesisFeedKey,
      controlTimeframe: opts.controlTimeframe,
      dataTimeframe: opts.dataTimeframe,
      tags
    };
    const space = await this._constructSpace(ctx, metadata);
    await space.open(ctx);
    await this._metadataStore.addSpace(metadata);
    space.initializeDataPipelineAsync(this._ctx);
    this.updated.emit();
    return space;
  }
  /**
  * Whether the space has been tombstoned (soft-deleted). Deleted spaces are never opened or replicated.
  */
  isSpaceDeleted(spaceKey) {
    return this._metadataStore.deletedSpaces.some((key) => key.equals(spaceKey)) || this._metadataStore.spaces.some((spaceMetadata) => spaceMetadata.key.equals(spaceKey) && spaceMetadata.state === SpaceState.SPACE_DELETED);
  }
  /**
  * Tombstones (soft-deletes) a space initiated locally on this device.
  * Records a SpaceDeleted credential in the HALO so the deletion replicates to the user's other devices,
  * then unloads the space locally. Data is not removed until garbage collection (future work).
  */
  async markSpaceDeleted(ctx, spaceKey) {
    if (this.isSpaceDeleted(spaceKey)) {
      return;
    }
    const credential = await this._signingContext.credentialSigner.createCredential({
      subject: spaceKey,
      assertion: {
        "@type": "dxos.halo.credentials.SpaceDeleted",
        spaceKey,
        "deletedAt": /* @__PURE__ */ new Date()
      }
    });
    await this._signingContext.recordCredential(credential);
    await this._tombstoneSpace(ctx, spaceKey);
  }
  /**
  * Tombstones a space in response to a SpaceDeleted credential replicated from another device.
  * Does not write a credential (one already exists in the HALO).
  */
  async handleRemoteSpaceDeleted(ctx, spaceKey) {
    if (this.isSpaceDeleted(spaceKey)) {
      return;
    }
    await this._tombstoneSpace(ctx, spaceKey);
  }
  /**
  * Persists the tombstone and unloads the space if it is currently loaded.
  * Must be called while holding the DataSpaceManager lock (see callers).
  */
  async _tombstoneSpace(ctx, spaceKey) {
    await this._metadataStore.addDeletedSpace(spaceKey);
    const space = this._spaces.get(spaceKey);
    if (space) {
      if (space.isOpen) {
        await space.close(ctx);
      }
      await space.delete();
      this._spaces.delete(spaceKey);
    }
    this.updated.emit();
  }
  async admitMember(options) {
    const space = this._spaceManager.spaces.get(options.spaceKey);
    invariant3(space, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 310, S: this, A: ["space", ""] });
    if (space.spaceState.getMemberRole(options.identityKey) !== SpaceMember3.Role.REMOVED) {
      throw new AlreadyJoinedError();
    }
    const credentials = await createAdmissionCredentials(this._signingContext.credentialSigner, options.identityKey, space.key, space.genesisFeedKey, options.role, space.spaceState.membershipChainHeads, options.profile, options.delegationCredentialId, space.spaceState.tags);
    invariant3(credentials[0].credential, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 317, S: this, A: ["credentials[0].credential", ""] });
    const spaceMemberCredential = credentials[0].credential.credential;
    invariant3(getCredentialAssertion(spaceMemberCredential)["@type"] === "dxos.halo.credentials.SpaceMember", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 319, S: this, A: ["getCredentialAssertion(spaceMemberCredential)['@type'] === 'dxos.halo.credentials.SpaceMember'", ""] });
    await writeMessages(space.controlPipeline.writer, credentials);
    return spaceMemberCredential;
  }
  /**
  * Wait until the space data pipeline is fully initialized.
  * Used by invitation handler.
  * TODO(dmaretskyi): Consider removing.
  */
  async waitUntilSpaceReady(spaceKey) {
    await cancelWithContext(this._ctx, this.updated.waitForCondition(() => {
      const space = this._spaces.get(spaceKey);
      return !!space && space.state === SpaceState.SPACE_READY;
    }));
  }
  async requestSpaceAdmissionCredential(ctx, spaceKey) {
    return this._spaceManager.requestSpaceAdmissionCredential(ctx, {
      spaceKey,
      identityKey: this._signingContext.identityKey,
      timeout: 15e3,
      swarmIdentity: {
        identityKey: this._signingContext.identityKey,
        peerKey: this._signingContext.deviceKey,
        credentialProvider: createAuthProvider(this._signingContext.credentialSigner),
        credentialAuthenticator: async () => true
      }
    });
  }
  async setSpaceEdgeReplicationSetting(ctx, spaceKey, setting) {
    const space = this._spaces.get(spaceKey);
    invariant3(space, "Space not found.", { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 348, S: this, A: ["space", "'Space not found.'"] });
    await this._metadataStore.setSpaceEdgeReplicationSetting(spaceKey, setting);
    if (space.isOpen) {
      switch (setting) {
        case EdgeReplicationSetting.DISABLED:
          await this._echoEdgeReplicator?.disconnectFromSpace(space.id);
          break;
        case EdgeReplicationSetting.ENABLED:
          await this._echoEdgeReplicator?.connectToSpace(ctx, space.id);
          break;
      }
    }
    space.stateUpdate.emit();
  }
  async _constructSpace(ctx, metadata) {
    log4("construct space", {
      metadata
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 363, S: this });
    const gossip = new Gossip({
      localPeerId: this._signingContext.deviceKey
    });
    const presence = new Presence({
      announceInterval: this._runtimeProps?.spaceMemberPresenceAnnounceInterval ?? PRESENCE_ANNOUNCE_INTERVAL,
      offlineTimeout: this._runtimeProps?.spaceMemberPresenceOfflineTimeout ?? PRESENCE_OFFLINE_TIMEOUT,
      identityKey: this._signingContext.identityKey,
      gossip
    });
    const controlFeed = metadata.controlFeedKey && await this._feedStore.openFeed(metadata.controlFeedKey, {
      writable: true
    });
    const dataFeed = metadata.dataFeedKey && await this._feedStore.openFeed(metadata.dataFeedKey, {
      writable: true,
      sparse: true
    });
    const space = await this._spaceManager.constructSpace({
      metadata,
      swarmIdentity: {
        identityKey: this._signingContext.identityKey,
        peerKey: this._signingContext.deviceKey,
        credentialProvider: createAuthProvider(this._signingContext.credentialSigner),
        credentialAuthenticator: deferFunction(() => dataSpace.authVerifier.verifier)
      },
      onAuthorizedConnection: (session) => queueMicrotask(async () => {
        try {
          if (!session.isOpen) {
            return;
          }
          session.addExtension("dxos.mesh.teleport.admission-discovery", new CredentialServerExtension(space));
          session.addExtension("dxos.mesh.teleport.gossip", gossip.createExtension({
            remotePeerId: session.remotePeerId
          }));
          session.addExtension("dxos.mesh.teleport.notarization", dataSpace.notarizationPlugin.createExtension());
          await this._connectEchoMeshReplicator(space, session);
        } catch (err) {
          log4.warn("error on authorized connection", {
            err
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 402, S: this });
          await session.close(err);
        }
      }),
      onAuthFailure: () => {
        log4.warn("auth failure", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 409, S: this });
      },
      onMemberRolesChanged: async (members) => {
        if (dataSpace?.state === SpaceState.SPACE_READY) {
          this._handleMemberRoleChanges(presence, space.protocol, members);
        }
      },
      memberKey: this._signingContext.identityKey,
      onDelegatedInvitationStatusChange: (invitation, isActive) => {
        return this._handleInvitationStatusChange(dataSpace, invitation, isActive);
      }
    });
    controlFeed && await space.setControlFeed(controlFeed);
    dataFeed && await space.setDataFeed(dataFeed);
    const dataSpace = new DataSpace({
      inner: space,
      initialState: metadata.state === SpaceState.SPACE_INACTIVE ? SpaceState.SPACE_INACTIVE : SpaceState.SPACE_CLOSED,
      metadataStore: this._metadataStore,
      gossip,
      presence,
      keyring: this._keyring,
      feedStore: this._feedStore,
      echoHost: this._echoHost,
      signingContext: this._signingContext,
      callbacks: {
        beforeReady: async () => {
          log4("before space ready", {
            space: space.key
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 435, S: this });
        },
        afterReady: async () => {
          log4("after space ready", {
            space: space.key,
            open: this._lifecycleState === LifecycleState.OPEN
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 440, S: this });
          if (this._lifecycleState === LifecycleState.OPEN) {
            await this._createDelegatedInvitations(dataSpace, [
              ...space.spaceState.invitations.entries()
            ]);
            this._handleMemberRoleChanges(presence, space.protocol, [
              ...space.spaceState.members.values()
            ]);
            this.updated.emit();
          }
        },
        beforeClose: async () => {
          log4("before space close", {
            space: space.key
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 455, S: this });
        }
      },
      cache: metadata.cache,
      tags: metadata.tags,
      edgeConnection: this._edgeConnection,
      edgeHttpClient: this._edgeHttpClient,
      edgeFeatures: this._edgeFeatures,
      activeEdgeNotarizationPollingInterval: this._runtimeProps?.activeEdgeNotarizationPollingInterval
    });
    dataSpace.postOpen.append(async () => {
      const setting = dataSpace.getEdgeReplicationSetting();
      if (!setting || setting === EdgeReplicationSetting.ENABLED) {
        await this._echoEdgeReplicator?.connectToSpace(this._ctx, dataSpace.id);
      } else if (this._echoEdgeReplicator) {
        log4("not connecting edge replicator because of EdgeReplicationSetting", {
          spaceId: dataSpace.id
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 473, S: this });
      }
    });
    dataSpace.preClose.append(async () => {
      const setting = dataSpace.getEdgeReplicationSetting();
      if (!setting || setting === EdgeReplicationSetting.ENABLED) {
        await this._echoEdgeReplicator?.disconnectFromSpace(dataSpace.id);
      }
    });
    presence.newPeer.on((peerState) => {
      if (dataSpace.state === SpaceState.SPACE_READY) {
        this._handleNewPeerConnected(space, peerState);
      }
    });
    if (metadata.controlTimeframe) {
      dataSpace.inner.controlPipeline.state.setTargetTimeframe(metadata.controlTimeframe);
    }
    this._spaces.set(metadata.key, dataSpace);
    return dataSpace;
  }
  async _connectEchoMeshReplicator(space, session) {
    const replicator = this._meshReplicator;
    if (!replicator) {
      log4.warn("p2p automerge replication disabled", {
        space: space.key
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 498, S: this });
      return;
    }
    await replicator.authorizeDevice(space.key, session.remotePeerId);
    if (session.isOpen) {
      session.addExtension("dxos.mesh.teleport.automerge", replicator.createExtension());
    }
  }
  _handleMemberRoleChanges(presence, spaceProtocol, memberInfo) {
    let closedSessions = 0;
    for (const member of memberInfo) {
      if (member.key.equals(presence.getLocalState().identityKey)) {
        continue;
      }
      const peers = presence.getPeersByIdentityKey(member.key);
      const sessions = peers.map((p) => p.peerId && spaceProtocol.sessions.get(p.peerId));
      const sessionsToClose = sessions.filter((s) => {
        return (s && member.role === SpaceMember3.Role.REMOVED !== (s.authStatus === AuthStatus.FAILURE)) ?? false;
      });
      sessionsToClose.forEach((session) => {
        void session.close().catch(log4.error);
      });
      closedSessions += sessionsToClose.length;
    }
    log4("processed member role changes", {
      roleChangeCount: memberInfo.length,
      peersOnline: presence.getPeersOnline().length,
      closedSessions
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 525, S: this });
    spaceProtocol.updateTopology();
  }
  _handleNewPeerConnected(space, peerState) {
    const role = space.spaceState.getMemberRole(peerState.identityKey);
    if (role === SpaceMember3.Role.REMOVED) {
      const session = peerState.peerId && space.protocol.sessions.get(peerState.peerId);
      if (session != null) {
        log4("closing a session with a removed peer", {
          peerId: peerState.peerId
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file6, L: 538, S: this });
        void session.close().catch(log4.error);
      }
    }
  }
  async _handleInvitationStatusChange(dataSpace, delegatedInvitation, isActive) {
    if (dataSpace?.state !== SpaceState.SPACE_READY) {
      return;
    }
    if (isActive) {
      await this._createDelegatedInvitations(dataSpace, [
        [
          delegatedInvitation.credentialId,
          delegatedInvitation.invitation
        ]
      ]);
    } else {
      await this._invitationsManager.cancelInvitation(delegatedInvitation.invitation);
    }
  }
  async _createDelegatedInvitations(space, invitations) {
    const tasks = invitations.map(([credentialId, invitation]) => {
      return this._invitationsManager.createInvitation(this._ctx, {
        type: Invitation.Type.DELEGATED,
        kind: Invitation.Kind.SPACE,
        spaceKey: space.key,
        authMethod: invitation.authMethod,
        invitationId: invitation.invitationId,
        swarmKey: invitation.swarmKey,
        guestKeypair: invitation.guestKey ? {
          publicKey: invitation.guestKey
        } : void 0,
        lifetime: invitation.expiresOn ? (invitation.expiresOn.getTime() - Date.now()) / 1e3 : void 0,
        multiUse: invitation.multiUse,
        delegationCredentialId: credentialId,
        persistent: false
      });
    });
    await Promise.all(tasks);
  }
};
_ts_decorate2([
  synchronized,
  trace.span({
    showInBrowserTimeline: true,
    op: "lifecycle"
  })
], DataSpaceManager.prototype, "_open", null);
_ts_decorate2([
  synchronized
], DataSpaceManager.prototype, "_close", null);
_ts_decorate2([
  synchronized,
  trace.span({
    showInBrowserTimeline: true,
    op: "lifecycle"
  })
], DataSpaceManager.prototype, "createSpace", null);
_ts_decorate2([
  synchronized,
  trace.span({
    showInBrowserTimeline: true,
    op: "lifecycle"
  })
], DataSpaceManager.prototype, "acceptSpace", null);
_ts_decorate2([
  synchronized
], DataSpaceManager.prototype, "markSpaceDeleted", null);
_ts_decorate2([
  synchronized
], DataSpaceManager.prototype, "handleRemoteSpaceDeleted", null);
DataSpaceManager = _ts_decorate2([
  trackLeaks("open", "close"),
  trace.resource({
    lifecycle: true
  })
], DataSpaceManager);

// src/packlets/spaces/spaces-service.ts
import { SubscriptionList as SubscriptionList2, UpdateScheduler, scheduleTask as scheduleTask2 } from "@dxos/async";
import { Stream as Stream7 } from "@dxos/codec-protobuf/stream";
import { Context as Context5 } from "@dxos/context";
import { createAdmissionCredentials as createAdmissionCredentials2, createDidFromIdentityKey as createDidFromIdentityKey2, getCredentialAssertion as getCredentialAssertion2 } from "@dxos/credentials";
import { raise } from "@dxos/debug";
import { writeMessages as writeMessages2 } from "@dxos/feed-store";
import { assertArgument as assertArgument5, assertState as assertState4, invariant as invariant6 } from "@dxos/invariant";
import { SpaceId } from "@dxos/keys";
import { log as log7 } from "@dxos/log";
import { ApiError, AuthorizationError, FeedProtocol as FeedProtocol2, IdentityNotInitializedError, SpaceNotFoundError, encodeError } from "@dxos/protocols";
import { SpaceArchive as SpaceArchive4, SpaceMember as SpaceMember4, SpaceState as SpaceState2 } from "@dxos/protocols/proto/dxos/client/services";
import { trace as trace2 } from "@dxos/tracing";

// src/packlets/space-export/archive-format.ts
import { SpaceArchive } from "@dxos/protocols/proto/dxos/client/services";
var JSON_EXTENSIONS = [
  ".dx.json",
  ".dx.json.gz",
  ".json"
];
var BINARY_EXTENSIONS = [
  ".tar",
  ".tar.gz"
];
var detectSpaceArchiveFormat = (archive) => {
  const filename = archive.filename?.toLowerCase() ?? "";
  if (JSON_EXTENSIONS.some((ext) => filename.endsWith(ext))) {
    return SpaceArchive.Format.JSON;
  }
  if (BINARY_EXTENSIONS.some((ext) => filename.endsWith(ext))) {
    return SpaceArchive.Format.BINARY;
  }
  const bytes = archive.contents;
  if (bytes && bytes.length > 0) {
    for (let i = 0; i < Math.min(bytes.length, 16); i++) {
      const byte = bytes[i];
      if (byte === 32 || byte === 9 || byte === 10 || byte === 13) {
        continue;
      }
      if (byte === 123) {
        return SpaceArchive.Format.JSON;
      }
      break;
    }
  }
  return SpaceArchive.Format.BINARY;
};

// src/packlets/space-export/serialized-space-reader.ts
import { Type as Type2 } from "@dxos/echo";
import { assertArgument as assertArgument2 } from "@dxos/invariant";
import { URI } from "@dxos/keys";
var ATTR_TYPE = "@type";
var ATTR_META = "@meta";
var ATTR_DELETED = "@deleted";
var ATTR_PARENT = "@parent";
var ATTR_RELATION_SOURCE = "@relationSource";
var ATTR_RELATION_TARGET = "@relationTarget";
var TYPE_KIND_SCHEMA_URI = Type2.getURI(Type2.Type).toString();
var INTERNAL_KEYS = /* @__PURE__ */ new Set([
  "id",
  ATTR_TYPE,
  ATTR_META,
  ATTR_DELETED,
  ATTR_PARENT,
  ATTR_RELATION_SOURCE,
  ATTR_RELATION_TARGET
]);
var readSerializedSpaceArchive = (archive) => {
  const text = new TextDecoder().decode(archive.contents);
  const parsed = JSON.parse(text);
  assertArgument2(typeof parsed === "object" && parsed !== null, "archive", "Invalid JSON archive payload");
  assertArgument2(typeof parsed.version === "number", "archive", "Missing SerializedSpace.version");
  assertArgument2(Array.isArray(parsed.objects), "archive", "Missing SerializedSpace.objects");
  return parsed;
};
var objJsonToObjectStructure = (obj) => {
  const data = {};
  for (const [key, value] of Object.entries(obj)) {
    if (INTERNAL_KEYS.has(key)) {
      continue;
    }
    data[key] = value;
  }
  const system = {};
  const type = obj[ATTR_TYPE];
  if (type) {
    system.type = {
      "/": URI.make(type)
    };
  }
  const parent = obj[ATTR_PARENT];
  if (typeof parent === "string") {
    system.parent = {
      "/": URI.make(parent)
    };
  }
  const relationSource = obj[ATTR_RELATION_SOURCE];
  const relationTarget = obj[ATTR_RELATION_TARGET];
  if (typeof relationSource === "string" || typeof relationTarget === "string") {
    system.kind = "relation";
    if (typeof relationSource === "string") {
      system.source = {
        "/": URI.make(relationSource)
      };
    }
    if (typeof relationTarget === "string") {
      system.target = {
        "/": URI.make(relationTarget)
      };
    }
  } else if (type === TYPE_KIND_SCHEMA_URI) {
    system.kind = "type";
  } else {
    system.kind = "object";
  }
  if (obj[ATTR_DELETED]) {
    system.deleted = true;
  }
  const meta = obj[ATTR_META];
  return {
    system,
    meta: {
      keys: meta?.keys ?? [],
      ...meta?.tags ? {
        tags: meta.tags
      } : {},
      // Preserve registry-provenance fields so persisted `Type.Type` entities
      // round-trip with their typename / semver (see the symmetric write in
      // `objectStructureToObjJson`).
      ...meta?.key !== void 0 ? {
        key: meta.key
      } : {},
      ...meta?.version !== void 0 ? {
        version: meta.version
      } : {}
    },
    data
  };
};
var buildDatabaseDirectoryFromObjects = (objects) => {
  const map = {};
  for (const obj of objects) {
    map[obj.id] = objJsonToObjectStructure(obj);
  }
  return {
    objects: map,
    links: {}
  };
};

// src/packlets/space-export/serialized-space-writer.ts
import { Context as Context4 } from "@dxos/context";
import { assertState as assertState2, invariant as invariant4 } from "@dxos/invariant";
import { DXN } from "@dxos/keys";
import { log as log5 } from "@dxos/log";
import { FeedProtocol } from "@dxos/protocols";
import { SpaceArchive as SpaceArchive2 } from "@dxos/protocols/proto/dxos/client/services";
import { createFilename } from "@dxos/util";
var __dxlog_file7 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/space-export/serialized-space-writer.ts";
var SERIALIZED_SPACE_VERSION = 1;
var FEED_TYPENAME = "org.dxos.type.feed";
var ATTR_ID = "id";
var ATTR_TYPE2 = "@type";
var ATTR_META2 = "@meta";
var ATTR_DELETED2 = "@deleted";
var ATTR_PARENT2 = "@parent";
var ATTR_RELATION_SOURCE2 = "@relationSource";
var ATTR_RELATION_TARGET2 = "@relationTarget";
var SYSTEM_FIELD_ORDER = [
  ATTR_ID,
  ATTR_TYPE2,
  ATTR_META2,
  ATTR_DELETED2,
  ATTR_PARENT2,
  ATTR_RELATION_SOURCE2,
  ATTR_RELATION_TARGET2
];
var orderObjJsonFields = (obj) => {
  const source = obj;
  const result = {};
  for (const key of SYSTEM_FIELD_ORDER) {
    if (key in source) {
      result[key] = source[key];
    }
  }
  for (const key of Object.keys(source)) {
    if (key.startsWith("@") && !(key in result)) {
      result[key] = source[key];
    }
  }
  for (const key of Object.keys(source)) {
    if (!(key in result)) {
      result[key] = source[key];
    }
  }
  return result;
};
var writeSerializedSpaceArchive = async (options) => {
  const { space, echoHost, exportedBy } = options;
  const rootUrl = space.automergeSpaceState.lastEpoch?.subject.assertion.automergeRoot;
  assertState2(rootUrl, "Space does not have a root URL");
  const databaseRoot = space.databaseRoot;
  assertState2(databaseRoot, "Space database root is not ready");
  const rootDoc = databaseRoot.doc();
  invariant4(rootDoc, "Space database root document is not loaded", { "~LogMeta": "~LogMeta", F: __dxlog_file7, L: 72, S: void 0, A: ["rootDoc", "'Space database root document is not loaded'"] });
  const objects = [];
  collectObjectsFromDoc(rootDoc, objects);
  for (const linkedUrl of databaseRoot.getAllLinkedDocuments()) {
    const handle = await echoHost.loadDoc(Context4.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file7, L: 77 }), linkedUrl);
    if (!handle) {
      log5.warn("linked document handle not available; skipping", {
        url: linkedUrl
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file7, L: 79, S: void 0 });
      continue;
    }
    const doc = handle.doc();
    collectObjectsFromDoc(doc, objects);
  }
  const feeds = await exportFeedData(space, echoHost, objects);
  const serialized = {
    version: SERIALIZED_SPACE_VERSION,
    timestamp: (/* @__PURE__ */ new Date()).toISOString(),
    originalSpaceId: space.id,
    exportedBy,
    createdAt: Date.now(),
    objects,
    ...feeds.length > 0 ? {
      feeds
    } : {}
  };
  const encoded = new TextEncoder().encode(JSON.stringify(serialized));
  return {
    filename: createFilename({
      parts: [
        space.id
      ],
      ext: "dx.json"
    }),
    contents: encoded,
    format: SpaceArchive2.Format.JSON
  };
};
var collectObjectsFromDoc = (doc, out) => {
  const docObjects = doc.objects ?? {};
  for (const [objectId, structure] of Object.entries(docObjects)) {
    out.push(objectStructureToObjJson(objectId, structure));
  }
};
var objectStructureToObjJson = (objectId, structure) => {
  const result = {
    [ATTR_ID]: objectId,
    [ATTR_TYPE2]: structure.system?.type?.["/"] ?? ""
  };
  if (structure.meta) {
    result[ATTR_META2] = {
      keys: structure.meta.keys ?? [],
      ...structure.meta.tags ? {
        tags: structure.meta.tags
      } : {},
      // Preserve the registry-provenance fields (typename / semver) so persisted
      // `Type.Type` entities round-trip with their `meta.key` / `meta.version`
      // intact — `Type.getTypename` / `Type.getVersion` read these. Without them
      // a type loaded from an archive would fall back to its object id.
      ...structure.meta.key !== void 0 ? {
        key: structure.meta.key
      } : {},
      ...structure.meta.version !== void 0 ? {
        version: structure.meta.version
      } : {}
    };
  }
  if (structure.system?.deleted) {
    result[ATTR_DELETED2] = true;
  }
  if (structure.system?.parent) {
    result[ATTR_PARENT2] = structure.system.parent["/"];
  }
  if (structure.system?.source) {
    result[ATTR_RELATION_SOURCE2] = structure.system.source["/"];
  }
  if (structure.system?.target) {
    result[ATTR_RELATION_TARGET2] = structure.system.target["/"];
  }
  Object.assign(result, structure.data);
  return result;
};
var exportFeedData = async (space, echoHost, objects) => {
  const feeds = [];
  const spaceId = space.id;
  for (const obj of objects) {
    if (obj[ATTR_TYPE2] == null) {
      continue;
    }
    const typeDxn = obj[ATTR_TYPE2];
    const typeDxnNsid = DXN.isDXN(typeDxn) ? DXN.getName(typeDxn) : void 0;
    if (typeDxnNsid !== FEED_TYPENAME) {
      continue;
    }
    const namespace = obj.namespace === "trace" ? "trace" : "data";
    try {
      const messages = await collectQueueMessages(echoHost, spaceId, obj.id, namespace);
      if (messages.length > 0) {
        feeds.push({
          feedObjectId: obj.id,
          namespace,
          messages
        });
      }
    } catch (err) {
      log5.warn("failed to export feed data", {
        feedObjectId: obj.id,
        error: err
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file7, L: 185, S: void 0 });
    }
  }
  return feeds;
};
var collectQueueMessages = async (echoHost, spaceId, queueId, namespace) => {
  const queuesNamespace = namespace === "trace" ? FeedProtocol.WellKnownNamespaces.trace : FeedProtocol.WellKnownNamespaces.data;
  const messages = [];
  let cursor;
  while (true) {
    const result = await echoHost.queuesService.queryQueue({
      query: {
        spaceId,
        queueIds: [
          queueId
        ],
        queuesNamespace,
        after: cursor
      }
    });
    const batch = (result.objects ?? []).flatMap((encoded) => {
      try {
        return [
          JSON.parse(encoded)
        ];
      } catch (err) {
        log5.verbose("queue object JSON parse failed; object ignored", {
          encoded,
          error: err
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file7, L: 214, S: void 0 });
        return [];
      }
    });
    if (batch.length === 0) {
      break;
    }
    for (const message of batch) {
      messages.push(orderObjJsonFields(message));
    }
    if (!result.nextCursor || result.nextCursor === cursor) {
      break;
    }
    cursor = result.nextCursor;
  }
  return messages;
};

// src/packlets/space-export/space-archive-reader.ts
import { assertArgument as assertArgument3, failedInvariant as failedInvariant2, invariant as invariant5 } from "@dxos/invariant";
import { log as log6 } from "@dxos/log";
import { SpaceArchiveFileStructure } from "@dxos/protocols";
var __dxlog_file8 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/space-export/space-archive-reader.ts";
var extractSpaceArchive = async (archive) => {
  const { Archive } = await import("@obsidize/tar-browserify");
  const { entries } = await Archive.extract(archive.contents);
  const metadataEntry = entries.find((entry2) => entry2.fileName === SpaceArchiveFileStructure.metadata);
  assertArgument3(metadataEntry, "metadataEntry", "Metadata entry not found");
  const metadata = JSON.parse(metadataEntry.getContentAsText());
  const documents = {};
  for (const entry2 of entries.filter((entry3) => entry3.fileName.startsWith(`${SpaceArchiveFileStructure.documents}/`))) {
    const documentId = entry2.fileName.replace(`${SpaceArchiveFileStructure.documents}/`, "").replace(/\.bin$/, "");
    invariant5(!documentId.includes("/"), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file8, L: 16, S: void 0, A: ["!documentId.includes('/')", ""] });
    documents[documentId] = entry2.content ?? failedInvariant2();
  }
  const feeds = {};
  const feedsPrefix = `${SpaceArchiveFileStructure.feeds}/`;
  const feedEntries = entries.filter((entry2) => entry2.fileName.startsWith(feedsPrefix));
  const feedMetadataByFeedId = /* @__PURE__ */ new Map();
  const feedBlocksByFeedId = /* @__PURE__ */ new Map();
  for (const entry2 of feedEntries) {
    const relativePath = entry2.fileName.slice(feedsPrefix.length);
    const pathParts = relativePath.split("/");
    if (pathParts.length !== 2) {
      continue;
    }
    const [feedId, fileName] = pathParts;
    invariant5(feedId, "Feed ID is required", { "~LogMeta": "~LogMeta", F: __dxlog_file8, L: 31, S: void 0, A: ["feedId", "'Feed ID is required'"] });
    invariant5(fileName, "File name is required", { "~LogMeta": "~LogMeta", F: __dxlog_file8, L: 32, S: void 0, A: ["fileName", "'File name is required'"] });
    if (fileName === SpaceArchiveFileStructure.feedMetadata) {
      const feedMetadata = JSON.parse(entry2.getContentAsText());
      feedMetadataByFeedId.set(feedId, feedMetadata);
    } else if (fileName.startsWith(SpaceArchiveFileStructure.feedBlocksPrefix) && fileName.endsWith(".json")) {
      const chunkIndexStr = fileName.slice(SpaceArchiveFileStructure.feedBlocksPrefix.length).replace(/\.json$/, "");
      const chunkIndex = parseInt(chunkIndexStr, 10);
      invariant5(!isNaN(chunkIndex), `Invalid chunk index: ${chunkIndexStr}`, { "~LogMeta": "~LogMeta", F: __dxlog_file8, L: 39, S: void 0, A: ["!isNaN(chunkIndex)", "`Invalid chunk index: ${chunkIndexStr}`"] });
      const blocks = JSON.parse(entry2.getContentAsText());
      if (!feedBlocksByFeedId.has(feedId)) {
        feedBlocksByFeedId.set(feedId, /* @__PURE__ */ new Map());
      }
      feedBlocksByFeedId.get(feedId).set(chunkIndex, blocks);
    }
  }
  for (const [feedId, feedMetadata] of feedMetadataByFeedId) {
    const blockChunks = feedBlocksByFeedId.get(feedId) ?? /* @__PURE__ */ new Map();
    const sortedChunkIndices = Array.from(blockChunks.keys()).sort((a, b) => a - b);
    const allBlocks = [];
    for (const chunkIndex of sortedChunkIndices) {
      allBlocks.push(...blockChunks.get(chunkIndex));
    }
    feeds[feedId] = {
      metadata: feedMetadata,
      blocks: allBlocks
    };
  }
  log6("extracted space archive", {
    metadata,
    documents,
    feedCount: Object.keys(feeds).length
  }, { "~LogMeta": "~LogMeta", F: __dxlog_file8, L: 59, S: void 0 });
  return {
    metadata,
    documents,
    feeds
  };
};

// src/packlets/space-export/space-archive-writer.ts
import { Resource as Resource3 } from "@dxos/context";
import { assertArgument as assertArgument4, assertState as assertState3 } from "@dxos/invariant";
import { FEED_ARCHIVE_BLOCKS_PER_CHUNK, SpaceArchiveFileStructure as SpaceArchiveFileStructure2, SpaceArchiveVersion } from "@dxos/protocols";
import { SpaceArchive as SpaceArchive3 } from "@dxos/protocols/proto/dxos/client/services";
import { createFilename as createFilename2 } from "@dxos/util";
var CURRENT_VERSION = SpaceArchiveVersion.V1;
var SpaceArchiveWriter = class extends Resource3 {
  _tar;
  _archive;
  _meta = void 0;
  _currentRootUrl = void 0;
  async _open(ctx) {
    this._tar = await import("@obsidize/tar-browserify");
  }
  async _close() {
    return Promise.resolve();
  }
  async begin(meta) {
    assertState3(this._tar, "Not open");
    assertState3(!this._meta, "Already started");
    this._meta = meta;
    this._archive = new this._tar.Archive();
  }
  async setCurrentRootUrl(url) {
    assertArgument4(url.startsWith("automerge:"), "url", "Invalid root URL");
    assertState3(this._tar, "Not open");
    assertState3(this._meta, "Not started");
    this._currentRootUrl = url;
  }
  async writeDocument(documentId, data) {
    assertArgument4(!documentId.startsWith("automerge:"), "documentId", "Invalid document ID");
    assertState3(this._archive, "Not open");
    this._archive.addBinaryFile(`${SpaceArchiveFileStructure2.documents}/${documentId}.bin`, data);
  }
  /**
  * Writes a feed with its metadata and blocks to the archive.
  * Blocks are written in chunks of {@link FEED_ARCHIVE_BLOCKS_PER_CHUNK}.
  */
  async writeFeed(feedId, namespace, blocks) {
    assertArgument4(feedId, "feedId", "Feed ID is required");
    assertArgument4(namespace, "namespace", "Namespace is required");
    assertState3(this._archive, "Not open");
    const feedPath = `${SpaceArchiveFileStructure2.feeds}/${feedId}`;
    const metadata = {
      id: feedId,
      namespace
    };
    this._archive.addTextFile(`${feedPath}/${SpaceArchiveFileStructure2.feedMetadata}`, JSON.stringify(metadata));
    for (let chunkIndex = 0; chunkIndex * FEED_ARCHIVE_BLOCKS_PER_CHUNK < blocks.length; chunkIndex++) {
      const start = chunkIndex * FEED_ARCHIVE_BLOCKS_PER_CHUNK;
      const end = Math.min(start + FEED_ARCHIVE_BLOCKS_PER_CHUNK, blocks.length);
      const chunk = blocks.slice(start, end);
      this._archive.addTextFile(`${feedPath}/${SpaceArchiveFileStructure2.feedBlocksPrefix}${chunkIndex}.json`, JSON.stringify(chunk));
    }
  }
  async finish() {
    assertState3(this._archive, "Not open");
    assertState3(this._meta, "Not started");
    assertState3(this._meta.spaceId, "No space ID set");
    assertState3(this._currentRootUrl, "No root URL set");
    const metadata = {
      version: CURRENT_VERSION,
      createdAt: Date.now(),
      exportedBy: this._meta.exportedBy,
      originalSpaceId: this._meta.spaceId,
      echo: {
        currentRootUrl: this._currentRootUrl
      }
    };
    this._archive.addTextFile(SpaceArchiveFileStructure2.metadata, JSON.stringify(metadata));
    const binary = this._archive.toUint8Array();
    return {
      filename: createFilename2({
        parts: [
          this._meta.spaceId
        ],
        ext: "tar"
      }),
      contents: binary,
      format: SpaceArchive3.Format.BINARY
    };
  }
};

// src/packlets/spaces/spaces-service.ts
var __dxlog_file9 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/spaces/spaces-service.ts";
function _ts_add_disposable_resource2(env, value, async) {
  if (value !== null && value !== void 0) {
    if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
    var dispose, inner;
    if (async) {
      if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
      dispose = value[Symbol.asyncDispose];
    }
    if (dispose === void 0) {
      if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
      dispose = value[Symbol.dispose];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    env.stack.push({
      value,
      dispose,
      async
    });
  } else if (async) {
    env.stack.push({
      async: true
    });
  }
  return value;
}
function _ts_dispose_resources2(env) {
  var _SuppressedError = typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
  };
  return (_ts_dispose_resources2 = function _ts_dispose_resources6(env2) {
    function fail(e) {
      env2.error = env2.hasError ? new _SuppressedError(e, env2.error, "An error was suppressed during disposal.") : e;
      env2.hasError = true;
    }
    var r, s = 0;
    function next() {
      while (r = env2.stack.pop()) {
        try {
          if (!r.async && s === 1) return s = 0, env2.stack.push(r), Promise.resolve().then(next);
          if (r.dispose) {
            var result = r.dispose.call(r.value);
            if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) {
              fail(e);
              return next();
            });
          } else s |= 1;
        } catch (e) {
          fail(e);
        }
      }
      if (s === 1) return env2.hasError ? Promise.reject(env2.error) : Promise.resolve();
      if (env2.hasError) throw env2.error;
    }
    return next();
  })(env);
}
var SpacesServiceImpl = class {
  _identityManager;
  _spaceManager;
  _echoHost;
  _getDataSpaceManager;
  constructor(_identityManager, _spaceManager, _echoHost, _getDataSpaceManager) {
    this._identityManager = _identityManager;
    this._spaceManager = _spaceManager;
    this._echoHost = _echoHost;
    this._getDataSpaceManager = _getDataSpaceManager;
  }
  async createSpace(request, options) {
    this._requireIdentity();
    const ctx = options?.ctx ?? new Context5(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 95 });
    const dataSpaceManager = await this._getDataSpaceManager();
    const space = await dataSpaceManager.createSpace(ctx, {
      tags: request?.tags,
      membershipPolicy: request?.membershipPolicy
    });
    await this._updateMetrics();
    return this._serializeSpace(space);
  }
  async updateSpace({ spaceKey, state, edgeReplication }, options) {
    const ctx = options?.ctx ?? Context5.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 105 });
    const dataSpaceManager = await this._getDataSpaceManager();
    const space = dataSpaceManager.spaces.get(spaceKey) ?? raise(new SpaceNotFoundError(spaceKey));
    if (state) {
      switch (state) {
        case SpaceState2.SPACE_ACTIVE:
          await space.activate(ctx);
          break;
        case SpaceState2.SPACE_INACTIVE:
          await space.deactivate(ctx);
          break;
        case SpaceState2.SPACE_DELETED:
          await dataSpaceManager.markSpaceDeleted(ctx, spaceKey);
          return;
        default:
          throw new ApiError({
            message: "Invalid space state"
          });
      }
    }
    if (edgeReplication !== void 0) {
      await dataSpaceManager.setSpaceEdgeReplicationSetting(ctx, spaceKey, edgeReplication);
    }
  }
  async updateMemberRole(request) {
    const identity = this._requireIdentity();
    const space = this._spaceManager.spaces.get(request.spaceKey);
    if (space == null) {
      throw new SpaceNotFoundError(request.spaceKey);
    }
    if (!space.spaceState.hasMembershipManagementPermission(identity.identityKey)) {
      throw new AuthorizationError({
        message: "No member management permission.",
        context: {
          spaceKey: space.key,
          role: space.spaceState.getMemberRole(identity.identityKey)
        }
      });
    }
    const credentials = await createAdmissionCredentials2(identity.getIdentityCredentialSigner(), request.memberKey, space.key, space.genesisFeedKey, request.newRole, space.spaceState.membershipChainHeads);
    invariant6(credentials[0].credential, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 146, S: this, A: ["credentials[0].credential", ""] });
    const spaceMemberCredential = credentials[0].credential.credential;
    invariant6(getCredentialAssertion2(spaceMemberCredential)["@type"] === "dxos.halo.credentials.SpaceMember", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 148, S: this, A: ["getCredentialAssertion(spaceMemberCredential)['@type'] === 'dxos.halo.credentials.SpaceMember'", ""] });
    await writeMessages2(space.controlPipeline.writer, credentials);
  }
  querySpaces() {
    return new Stream7(({ next, ctx }) => {
      const scheduler = new UpdateScheduler(ctx, async () => {
        const dataSpaceManager = await this._getDataSpaceManager();
        const spaces = await Promise.all(Array.from(dataSpaceManager.spaces.values()).map((space) => this._serializeSpace(space)));
        log7("update", () => ({
          ids: spaces.map((space) => space.id)
        }), { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 156, S: this });
        await this._updateMetrics();
        next({
          spaces
        });
      }, {
        maxFrequency: false ? void 0 : 2
      });
      scheduleTask2(ctx, async () => {
        const dataSpaceManager = await this._getDataSpaceManager();
        const subscriptions = new SubscriptionList2();
        ctx.onDispose(() => subscriptions.clear());
        const subscribeSpaces = () => {
          subscriptions.clear();
          for (const space of dataSpaceManager.spaces.values()) {
            let lastState;
            subscriptions.add(space.stateUpdate.on(ctx, () => {
              if (space.state !== lastState) {
                scheduler.forceTrigger();
              } else {
                scheduler.trigger();
              }
              lastState = space.state;
            }));
            subscriptions.add(space.presence.updated.on(ctx, () => scheduler.trigger()));
            subscriptions.add(space.automergeSpaceState.onNewEpoch.on(ctx, () => scheduler.trigger()));
            subscriptions.add(space.inner.controlPipeline.state.timeframeUpdate.on(ctx, () => scheduler.trigger()));
          }
        };
        dataSpaceManager.updated.on(ctx, () => {
          subscribeSpaces();
          scheduler.trigger();
        });
        subscribeSpaces();
        scheduler.trigger();
      });
      if (!this._identityManager.identity) {
        next({
          spaces: []
        });
      }
    });
  }
  async postMessage({ spaceKey, channel, message }) {
    const dataSpaceManager = await this._getDataSpaceManager();
    const space = dataSpaceManager.spaces.get(spaceKey) ?? raise(new SpaceNotFoundError(spaceKey));
    await space.postMessage(getChannelId(channel), message);
  }
  subscribeMessages({ spaceKey, channel }) {
    return new Stream7(({ ctx, next }) => {
      scheduleTask2(ctx, async () => {
        const dataSpaceManager = await this._getDataSpaceManager();
        const space = dataSpaceManager.spaces.get(spaceKey) ?? raise(new SpaceNotFoundError(spaceKey));
        const handle = space.listen(getChannelId(channel), (message) => {
          next(message);
        });
        ctx.onDispose(() => handle.unsubscribe());
      });
    });
  }
  queryCredentials({ spaceKey, noTail }) {
    return new Stream7(({ ctx, next, close }) => {
      const space = this._spaceManager.spaces.get(spaceKey) ?? raise(new SpaceNotFoundError(spaceKey));
      const processor = {
        processCredential: async (credential) => {
          next(credential);
        }
      };
      ctx.onDispose(() => space.spaceState.removeCredentialProcessor(processor));
      scheduleTask2(ctx, async () => {
        await space.spaceState.addCredentialProcessor(processor);
        if (noTail) {
          close();
        }
      });
    });
  }
  async writeCredentials({ spaceKey, credentials }) {
    const space = this._spaceManager.spaces.get(spaceKey) ?? raise(new SpaceNotFoundError(spaceKey));
    for (const credential of credentials ?? []) {
      if (credential.proof) {
        await space.controlPipeline.writer.write({
          credential: {
            credential
          }
        });
      } else {
        invariant6(!credential.id, "Id on unsigned credentials is not allowed", { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 248, S: this, A: ["!credential.id", "'Id on unsigned credentials is not allowed'"] });
        invariant6(this._identityManager.identity, "Identity is not available", { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 249, S: this, A: ["this._identityManager.identity", "'Identity is not available'"] });
        const signer = this._identityManager.identity.getIdentityCredentialSigner();
        invariant6(credential.issuer.equals(signer.getIssuer()), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 251, S: this, A: ["credential.issuer.equals(signer.getIssuer())", ""] });
        const signedCredential = await signer.createCredential({
          subject: credential.subject.id,
          assertion: credential.subject.assertion
        });
        await space.controlPipeline.writer.write({
          credential: {
            credential: signedCredential
          }
        });
      }
    }
  }
  async createEpoch({ spaceKey, migration, automergeRootUrl }) {
    const dataSpaceManager = await this._getDataSpaceManager();
    const space = dataSpaceManager.spaces.get(spaceKey) ?? raise(new SpaceNotFoundError(spaceKey));
    const result = await space.createEpoch({
      migration,
      newAutomergeRoot: automergeRootUrl
    });
    return {
      epochCredential: result?.credential,
      controlTimeframe: result?.timeframe
    };
  }
  async admitContact(request) {
    const dataSpaceManager = await this._getDataSpaceManager();
    await dataSpaceManager.admitMember({
      spaceKey: request.spaceKey,
      identityKey: request.contact.identityKey,
      role: request.role
    });
  }
  async joinBySpaceKey({ spaceKey }, options) {
    const ctx = options?.ctx ?? Context5.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 285 });
    const dataSpaceManager = await this._getDataSpaceManager();
    const credential = await dataSpaceManager.requestSpaceAdmissionCredential(ctx, spaceKey);
    return this._joinByAdmission(ctx, {
      credential
    });
  }
  async exportSpace(request) {
    const env = {
      stack: [],
      error: void 0,
      hasError: false
    };
    try {
      assertArgument5(SpaceId.isValid(request.spaceId), "spaceId", "Invalid space ID");
      const dataSpaceManager = await this._getDataSpaceManager();
      const space = dataSpaceManager.getSpaceById(request.spaceId) ?? raise(new Error("Space not found"));
      const format = request.format ?? SpaceArchive4.Format.BINARY;
      if (format === SpaceArchive4.Format.JSON) {
        const archive2 = await writeSerializedSpaceArchive({
          space,
          echoHost: this._echoHost
        });
        return {
          archive: archive2
        };
      }
      const writer = _ts_add_disposable_resource2(env, await new SpaceArchiveWriter().open(), true);
      await writer.begin({
        spaceId: space.id
      });
      const rootUrl = space.automergeSpaceState.lastEpoch?.subject.assertion.automergeRoot;
      assertState4(rootUrl, "Space does not have a root URL");
      await writer.setCurrentRootUrl(rootUrl);
      for await (const [documentId, data] of space.getAllDocuments()) {
        await writer.writeDocument(documentId, data);
      }
      const feeds = await space.getAllFeeds();
      for (const feed of feeds) {
        const archiveBlocks = feed.blocks.map((block) => ({
          actorId: block.actorId,
          sequence: block.sequence,
          prevActorId: block.prevActorId,
          prevSequence: block.prevSequence,
          position: block.position,
          timestamp: block.timestamp,
          data: Buffer.from(block.data).toString("base64")
        }));
        await writer.writeFeed(feed.feedId, feed.feedNamespace, archiveBlocks);
      }
      const archive = await writer.finish();
      return {
        archive
      };
    } catch (e) {
      env.error = e;
      env.hasError = true;
    } finally {
      const result = _ts_dispose_resources2(env);
      if (result) await result;
    }
  }
  async importSpace(request, options) {
    const ctx = options?.ctx ?? Context5.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 348 });
    const dataSpaceManager = await this._getDataSpaceManager();
    const format = request.archive.format ?? detectSpaceArchiveFormat(request.archive);
    if (format === SpaceArchive4.Format.JSON) {
      const serialized = readSerializedSpaceArchive(request.archive);
      const space2 = await dataSpaceManager.createSpace(ctx, {
        tags: request.tags
      });
      await this._hydrateSpaceFromSerialized(space2, serialized);
      await this._updateMetrics();
      return {
        newSpaceId: space2.id
      };
    }
    const extracted = await extractSpaceArchive(request.archive);
    invariant6(extracted.metadata.echo?.currentRootUrl, "Space archive does not contain a root URL", { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 363, S: this, A: ["extracted.metadata.echo?.currentRootUrl", "'Space archive does not contain a root URL'"] });
    const space = await dataSpaceManager.createSpace(ctx, {
      documents: extracted.documents,
      rootUrl: extracted.metadata.echo?.currentRootUrl,
      tags: request.tags
    });
    await this._updateMetrics();
    return {
      newSpaceId: space.id
    };
  }
  /**
  * Populate a freshly-created space with the objects and feed messages described in a {@link SerializedSpace}.
  *
  * Objects are written directly into the space's automerge root document as inline
  * {@link EntityStructure} entries; feed messages are appended to the appropriate queue
  * via {@link EchoHost.queuesService}.
  */
  async _hydrateSpaceFromSerialized(space, serialized) {
    const databaseRoot = space.databaseRoot;
    assertState4(databaseRoot, "Space database root is not ready");
    databaseRoot.handle.change((doc) => {
      if (!doc.objects) {
        doc.objects = {};
      }
      for (const obj of serialized.objects) {
        doc.objects[obj.id] = objJsonToObjectStructure(obj);
      }
    });
    for (const feed of serialized.feeds ?? []) {
      if (feed.messages.length === 0) {
        continue;
      }
      const namespace = feed.namespace === "trace" ? FeedProtocol2.WellKnownNamespaces.trace : FeedProtocol2.WellKnownNamespaces.data;
      try {
        await this._echoHost.queuesService.insertIntoQueue({
          spaceId: space.id,
          queueId: feed.feedObjectId,
          subspaceTag: namespace,
          objects: feed.messages.map((message) => JSON.stringify(message))
        });
      } catch (err) {
        log7.warn("failed to import feed data", {
          feedObjectId: feed.feedObjectId,
          error: err
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 404, S: this });
      }
    }
  }
  async _joinByAdmission(ctx, { credential }) {
    const assertion = getCredentialAssertion2(credential);
    invariant6(assertion["@type"] === "dxos.halo.credentials.SpaceMember", "Invalid credential", { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 413, S: this, A: ["assertion['@type'] === 'dxos.halo.credentials.SpaceMember'", "'Invalid credential'"] });
    const myIdentity = this._identityManager.identity;
    invariant6(myIdentity && credential.subject.id.equals(myIdentity.identityKey), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file9, L: 415, S: this, A: ["myIdentity && credential.subject.id.equals(myIdentity.identityKey)", ""] });
    const dataSpaceManager = await this._getDataSpaceManager();
    let dataSpace = dataSpaceManager.spaces.get(assertion.spaceKey);
    if (!dataSpace) {
      dataSpace = await dataSpaceManager.acceptSpace(ctx, {
        spaceKey: assertion.spaceKey,
        genesisFeedKey: assertion.genesisFeedKey,
        tags: assertion.tags
      });
      await myIdentity.controlPipeline.writer.write({
        credential: {
          credential
        }
      });
    }
    return {
      space: await this._serializeSpace(dataSpace)
    };
  }
  async _serializeSpace(space) {
    return {
      id: space.id,
      spaceKey: space.key,
      state: space.state,
      error: space.error ? encodeError(space.error) : void 0,
      pipeline: {
        currentEpoch: space.automergeSpaceState.lastEpoch,
        appliedEpoch: space.automergeSpaceState.lastEpoch,
        controlFeeds: space.inner.controlPipeline.state.feeds.map((feed) => feed.key),
        currentControlTimeframe: space.inner.controlPipeline.state.timeframe,
        targetControlTimeframe: space.inner.controlPipeline.state.targetTimeframe,
        totalControlTimeframe: space.inner.controlPipeline.state.endTimeframe,
        dataFeeds: void 0,
        startDataTimeframe: void 0,
        currentDataTimeframe: void 0,
        targetDataTimeframe: void 0,
        totalDataTimeframe: void 0,
        spaceRootUrl: space.databaseRoot?.url
      },
      members: await Promise.all(Array.from(space.inner.spaceState.members.values()).map(async (member) => {
        const peers = space.presence.getPeersOnline().filter(({ identityKey }) => identityKey.equals(member.key));
        const isMe = this._identityManager.identity?.identityKey.equals(member.key);
        if (isMe) {
          peers.push(space.presence.getLocalState());
        }
        return {
          identity: {
            did: await createDidFromIdentityKey2(member.key),
            identityKey: member.key,
            profile: member.profile ?? {}
          },
          role: member.role,
          presence: peers.length > 0 ? SpaceMember4.PresenceState.ONLINE : SpaceMember4.PresenceState.OFFLINE,
          peerStates: peers
        };
      })),
      creator: space.inner.spaceState.creator?.key,
      tags: space.tags,
      membershipPolicy: space.membershipPolicy,
      cache: space.cache,
      metrics: space.metrics,
      edgeReplication: space.getEdgeReplicationSetting()
    };
  }
  _requireIdentity() {
    if (!this._identityManager.identity) {
      throw new IdentityNotInitializedError({
        message: "This device has no HALO identity available. See https://docs.dxos.org/guide/platform/halo"
      });
    }
    return this._identityManager.identity;
  }
  async _updateMetrics() {
    const dataSpaceManager = await this._getDataSpaceManager();
    const identity = this._identityManager.identity?.identityKey.truncate();
    if (identity) {
      trace2.metrics.gauge("dxos.echo.space.count", dataSpaceManager.spaces.size, {
        tags: {
          identity
        }
      });
    }
  }
};
var getChannelId = (channel) => `user-channel/${channel}`;

// src/packlets/identity/identity.ts
var __dxlog_file10 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/identity/identity.ts";
function _ts_decorate3(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var Identity = class {
  space;
  _signer;
  _presence;
  _deviceStateMachine;
  _profileStateMachine;
  _edgeFeedReplicator = void 0;
  authVerifier;
  did;
  identityKey;
  deviceKey;
  stateUpdate = new Event3();
  constructor(params) {
    this.space = params.space;
    this._signer = params.signer;
    this._presence = params.presence;
    this.did = params.did;
    this.identityKey = params.identityKey;
    this.deviceKey = params.deviceKey;
    log8.trace("dxos.halo.device", {
      deviceKey: params.deviceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file10, L: 41, S: this });
    this._deviceStateMachine = new DeviceStateMachine({
      identityKey: this.identityKey,
      deviceKey: this.deviceKey,
      onUpdate: () => this.stateUpdate.emit()
    });
    this._profileStateMachine = new ProfileStateMachine({
      identityKey: this.identityKey,
      onUpdate: () => this.stateUpdate.emit()
    });
    this.authVerifier = new TrustedKeySetAuthVerifier({
      trustedKeysProvider: () => new ComplexSet(PublicKey5.hash, this.authorizedDeviceKeys.keys()),
      update: this.stateUpdate,
      authTimeout: AUTH_TIMEOUT
    });
    if (params.edgeConnection && params.edgeFeatures?.feedReplicator) {
      this._edgeFeedReplicator = new EdgeFeedReplicator({
        messenger: params.edgeConnection,
        spaceId: this.space.id
      });
    }
  }
  // TODO(burdon): Expose state object?
  get authorizedDeviceKeys() {
    return this._deviceStateMachine.authorizedDeviceKeys;
  }
  async open(ctx) {
    await this._presence?.open();
    await this.space.spaceState.addCredentialProcessor(this._deviceStateMachine);
    await this.space.spaceState.addCredentialProcessor(this._profileStateMachine);
    if (this._edgeFeedReplicator) {
      this.space.protocol.feedAdded.append(this._onFeedAdded);
    }
    await this.space.open(ctx);
  }
  async joinNetwork(ctx) {
    await this.space.startProtocol(ctx);
    await this._edgeFeedReplicator?.open();
  }
  async close(ctx) {
    await this._presence?.close();
    await this.authVerifier.close();
    await this.space.spaceState.removeCredentialProcessor(this._profileStateMachine);
    await this.space.spaceState.removeCredentialProcessor(this._deviceStateMachine);
    if (this._edgeFeedReplicator) {
      this.space.protocol.feedAdded.remove(this._onFeedAdded);
    }
    await this._edgeFeedReplicator?.close();
    await this.space.close(ctx);
  }
  async ready() {
    await this._deviceStateMachine.deviceChainReady.wait();
    await this.controlPipeline.state.waitUntilReachedTargetTimeframe({
      timeout: LOAD_CONTROL_FEEDS_TIMEOUT
    });
  }
  get profileDocument() {
    return this._profileStateMachine.profile;
  }
  /**
  * @test-only
  */
  get controlPipeline() {
    return this.space.controlPipeline;
  }
  get haloSpaceId() {
    return this.space.id;
  }
  get haloSpaceKey() {
    return this.space.key;
  }
  get haloGenesisFeedKey() {
    return this.space.genesisFeedKey;
  }
  get deviceCredentialChain() {
    return this._deviceStateMachine.deviceCredentialChain;
  }
  get presence() {
    return this._presence;
  }
  get signer() {
    return this._signer;
  }
  /**
  * Issues credentials as identity.
  * Requires identity to be ready.
  */
  getIdentityCredentialSigner() {
    invariant7(this._deviceStateMachine.deviceCredentialChain, "Device credential chain is not ready.", { "~LogMeta": "~LogMeta", F: __dxlog_file10, L: 129, S: this, A: ["this._deviceStateMachine.deviceCredentialChain", "'Device credential chain is not ready.'"] });
    return createCredentialSignerWithChain(this._signer, this._deviceStateMachine.deviceCredentialChain, this.deviceKey);
  }
  /**
  * Issues credentials as device.
  */
  getDeviceCredentialSigner() {
    return createCredentialSignerWithKey(this._signer, this.deviceKey);
  }
  async admitDevice({ deviceKey, controlFeedKey, dataFeedKey }) {
    log8("Admitting device:", {
      identityKey: this.identityKey,
      hostDevice: this.deviceKey,
      deviceKey,
      controlFeedKey,
      dataFeedKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file10, L: 138, S: this });
    const signer = this.getIdentityCredentialSigner();
    const deviceCredential = await signer.createCredential({
      subject: deviceKey,
      assertion: {
        "@type": "dxos.halo.credentials.AuthorizedDevice",
        "identityKey": this.identityKey,
        deviceKey
      }
    });
    await writeMessages3(this.controlPipeline.writer, [
      deviceCredential,
      await signer.createCredential({
        subject: controlFeedKey,
        assertion: {
          "@type": "dxos.halo.credentials.AdmittedFeed",
          "spaceKey": this.haloSpaceKey,
          deviceKey,
          "identityKey": this.identityKey,
          "designation": AdmittedFeed2.Designation.CONTROL
        }
      }),
      await signer.createCredential({
        subject: dataFeedKey,
        assertion: {
          "@type": "dxos.halo.credentials.AdmittedFeed",
          "spaceKey": this.haloSpaceKey,
          deviceKey,
          "identityKey": this.identityKey,
          "designation": AdmittedFeed2.Designation.DATA
        }
      })
    ].map((credential) => ({
      credential: {
        credential
      }
    })));
    return deviceCredential;
  }
  _onFeedAdded = async (feed) => {
    await this._edgeFeedReplicator.addFeed(feed);
  };
};
_ts_decorate3([
  trace3.span({
    op: "lifecycle"
  })
], Identity.prototype, "open", null);
_ts_decorate3([
  trace3.span({
    op: "lifecycle"
  })
], Identity.prototype, "close", null);
Identity = _ts_decorate3([
  trace3.resource()
], Identity);

// src/packlets/identity/identity-manager.ts
import platform from "platform";
import { Event as Event4 } from "@dxos/async";
import { Context as Context6 } from "@dxos/context";
import { CredentialGenerator, createCredentialSignerWithKey as createCredentialSignerWithKey2, createDidFromIdentityKey as createDidFromIdentityKey3 } from "@dxos/credentials";
import { invariant as invariant8 } from "@dxos/invariant";
import { log as log9 } from "@dxos/log";
import { Device, DeviceKind } from "@dxos/protocols/proto/dxos/client/services";
import { AdmittedFeed as AdmittedFeed3, DeviceType } from "@dxos/protocols/proto/dxos/halo/credentials";
import { Gossip as Gossip2, Presence as Presence2 } from "@dxos/teleport-extension-gossip";
import { Timeframe as Timeframe2 } from "@dxos/timeframe";
import { trace as Trace } from "@dxos/tracing";
import { deferFunction as deferFunction2, isNode, isTauri } from "@dxos/util";
var __dxlog_file11 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/identity/identity-manager.ts";
function _ts_decorate4(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var DEVICE_PRESENCE_ANNOUNCE_INTERVAL = 1e4;
var DEVICE_PRESENCE_OFFLINE_TIMEOUT = 2e4;
var IdentityManager = class {
  stateUpdate = new Event4();
  _metadataStore;
  _keyring;
  _feedStore;
  _spaceManager;
  _devicePresenceAnnounceInterval;
  _devicePresenceOfflineTimeout;
  _edgeConnection;
  _edgeFeatures;
  _identity;
  // TODO(dmaretskyi): Perhaps this should take/generate the peerKey outside of an initialized identity.
  constructor(params) {
    this._metadataStore = params.metadataStore;
    this._keyring = params.keyring;
    this._feedStore = params.feedStore;
    this._spaceManager = params.spaceManager;
    this._edgeConnection = params.edgeConnection;
    this._edgeFeatures = params.edgeFeatures;
    this._devicePresenceAnnounceInterval = params.devicePresenceAnnounceInterval ?? DEVICE_PRESENCE_ANNOUNCE_INTERVAL;
    this._devicePresenceOfflineTimeout = params.devicePresenceOfflineTimeout ?? DEVICE_PRESENCE_OFFLINE_TIMEOUT;
  }
  get identity() {
    return this._identity;
  }
  async open(ctx) {
    log9("opening identity manager", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 52, S: this });
    const identityRecord = this._metadataStore.getIdentityRecord();
    log9("identity record", {
      identityRecord
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 54, S: this });
    if (identityRecord) {
      this._identity = await this._constructIdentity(identityRecord);
      await this._identity.open(ctx);
      await this._identity.ready();
      log9.trace("dxos.halo.identity", {
        identityKey: identityRecord.identityKey,
        displayName: this._identity.profileDocument?.displayName
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 61, S: this });
      this.stateUpdate.emit();
    }
    log9("opened identity manager", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 67, S: this });
  }
  async close(ctx) {
    await this._identity?.close(ctx);
  }
  async createIdentity({ profile, deviceProfile } = {}, ctx) {
    invariant8(!this._identity, "Identity already exists.", { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 73, S: this, A: ["!this._identity", "'Identity already exists.'"] });
    log9("creating identity...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 74, S: this });
    const controlFeedKey = await this._keyring.createKey();
    const identityRecord = {
      identityKey: await this._keyring.createKey(),
      deviceKey: await this._keyring.createKey(),
      haloSpace: {
        key: await this._keyring.createKey(),
        genesisFeedKey: controlFeedKey,
        controlFeedKey,
        dataFeedKey: await this._keyring.createKey()
      }
    };
    const identity = await this._constructIdentity(identityRecord);
    await identity.open(ctx ?? Context6.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 87 }));
    {
      const generator = new CredentialGenerator(this._keyring, identityRecord.identityKey, identityRecord.deviceKey);
      invariant8(identityRecord.haloSpace.genesisFeedKey, "Genesis feed key is required.", { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 90, S: this, A: ["identityRecord.haloSpace.genesisFeedKey", "'Genesis feed key is required.'"] });
      invariant8(identityRecord.haloSpace.dataFeedKey, "Data feed key is required.", { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 91, S: this, A: ["identityRecord.haloSpace.dataFeedKey", "'Data feed key is required.'"] });
      const credentials = [
        // Space genesis.
        ...await generator.createSpaceGenesis(identityRecord.haloSpace.key, identityRecord.haloSpace.genesisFeedKey),
        // Feed admission.
        await generator.createFeedAdmission(identityRecord.haloSpace.key, identityRecord.haloSpace.dataFeedKey, AdmittedFeed3.Designation.DATA)
      ];
      if (profile) {
        credentials.push(await generator.createProfileCredential(profile));
      }
      credentials.push(await generator.createDeviceAuthorization(identityRecord.deviceKey));
      credentials.push(await generator.createDeviceProfile({
        ...this.createDefaultDeviceProfile(),
        ...deviceProfile
      }));
      for (const credential of credentials) {
        await identity.controlPipeline.writer.write({
          credential: {
            credential
          }
        });
      }
    }
    await this._metadataStore.setIdentityRecord(identityRecord);
    this._identity = identity;
    await this._identity.ready();
    log9.trace("dxos.halo.identity", {
      identityKey: identityRecord.identityKey,
      displayName: this._identity.profileDocument?.displayName
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 120, S: this });
    this.stateUpdate.emit();
    log9("created identity", {
      identityKey: identity.identityKey,
      deviceKey: identity.deviceKey,
      profile: identity.profileDocument
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 125, S: this });
    return identity;
  }
  createDefaultDeviceProfile() {
    let type;
    if (isNode()) {
      type = DeviceType.AGENT;
    } else {
      if (platform.name?.startsWith("iOS") || platform.name?.startsWith("Android")) {
        type = DeviceType.MOBILE;
      } else if (isTauri() || !platform.name) {
        type = DeviceType.NATIVE;
      } else {
        type = DeviceType.BROWSER;
      }
    }
    const os = platform.os?.family === "OS X" ? "macOS" : platform.os?.family;
    const name = type === DeviceType.NATIVE || type === DeviceType.MOBILE ? "App" : platform.name;
    return {
      type,
      platform: name,
      platformVersion: platform.version,
      architecture: typeof platform.os?.architecture === "number" ? String(platform.os.architecture) : void 0,
      os,
      osVersion: platform.os?.version
    };
  }
  /**
  * Prepare an identity object as the first step of acceptIdentity flow.
  */
  async prepareIdentity(params, ctx) {
    log9("accepting identity", {
      params
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 162, S: this });
    invariant8(!this._identity, "Identity already exists.", { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 165, S: this, A: ["!this._identity", "'Identity already exists.'"] });
    const identityRecord = {
      identityKey: params.identityKey,
      deviceKey: params.deviceKey,
      haloSpace: {
        key: params.haloSpaceKey,
        genesisFeedKey: params.haloGenesisFeedKey,
        controlFeedKey: params.controlFeedKey,
        dataFeedKey: params.dataFeedKey,
        controlTimeframe: params.controlTimeframe
      }
    };
    const identity = await this._constructIdentity(identityRecord);
    await identity.open(ctx ?? Context6.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 178 }));
    return {
      identity,
      identityRecord
    };
  }
  /**
  * Accept an existing identity. Expects its device key to be authorized (now or later).
  */
  async acceptIdentity(identity, identityRecord, profile) {
    this._identity = identity;
    await this._identity.ready();
    await this._metadataStore.setIdentityRecord(identityRecord);
    log9.trace("dxos.halo.identity", {
      identityKey: this._identity.identityKey,
      displayName: this._identity.profileDocument?.displayName
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 191, S: this });
    await this.updateDeviceProfile({
      ...this.createDefaultDeviceProfile(),
      ...profile
    });
    this.stateUpdate.emit();
    log9("accepted identity", {
      identityKey: identity.identityKey,
      deviceKey: identity.deviceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 200, S: this });
  }
  /**
  * Update the profile document of an existing identity.
  */
  async updateProfile(profile) {
    invariant8(this._identity, "Identity not initialized.", { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 208, S: this, A: ["this._identity", "'Identity not initialized.'"] });
    const credential = await this._identity.getIdentityCredentialSigner().createCredential({
      subject: this._identity.identityKey,
      assertion: {
        "@type": "dxos.halo.credentials.IdentityProfile",
        profile
      }
    });
    const receipt = await this._identity.controlPipeline.writer.write({
      credential: {
        credential
      }
    });
    await this._identity.controlPipeline.state.waitUntilTimeframe(new Timeframe2([
      [
        receipt.feedKey,
        receipt.seq
      ]
    ]));
    this.stateUpdate.emit();
    return profile;
  }
  async updateDeviceProfile(profile) {
    invariant8(this._identity, "Identity not initialized.", { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 232, S: this, A: ["this._identity", "'Identity not initialized.'"] });
    const credential = await this._identity.getDeviceCredentialSigner().createCredential({
      subject: this._identity.deviceKey,
      assertion: {
        "@type": "dxos.halo.credentials.DeviceProfile",
        profile
      }
    });
    const receipt = await this._identity.controlPipeline.writer.write({
      credential: {
        credential
      }
    });
    await this._identity.controlPipeline.state.waitUntilTimeframe(new Timeframe2([
      [
        receipt.feedKey,
        receipt.seq
      ]
    ]));
    this.stateUpdate.emit();
    return {
      deviceKey: this._identity.deviceKey,
      kind: DeviceKind.CURRENT,
      presence: Device.PresenceState.ONLINE,
      profile
    };
  }
  async _constructIdentity(identityRecord) {
    invariant8(!this._identity, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 263, S: this, A: ["!this._identity", ""] });
    log9("constructing identity", {
      identityRecord
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 264, S: this });
    const gossip = new Gossip2({
      localPeerId: identityRecord.deviceKey
    });
    const presence = new Presence2({
      announceInterval: this._devicePresenceAnnounceInterval,
      offlineTimeout: this._devicePresenceOfflineTimeout,
      identityKey: identityRecord.deviceKey,
      gossip
    });
    invariant8(identityRecord.haloSpace.controlFeedKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 277, S: this, A: ["identityRecord.haloSpace.controlFeedKey", ""] });
    const controlFeed = await this._feedStore.openFeed(identityRecord.haloSpace.controlFeedKey, {
      writable: true
    });
    invariant8(identityRecord.haloSpace.dataFeedKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 281, S: this, A: ["identityRecord.haloSpace.dataFeedKey", ""] });
    const dataFeed = await this._feedStore.openFeed(identityRecord.haloSpace.dataFeedKey, {
      writable: true,
      sparse: true
    });
    const space = await this._constructSpace({
      spaceRecord: identityRecord.haloSpace,
      swarmIdentity: {
        identityKey: identityRecord.identityKey,
        peerKey: identityRecord.deviceKey,
        credentialProvider: createAuthProvider(createCredentialSignerWithKey2(this._keyring, identityRecord.deviceKey)),
        credentialAuthenticator: deferFunction2(() => identity.authVerifier.verifier)
      },
      gossip,
      identityKey: identityRecord.identityKey
    });
    await space.setControlFeed(controlFeed);
    await space.setDataFeed(dataFeed);
    const did = await createDidFromIdentityKey3(identityRecord.identityKey);
    const identity = new Identity({
      space,
      presence,
      signer: this._keyring,
      did,
      identityKey: identityRecord.identityKey,
      deviceKey: identityRecord.deviceKey,
      edgeConnection: this._edgeConnection,
      edgeFeatures: this._edgeFeatures
    });
    log9("done", {
      identityKey: identityRecord.identityKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 310, S: this });
    if (identityRecord.haloSpace.controlTimeframe) {
      identity.controlPipeline.state.setTargetTimeframe(identityRecord.haloSpace.controlTimeframe);
    }
    identity.stateUpdate.on(() => this.stateUpdate.emit());
    return identity;
  }
  async _constructSpace({ spaceRecord, swarmIdentity, identityKey, gossip }) {
    return this._spaceManager.constructSpace({
      metadata: {
        key: spaceRecord.key,
        genesisFeedKey: spaceRecord.genesisFeedKey
      },
      swarmIdentity,
      onAuthorizedConnection: (session) => {
        session.addExtension("dxos.mesh.teleport.gossip", gossip.createExtension({
          remotePeerId: session.remotePeerId
        }));
      },
      onAuthFailure: () => {
        log9.warn("auth failure", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file11, L: 333, S: this });
      },
      memberKey: identityKey,
      onDelegatedInvitationStatusChange: async () => {
      },
      onMemberRolesChanged: async () => {
      }
    });
  }
};
_ts_decorate4([
  Trace.span({
    showInBrowserTimeline: true
  })
], IdentityManager.prototype, "open", null);
IdentityManager = _ts_decorate4([
  Trace.resource()
], IdentityManager);

// src/packlets/identity/identity-service.ts
import { Stream as Stream8 } from "@dxos/codec-protobuf/stream";
import { Context as Context7, Resource as Resource4 } from "@dxos/context";
import { createCredential as createCredential2, signPresentation } from "@dxos/credentials";
import { invariant as invariant9 } from "@dxos/invariant";
var __dxlog_file12 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/identity/identity-service.ts";
var IdentityServiceImpl = class extends Resource4 {
  _identityManager;
  _recoveryManager;
  _keyring;
  _createIdentity;
  _onProfileUpdate;
  constructor(_identityManager, _recoveryManager, _keyring, _createIdentity, _onProfileUpdate) {
    super(), this._identityManager = _identityManager, this._recoveryManager = _recoveryManager, this._keyring = _keyring, this._createIdentity = _createIdentity, this._onProfileUpdate = _onProfileUpdate;
  }
  async createIdentity(request, options) {
    const ctx = options?.ctx ?? Context7.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file12, L: 18 });
    await this._createIdentity({
      profile: request.profile,
      deviceProfile: request.deviceProfile
    }, ctx);
    return this._getIdentity();
  }
  queryIdentity() {
    return new Stream8(({ next }) => {
      const emitNext = () => next({
        identity: this._getIdentity()
      });
      emitNext();
      return this._identityManager.stateUpdate.on(emitNext);
    });
  }
  _getIdentity() {
    if (!this._identityManager.identity) {
      return void 0;
    }
    return {
      did: this._identityManager.identity.did,
      identityKey: this._identityManager.identity.identityKey,
      spaceKey: this._identityManager.identity.space.key,
      profile: this._identityManager.identity.profileDocument
    };
  }
  async updateProfile(profile) {
    invariant9(this._identityManager.identity, "Identity not initialized.", { "~LogMeta": "~LogMeta", F: __dxlog_file12, L: 46, S: this, A: ["this._identityManager.identity", "'Identity not initialized.'"] });
    await this._identityManager.updateProfile(profile);
    await this._onProfileUpdate?.(this._identityManager.identity.profileDocument);
    return this._getIdentity();
  }
  async createRecoveryCredential(request) {
    return this._recoveryManager.createRecoveryCredential(request);
  }
  async requestRecoveryChallenge(_request, options) {
    return this._recoveryManager.requestRecoveryChallenge(options?.ctx ?? Context7.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file12, L: 55 }));
  }
  async recoverIdentity(request, options) {
    const ctx = options?.ctx ?? Context7.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file12, L: 58 });
    if (request.recoveryCode) {
      await this._recoveryManager.recoverIdentity(ctx, {
        recoveryCode: request.recoveryCode
      });
    } else if (request.external) {
      await this._recoveryManager.recoverIdentityWithExternalSignature(ctx, request.external);
    } else if (request.token) {
      await this._recoveryManager.recoverIdentityWithToken(ctx, {
        token: request.token
      });
    } else if (request.recoveryProof) {
      await this._recoveryManager.recoverIdentityWithToken(ctx, {
        recoveryProof: request.recoveryProof
      });
    } else {
      throw new Error("Invalid request.");
    }
    return this._getIdentity();
  }
  // TODO(burdon): Rename createPresentation?
  async signPresentation({ presentation, nonce }) {
    invariant9(this._identityManager.identity, "Identity not initialized.", { "~LogMeta": "~LogMeta", F: __dxlog_file12, L: 80, S: this, A: ["this._identityManager.identity", "'Identity not initialized.'"] });
    return await signPresentation({
      presentation,
      signer: this._keyring,
      signerKey: this._identityManager.identity.deviceKey,
      chain: this._identityManager.identity.deviceCredentialChain,
      nonce
    });
  }
  async createAuthCredential() {
    const identity = this._identityManager.identity;
    invariant9(identity, "Identity not initialized.", { "~LogMeta": "~LogMeta", F: __dxlog_file12, L: 91, S: this, A: ["identity", "'Identity not initialized.'"] });
    return await createCredential2({
      assertion: {
        "@type": "dxos.halo.credentials.Auth"
      },
      issuer: identity.identityKey,
      subject: identity.identityKey,
      chain: identity.deviceCredentialChain,
      signingKey: identity.deviceKey,
      signer: this._keyring
    });
  }
};

// src/packlets/spaces/automerge-space-state.ts
import { Event as Event5 } from "@dxos/async";
import { Resource as Resource5 } from "@dxos/context";
import { checkCredentialType } from "@dxos/credentials";
var AutomergeSpaceState = class extends Resource5 {
  _onNewRoot;
  rootUrl = void 0;
  lastEpoch = void 0;
  onNewEpoch = new Event5();
  _isProcessingRootDocs = false;
  constructor(_onNewRoot) {
    super(), this._onNewRoot = _onNewRoot;
  }
  async _open(ctx) {
  }
  async _close(ctx) {
    this._isProcessingRootDocs = false;
  }
  async processCredential(credential) {
    if (!checkCredentialType(credential, "dxos.halo.credentials.Epoch")) {
      return;
    }
    this.lastEpoch = credential;
    if (credential.subject.assertion.automergeRoot) {
      this.rootUrl = credential.subject.assertion.automergeRoot;
      if (this._isProcessingRootDocs) {
        this._onNewRoot(this.rootUrl);
      }
    }
    this.onNewEpoch.emit(credential);
  }
  startProcessingRootDocs() {
    if (this._isProcessingRootDocs) {
      return;
    }
    if (this.rootUrl) {
      this._onNewRoot(this.rootUrl);
    }
    this._isProcessingRootDocs = true;
  }
  async ensureEpochInitialized() {
    await this.onNewEpoch.waitForCondition(() => !!this.lastEpoch);
  }
};

// src/packlets/spaces/epoch-migrations.ts
import { invariant as invariant10 } from "@dxos/invariant";
import { CreateEpochRequest } from "@dxos/protocols/proto/dxos/client/services";
var __dxlog_file13 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/spaces/epoch-migrations.ts";
var LOAD_DOC_TIMEOUT = 1e4;
var runEpochMigration = async (ctx, context) => {
  switch (context.migration) {
    case CreateEpochRequest.Migration.INIT_AUTOMERGE: {
      const document = await context.echoHost.createDoc();
      await context.echoHost.flush(ctx);
      return {
        newRoot: document.url
      };
    }
    case CreateEpochRequest.Migration.PRUNE_AUTOMERGE_ROOT_HISTORY: {
      if (!context.currentRoot) {
        throw new Error("Space does not have an automerge root");
      }
      const rootHandle = await context.echoHost.loadDoc(ctx, context.currentRoot, {
        timeout: LOAD_DOC_TIMEOUT
      });
      invariant10(rootHandle, "Automerge root document must load for history prune migration.", { "~LogMeta": "~LogMeta", F: __dxlog_file13, L: 25, S: void 0, A: ["rootHandle", "'Automerge root document must load for history prune migration.'"] });
      const newRoot = await context.echoHost.createDoc(rootHandle.doc());
      await context.echoHost.flush(ctx);
      return {
        newRoot: newRoot.url
      };
    }
    case CreateEpochRequest.Migration.FRAGMENT_AUTOMERGE_ROOT: {
      throw new Error("Migration not available");
    }
    case CreateEpochRequest.Migration.MIGRATE_REFERENCES_TO_DXN: {
      throw new Error("Migration not available");
    }
    // TODO(dmaretskyi): This path doesn't seem to fit here. This is not a migration.
    case CreateEpochRequest.Migration.REPLACE_AUTOMERGE_ROOT: {
      invariant10(context.newAutomergeRoot, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file13, L: 43, S: void 0, A: ["context.newAutomergeRoot", ""] });
      await context.echoHost.flush(ctx);
      return {
        newRoot: context.newAutomergeRoot
      };
    }
  }
  return {};
};

// src/packlets/spaces/notarization-plugin.ts
import { DeferredTask, Event as Event6, TimeoutError, Trigger as Trigger2, scheduleMicroTask as scheduleMicroTask2, scheduleTask as scheduleTask3, sleep } from "@dxos/async";
import { Resource as Resource6, rejectOnDispose } from "@dxos/context";
import { verifyCredential as verifyCredential2 } from "@dxos/credentials";
import { invariant as invariant11 } from "@dxos/invariant";
import { PublicKey as PublicKey6 } from "@dxos/keys";
import { log as log10, logInfo as logInfo2 } from "@dxos/log";
import { EdgeCallFailedError } from "@dxos/protocols";
import { schema as schema2 } from "@dxos/protocols/proto";
import { RpcExtension } from "@dxos/teleport";
import { ComplexMap as ComplexMap4, ComplexSet as ComplexSet2, entry } from "@dxos/util";
var __dxlog_file14 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/spaces/notarization-plugin.ts";
function _ts_decorate5(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var DEFAULT_RETRY_TIMEOUT = 1e3;
var DEFAULT_SUCCESS_DELAY = 1e3;
var DEFAULT_NOTARIZE_TIMEOUT = 1e4;
var DEFAULT_ACTIVE_EDGE_POLLING_INTERVAL = 3e3;
var MAX_EDGE_RETRIES = 2;
var WRITER_NOT_SET_ERROR_CODE = "WRITER_NOT_SET";
var credentialCodec = schema2.getCodecForType("dxos.halo.credentials.Credential");
var NotarizationPlugin = class extends Resource6 {
  _extensionOpened = new Event6();
  _writer;
  _extensions = /* @__PURE__ */ new Set();
  _processedCredentials = new ComplexSet2(PublicKey6.hash);
  _processCredentialsTriggers = new ComplexMap4(PublicKey6.hash);
  _activeEdgePollingIntervalHandle = void 0;
  _activeEdgePollingInterval;
  _activeEdgePollingEnabled = false;
  _spaceId;
  _edgeClient;
  constructor(params) {
    super();
    this._spaceId = params.spaceId;
    this._activeEdgePollingInterval = params.activeEdgePollingInterval ?? DEFAULT_ACTIVE_EDGE_POLLING_INTERVAL;
    if (params.edgeClient && params.edgeFeatures?.feedReplicator) {
      this._edgeClient = params.edgeClient;
    }
  }
  setActiveEdgePollingEnabled(enabled) {
    const client = this._edgeClient;
    invariant11(client, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 50, S: this, A: ["client", ""] });
    this._activeEdgePollingEnabled = enabled;
    if (this.isOpen) {
      if (enabled && !this._activeEdgePollingIntervalHandle) {
        this._startPeriodicEdgePolling(client);
      } else if (!enabled && this._activeEdgePollingIntervalHandle) {
        this._stopPeriodicEdgePolling();
      }
    }
  }
  get hasWriter() {
    return !!this._writer;
  }
  async _open() {
    if (this._edgeClient) {
      if (this._activeEdgePollingEnabled) {
        this._startPeriodicEdgePolling(this._edgeClient);
      }
      if (this._writer) {
        this._notarizePendingEdgeCredentials(this._edgeClient, this._writer);
      }
    }
  }
  async _close() {
    this._stopPeriodicEdgePolling();
    await this._ctx.dispose();
  }
  /**
  * Request credentials to be notarized.
  */
  async notarize({ ctx: opCtx, credentials, timeout = DEFAULT_NOTARIZE_TIMEOUT, retryTimeout = DEFAULT_RETRY_TIMEOUT, successDelay = DEFAULT_SUCCESS_DELAY, edgeRetryJitter }) {
    log10("notarize", {
      credentials
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 80, S: this });
    invariant11(credentials.every((credential) => credential.id), "Credentials must have an id", { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 83, S: this, A: ["credentials.every((credential)=>credential.id)", "'Credentials must have an id'"] });
    const errors = new Trigger2();
    const ctx = this._ctx.derive({
      onError: (err) => {
        log10.warn("Notarization error", {
          err
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 87, S: this });
        void ctx.dispose();
        errors.throw(err);
      }
    });
    opCtx?.onDispose(() => ctx.dispose());
    if (timeout !== 0) {
      this._scheduleTimeout(ctx, errors, timeout);
    }
    const allNotarized = Promise.all(credentials.map((credential) => this._waitUntilProcessed(credential.id)));
    this._tryNotarizeCredentialsWithPeers(ctx, credentials, {
      retryTimeout,
      successDelay
    });
    if (this._edgeClient) {
      this._tryNotarizeCredentialsWithEdge(ctx, this._edgeClient, credentials, {
        retryTimeout,
        successDelay,
        jitter: edgeRetryJitter
      });
    }
    try {
      await Promise.race([
        rejectOnDispose(ctx),
        allNotarized,
        errors.wait()
      ]);
      log10("done", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 116, S: this });
    } finally {
      await ctx.dispose();
    }
  }
  _tryNotarizeCredentialsWithPeers(ctx, credentials, { retryTimeout, successDelay }) {
    const peersTried = /* @__PURE__ */ new Set();
    const notarizeTask = new DeferredTask(ctx, async () => {
      try {
        if (this._extensions.size === 0) {
          return;
        }
        const peer = [
          ...this._extensions
        ].find((peer2) => !peersTried.has(peer2));
        if (!peer) {
          log10.info("Exhausted all peers to notarize with", {
            retryIn: retryTimeout
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 134, S: this });
          peersTried.clear();
          scheduleTask3(ctx, () => notarizeTask.schedule(), retryTimeout);
          return;
        }
        peersTried.add(peer);
        log10("try notarizing", {
          peer: peer.localPeerId,
          credentialId: credentials.map((credential) => credential.id)
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 142, S: this });
        await peer.rpc.NotarizationService.notarize({
          credentials: credentials.filter((credential) => !this._processedCredentials.has(credential.id))
        });
        log10("success", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 149, S: this });
        await sleep(successDelay);
      } catch (err) {
        if (!ctx.disposed && !err.message.includes(WRITER_NOT_SET_ERROR_CODE)) {
          log10.info("error notarizing (recoverable)", err, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 153, S: this });
        }
        notarizeTask.schedule();
      }
    });
    notarizeTask.schedule();
    this._extensionOpened.on(ctx, () => notarizeTask.schedule());
  }
  _tryNotarizeCredentialsWithEdge(ctx, client, credentials, timeouts) {
    const encodedCredentials = credentials.map((credential) => {
      const binary = credentialCodec.encode(credential);
      return Buffer.from(binary).toString("base64");
    });
    scheduleTask3(ctx, async () => {
      try {
        await client.notarizeCredentials(ctx, this._spaceId, {
          credentials: encodedCredentials
        }, {
          retry: {
            count: MAX_EDGE_RETRIES,
            timeout: timeouts.retryTimeout,
            jitter: timeouts.jitter
          }
        });
        log10("edge notarization success", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 177, S: this });
      } catch (error) {
        handleEdgeError(error);
      }
    });
  }
  /**
  * Called with credentials arriving from the control pipeline.
  */
  async processCredential(credential) {
    if (!credential.id) {
      return;
    }
    this._processCredentialsTriggers.get(credential.id)?.wake();
    this._processedCredentials.add(credential.id);
    this._processCredentialsTriggers.delete(credential.id);
  }
  setWriter(writer) {
    invariant11(!this._writer, "Writer already set.", { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 194, S: this, A: ["!this._writer", "'Writer already set.'"] });
    this._writer = writer;
    if (this._edgeClient && this.isOpen) {
      this._notarizePendingEdgeCredentials(this._edgeClient, writer);
    }
  }
  _startPeriodicEdgePolling(client) {
    this._activeEdgePollingIntervalHandle = setInterval(() => {
      if (this._writer) {
        this._notarizePendingEdgeCredentials(client, this._writer);
      }
    }, this._activeEdgePollingInterval);
  }
  _stopPeriodicEdgePolling() {
    if (this._activeEdgePollingIntervalHandle) {
      clearInterval(this._activeEdgePollingIntervalHandle);
      this._activeEdgePollingIntervalHandle = void 0;
    }
  }
  /**
  * The method is used only for adding agent feeds to spaces.
  * When an agent is created we can admit them into all the existing spaces. In case the operation fails
  * this method will fix it on the next space open.
  * Given how rarely this happens there's no need to poll the endpoint.
  */
  _notarizePendingEdgeCredentials(client, writer) {
    scheduleMicroTask2(this._ctx, async () => {
      try {
        const response = await client.getCredentialsForNotarization(this._ctx, this._spaceId, {
          retry: {
            count: MAX_EDGE_RETRIES
          }
        });
        const credentials = response.awaitingNotarization.credentials;
        if (!credentials.length) {
          log10("edge did not return credentials for notarization", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 228, S: this });
          return;
        }
        log10("got edge credentials for notarization", {
          count: credentials.length
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 231, S: this });
        const decodedCredentials = credentials.map((credential) => {
          const binary = Buffer.from(credential, "base64");
          return credentialCodec.decode(binary);
        });
        await this._notarizeCredentials(writer, decodedCredentials);
        log10.info("notarized edge credentials", {
          count: decodedCredentials.length
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 239, S: this });
      } catch (error) {
        handleEdgeError(error);
      }
    });
  }
  async _waitUntilProcessed(id) {
    if (this._processedCredentials.has(id)) {
      return;
    }
    await entry(this._processCredentialsTriggers, id).orInsert(new Trigger2()).value.wait();
  }
  /**
  * Requests from other peers to notarize credentials.
  */
  async _onNotarize(request) {
    if (!this._writer) {
      throw new Error(WRITER_NOT_SET_ERROR_CODE);
    }
    await this._notarizeCredentials(this._writer, request.credentials ?? []);
  }
  async _notarizeCredentials(writer, credentials) {
    for (const credential of credentials) {
      invariant11(credential.id, "Credential must have an id", { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 263, S: this, A: ["credential.id", "'Credential must have an id'"] });
      if (this._processedCredentials.has(credential.id)) {
        continue;
      }
      const verificationResult = await verifyCredential2(credential);
      if (verificationResult.kind === "fail") {
        throw new Error(`Credential verification failed: ${verificationResult.errors.join("\n")}.`);
      }
      await writer.write(credential);
    }
  }
  createExtension() {
    const extension = new NotarizationTeleportExtension({
      onOpen: async () => {
        log10("extension opened", {
          peer: extension.localPeerId
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 277, S: this });
        this._extensions.add(extension);
        this._extensionOpened.emit();
      },
      onClose: async () => {
        log10("extension closed", {
          peer: extension.localPeerId
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 284, S: this });
        this._extensions.delete(extension);
      },
      onNotarize: this._onNotarize.bind(this)
    });
    return extension;
  }
  _scheduleTimeout(ctx, errors, timeout) {
    scheduleTask3(ctx, () => {
      log10.warn("Notarization timeout", {
        timeout,
        peers: Array.from(this._extensions).map((extension) => extension.remotePeerId)
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 295, S: this });
      void ctx.dispose();
      errors.throw(new TimeoutError(timeout, "Notarization timed out"));
    }, timeout);
  }
};
_ts_decorate5([
  logInfo2
], NotarizationPlugin.prototype, "_spaceId", void 0);
var handleEdgeError = (error) => {
  if (!(error instanceof EdgeCallFailedError) || error.data) {
    log10.catch(error, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 309, S: void 0 });
  } else {
    log10.info("Edge notarization failure", {
      message: error.message
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file14, L: 311, S: void 0 });
  }
};
var NotarizationTeleportExtension = class extends RpcExtension {
  _params;
  constructor(_params) {
    super({
      requested: {
        NotarizationService: schema2.getService("dxos.mesh.teleport.notarization.NotarizationService")
      },
      exposed: {
        NotarizationService: schema2.getService("dxos.mesh.teleport.notarization.NotarizationService")
      }
    }), this._params = _params;
  }
  async getHandlers() {
    return {
      NotarizationService: {
        notarize: async (request) => {
          await this._params.onNotarize(request);
        }
      }
    };
  }
  async onOpen(ctx) {
    await super.onOpen(ctx);
    await this._params.onOpen();
  }
  async onClose(err) {
    await this._params.onClose();
    await super.onClose(err);
  }
};

// src/packlets/spaces/data-space.ts
var __dxlog_file15 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/spaces/data-space.ts";
function _ts_decorate6(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_add_disposable_resource3(env, value, async) {
  if (value !== null && value !== void 0) {
    if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
    var dispose, inner;
    if (async) {
      if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
      dispose = value[Symbol.asyncDispose];
    }
    if (dispose === void 0) {
      if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
      dispose = value[Symbol.dispose];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    env.stack.push({
      value,
      dispose,
      async
    });
  } else if (async) {
    env.stack.push({
      async: true
    });
  }
  return value;
}
function _ts_dispose_resources3(env) {
  var _SuppressedError = typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
  };
  return (_ts_dispose_resources3 = function _ts_dispose_resources6(env2) {
    function fail(e) {
      env2.error = env2.hasError ? new _SuppressedError(e, env2.error, "An error was suppressed during disposal.") : e;
      env2.hasError = true;
    }
    var r, s = 0;
    function next() {
      while (r = env2.stack.pop()) {
        try {
          if (!r.async && s === 1) return s = 0, env2.stack.push(r), Promise.resolve().then(next);
          if (r.dispose) {
            var result = r.dispose.call(r.value);
            if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) {
              fail(e);
              return next();
            });
          } else s |= 1;
        } catch (e) {
          fail(e);
        }
      }
      if (s === 1) return env2.hasError ? Promise.reject(env2.error) : Promise.resolve();
      if (env2.hasError) throw env2.error;
    }
    return next();
  })(env);
}
var DataSpace = class {
  _ctx = new Context8(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 97 });
  _inner;
  _gossip;
  _presence;
  _keyring;
  _feedStore;
  _metadataStore;
  _signingContext;
  _notarizationPlugin;
  _callbacks;
  _cache = void 0;
  _echoHost;
  _edgeFeedReplicator = void 0;
  // TODO(dmaretskyi): Move into Space?
  _automergeSpaceState = new AutomergeSpaceState((rootUrl) => this._onNewAutomergeRoot(rootUrl));
  _epochProcessingMutex = new Mutex2();
  _state = SpaceState3.SPACE_CLOSED;
  /** Immutable tags from space metadata, available immediately. */
  tags;
  _databaseRoot = null;
  /**
  * Error for _state === SpaceState.SPACE_ERROR.
  */
  error = void 0;
  authVerifier;
  stateUpdate = new Event7();
  postOpen = new CallbackCollection();
  preClose = new CallbackCollection();
  metrics = {};
  constructor(params) {
    this._inner = params.inner;
    this._inner.stateUpdate.on(this._ctx, () => this.stateUpdate.emit());
    this._gossip = params.gossip;
    this._presence = params.presence;
    this._keyring = params.keyring;
    this._feedStore = params.feedStore;
    this._metadataStore = params.metadataStore;
    this._signingContext = params.signingContext;
    this._callbacks = params.callbacks ?? {};
    this._echoHost = params.echoHost;
    this._notarizationPlugin = new NotarizationPlugin({
      spaceId: this._inner.id,
      edgeClient: params.edgeHttpClient,
      edgeFeatures: params.edgeFeatures,
      activeEdgePollingInterval: params.activeEdgeNotarizationPollingInterval
    });
    this.authVerifier = new TrustedKeySetAuthVerifier({
      trustedKeysProvider: () => new ComplexSet3(PublicKey7.hash, Array.from(this._inner.spaceState.members.values()).filter((member) => member.role !== SpaceMember5.Role.REMOVED).map((member) => member.key)),
      update: this._inner.stateUpdate,
      authTimeout: AUTH_TIMEOUT2
    });
    this._cache = params.cache;
    this.tags = params.tags ?? [];
    if (params.edgeConnection && params.edgeFeatures?.feedReplicator) {
      this._edgeFeedReplicator = new EdgeFeedReplicator({
        messenger: params.edgeConnection,
        spaceId: this.id
      });
    }
    this._state = params.initialState;
    log11("new state", {
      state: SpaceState3[this._state]
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 155, S: this });
  }
  get id() {
    return this._inner.id;
  }
  get key() {
    return this._inner.key;
  }
  get isOpen() {
    return this._inner.isOpen;
  }
  get state() {
    return this._state;
  }
  // TODO(burdon): Can we mark this for debugging only?
  get inner() {
    return this._inner;
  }
  get presence() {
    return this._presence;
  }
  get notarizationPlugin() {
    return this._notarizationPlugin;
  }
  get cache() {
    return this._cache;
  }
  /** Membership policy from the genesis credential, defaults to INVITE. */
  get membershipPolicy() {
    return this._inner.spaceState.genesisCredential ? this._inner.spaceState.membershipPolicy : MembershipPolicy2.INVITE;
  }
  get automergeSpaceState() {
    return this._automergeSpaceState;
  }
  get databaseRoot() {
    return this._databaseRoot;
  }
  get _automergeInfo() {
    return {
      rootUrl: this._automergeSpaceState.rootUrl,
      lastEpoch: this._automergeSpaceState.lastEpoch
    };
  }
  async open(ctx) {
    if (this._state === SpaceState3.SPACE_CLOSED) {
      await this._open(ctx);
    }
  }
  async _open(ctx) {
    await this._presence.open();
    await this._gossip.open();
    await this._notarizationPlugin.open();
    await this._inner.spaceState.addCredentialProcessor(this._notarizationPlugin);
    await this._automergeSpaceState.open();
    await this._inner.spaceState.addCredentialProcessor(this._automergeSpaceState);
    if (this._edgeFeedReplicator) {
      this.inner.protocol.feedAdded.append(this._onFeedAdded);
    }
    await this._inner.open(ctx);
    await this._inner.startProtocol(ctx);
    await this._edgeFeedReplicator?.open();
    this._state = SpaceState3.SPACE_CONTROL_ONLY;
    log11("new state", {
      state: SpaceState3[this._state]
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 218, S: this });
    this.stateUpdate.emit();
    this.metrics = {};
    this.metrics.open = /* @__PURE__ */ new Date();
    await this.postOpen.callSerial();
  }
  async close(ctx) {
    await this._close(ctx);
  }
  async _close(ctx) {
    await this._callbacks.beforeClose?.();
    await this.preClose.callSerial();
    this._state = SpaceState3.SPACE_CLOSED;
    log11("new state", {
      state: SpaceState3[this._state]
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 233, S: this });
    await this._ctx.dispose();
    this._ctx = new Context8(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 237 });
    if (this._edgeFeedReplicator) {
      this.inner.protocol.feedAdded.remove(this._onFeedAdded);
    }
    await this._edgeFeedReplicator?.close();
    await this.authVerifier.close();
    await this._inner.close(ctx);
    await this._inner.spaceState.removeCredentialProcessor(this._automergeSpaceState);
    await this._automergeSpaceState.close();
    await this._inner.spaceState.removeCredentialProcessor(this._notarizationPlugin);
    await this._notarizationPlugin.close();
    await this._presence.close();
    await this._gossip.close();
  }
  async postMessage(channel, message) {
    return this._gossip.postMessage(channel, message);
  }
  listen(channel, callback) {
    return this._gossip.listen(channel, callback);
  }
  /**
  * Initialize the data pipeline in a separate task.
  * @param callerCtx - Trace context from the caller (e.g., activate or createSpace) for span parenting.
  */
  initializeDataPipelineAsync(callerCtx) {
    const traceCtx = callerCtx ?? this._ctx;
    scheduleTask4(this._ctx, async () => {
      try {
        this.metrics.pipelineInitBegin = /* @__PURE__ */ new Date();
        await this.initializeDataPipeline(traceCtx);
      } catch (err) {
        if (err instanceof CancelledError || err instanceof ContextDisposedError) {
          log11("data pipeline initialization cancelled", err, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 268, S: this });
          return;
        }
        log11.error("Error initializing data pipeline", err, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 271, S: this });
        this._state = SpaceState3.SPACE_ERROR;
        log11("new state", {
          state: SpaceState3[this._state]
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 273, S: this });
        this.error = err;
        this.stateUpdate.emit();
      } finally {
        this.metrics.ready = /* @__PURE__ */ new Date();
      }
    });
  }
  async initializeDataPipeline(ctx) {
    if (this._state !== SpaceState3.SPACE_CONTROL_ONLY) {
      throw new SystemError({
        message: "Invalid operation"
      });
    }
    this._state = SpaceState3.SPACE_INITIALIZING;
    log11("new state", {
      state: SpaceState3[this._state]
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 290, S: this });
    log11("initializing control pipeline", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 293, S: this });
    await this._initializeAndReadControlPipeline(ctx);
    await sleep2(1);
    const ready = this.stateUpdate.waitForCondition(() => this._state === SpaceState3.SPACE_READY);
    log11("initializing automerge root", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 298, S: this });
    this._automergeSpaceState.startProcessingRootDocs();
    log11("waiting for space to be ready", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 301, S: this });
    await ready;
    log11("space is ready", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 303, S: this });
  }
  async *getAllDocuments() {
    invariant12(this._databaseRoot, "Space is not ready", { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 306, S: this, A: ["this._databaseRoot", "'Space is not ready'"] });
    const doc = this._databaseRoot.doc() ?? failedInvariant3();
    const root = save(doc);
    yield [
      this._databaseRoot.documentId,
      root
    ];
    for (const documentUrl of this._databaseRoot.getAllLinkedDocuments()) {
      const data = await this._echoHost.exportDoc(documentUrl);
      yield [
        documentUrl.replace(/^automerge:/, ""),
        data
      ];
    }
  }
  /**
  * Get all feeds and their blocks for this space.
  * Used for space archive export.
  */
  async getAllFeeds() {
    return this._echoHost.getAllFeedsForSpace(this.id);
  }
  async _enterReadyState() {
    await this._callbacks.beforeReady?.();
    this._state = SpaceState3.SPACE_READY;
    log11("new state", {
      state: SpaceState3[this._state]
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 330, S: this });
    this.stateUpdate.emit();
    await this._callbacks.afterReady?.();
  }
  async _initializeAndReadControlPipeline(ctx) {
    await this._inner.controlPipeline.state.waitUntilReachedTargetTimeframe({
      ctx,
      timeout: 1e4,
      breakOnStall: false
    });
    this.metrics.controlPipelineReady = /* @__PURE__ */ new Date();
    await this._createWritableFeeds();
    log11("writable feeds created", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 344, S: this });
    this.stateUpdate.emit();
    if (!this.notarizationPlugin.hasWriter) {
      this.notarizationPlugin.setWriter(createMappedFeedWriter((credential) => ({
        credential: {
          credential
        }
      }), this._inner.controlPipeline.writer));
    }
  }
  async _createWritableFeeds() {
    const credentials = [];
    if (!this.inner.controlFeedKey) {
      const controlFeed = await this._feedStore.openFeed(await this._keyring.createKey(), {
        writable: true
      });
      await this.inner.setControlFeed(controlFeed);
      credentials.push(await this._signingContext.credentialSigner.createCredential({
        subject: controlFeed.key,
        assertion: {
          "@type": "dxos.halo.credentials.AdmittedFeed",
          "spaceKey": this.key,
          "deviceKey": this._signingContext.deviceKey,
          "identityKey": this._signingContext.identityKey,
          "designation": AdmittedFeed4.Designation.CONTROL
        }
      }));
    }
    if (!this.inner.dataFeedKey) {
      const dataFeed = await this._feedStore.openFeed(await this._keyring.createKey(), {
        writable: true,
        sparse: true
      });
      await this.inner.setDataFeed(dataFeed);
      credentials.push(await this._signingContext.credentialSigner.createCredential({
        subject: dataFeed.key,
        assertion: {
          "@type": "dxos.halo.credentials.AdmittedFeed",
          "spaceKey": this.key,
          "deviceKey": this._signingContext.deviceKey,
          "identityKey": this._signingContext.identityKey,
          "designation": AdmittedFeed4.Designation.DATA
        }
      }));
    }
    if (credentials.length > 0) {
      try {
        log11("will notarize credentials for feed admission", {
          count: credentials.length
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 391, S: this });
        await this.notarizationPlugin.notarize({
          ctx: this._ctx,
          credentials,
          timeout: 0
        });
        log11("credentials notarized", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 400, S: this });
      } catch (err) {
        if (err instanceof ContextDisposedError) {
          return;
        }
        log11.error("error notarizing credentials for feed admission", err, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 405, S: this });
        throw err;
      }
      await this._metadataStore.setWritableFeedKeys(this.key, this.inner.controlFeedKey, this.inner.dataFeedKey);
    }
  }
  _onNewAutomergeRoot(rootUrl) {
    log11("loading automerge root doc for space", {
      space: this.key,
      rootUrl
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 413, S: this });
    let handle = null;
    queueMicrotask(async () => {
      try {
        const env = {
          stack: [],
          error: void 0,
          hasError: false
        };
        try {
          await warnAfterTimeout(5e3, "Automerge root doc load timeout (DataSpace)", async () => {
            handle = await cancelWithContext2(this._ctx, this._echoHost.loadDoc(this._ctx, rootUrl, {
              fetchFromNetwork: true
            }));
          });
          if (this._ctx.disposed) {
            return;
          }
          if (!handle) {
            log11.warn("automerge root doc not available yet", {
              space: this.key,
              rootUrl
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 436, S: this });
            return;
          }
          const _guard = _ts_add_disposable_resource3(env, await this._epochProcessingMutex.acquire(), false);
          const doc = handle.doc();
          if (!doc.access?.spaceKey) {
            handle.change((doc2) => {
              doc2.access = {
                spaceKey: this.key.toHex()
              };
            });
          }
          const root = await this._echoHost.openSpaceRoot(this._ctx, this.id, handle.url);
          this._databaseRoot = root;
          if (root.getVersion() !== SpaceDocVersion.CURRENT) {
            this._state = SpaceState3.SPACE_REQUIRES_MIGRATION;
            this.stateUpdate.emit();
          } else if (this._state !== SpaceState3.SPACE_READY) {
            await this._enterReadyState();
          } else {
            this.stateUpdate.emit();
          }
        } catch (e) {
          env.error = e;
          env.hasError = true;
        } finally {
          _ts_dispose_resources3(env);
        }
      } catch (err) {
        if (err instanceof ContextDisposedError) {
          return;
        }
        log11.warn("error loading automerge root doc", {
          space: this.key,
          rootUrl,
          err
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 475, S: this });
      }
    });
  }
  // TODO(dmaretskyi): Use profile from signing context.
  async updateOwnProfile(profile) {
    const credential = await this._signingContext.credentialSigner.createCredential({
      subject: this._signingContext.identityKey,
      assertion: {
        "@type": "dxos.halo.credentials.MemberProfile",
        profile
      }
    });
    await this.inner.controlPipeline.writer.write({
      credential: {
        credential
      }
    });
  }
  async createEpoch(options) {
    const ctx = this._ctx.derive();
    if (!options?.migration) {
      return null;
    }
    const { newRoot } = await runEpochMigration(ctx, {
      echoHost: this._echoHost,
      spaceId: this.id,
      spaceKey: this.key,
      migration: options.migration,
      currentRoot: this._automergeSpaceState.rootUrl ?? null,
      newAutomergeRoot: options.newAutomergeRoot
    });
    const epoch = {
      previousId: this._automergeSpaceState.lastEpoch?.id,
      number: (this._automergeSpaceState.lastEpoch?.subject.assertion.number ?? -1) + 1,
      timeframe: this._automergeSpaceState.lastEpoch?.subject.assertion.timeframe ?? new Timeframe3(),
      automergeRoot: newRoot ?? this._automergeSpaceState.rootUrl
    };
    const credential = await this._signingContext.credentialSigner.createCredential({
      subject: this.key,
      assertion: {
        "@type": "dxos.halo.credentials.Epoch",
        ...epoch
      }
    });
    const receipt = await this.inner.controlPipeline.writer.write({
      credential: {
        credential
      }
    });
    const timeframe = new Timeframe3([
      [
        receipt.feedKey,
        receipt.seq
      ]
    ]);
    await this.inner.controlPipeline.state.waitUntilTimeframe(timeframe);
    await this._echoHost.updateIndexes();
    return {
      credential,
      timeframe
    };
  }
  async activate(ctx) {
    if (![
      SpaceState3.SPACE_CLOSED,
      SpaceState3.SPACE_INACTIVE
    ].includes(this._state)) {
      return;
    }
    await this._metadataStore.setSpaceState(this.key, SpaceState3.SPACE_ACTIVE);
    await this._open(ctx);
    this.initializeDataPipelineAsync(ctx);
  }
  async deactivate(ctx) {
    if (this._state === SpaceState3.SPACE_INACTIVE) {
      return;
    }
    await this._metadataStore.setSpaceState(this.key, SpaceState3.SPACE_INACTIVE);
    if (this._state !== SpaceState3.SPACE_CLOSED) {
      await this._close(ctx);
    }
    this._state = SpaceState3.SPACE_INACTIVE;
    log11("new state", {
      state: SpaceState3[this._state]
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 563, S: this });
    this.stateUpdate.emit();
  }
  /**
  * Tombstones (soft-deletes) the space by moving it to a terminal state.
  * This is intentionally separate from teardown: the caller is responsible for {@link close}-ing the
  * space (teardown uses the resource lifecycle, distinct from this state transition).
  * Terminal: {@link activate} will not re-open a deleted space. Data is not removed until garbage
  * collection (future work).
  */
  async delete() {
    if (this._state === SpaceState3.SPACE_DELETED) {
      return;
    }
    await this._metadataStore.setSpaceState(this.key, SpaceState3.SPACE_DELETED);
    this._state = SpaceState3.SPACE_DELETED;
    log11("new state", {
      state: SpaceState3[this._state]
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file15, L: 580, S: this });
    this.stateUpdate.emit();
  }
  getEdgeReplicationSetting() {
    return this._metadataStore.getSpaceEdgeReplicationSetting(this.key);
  }
  _onFeedAdded = async (feed) => {
    await this._edgeFeedReplicator.addFeed(feed);
  };
};
_ts_decorate6([
  trace4.info()
], DataSpace.prototype, "_inner", void 0);
_ts_decorate6([
  trace4.info()
], DataSpace.prototype, "id", null);
_ts_decorate6([
  trace4.info()
], DataSpace.prototype, "key", null);
_ts_decorate6([
  trace4.info({
    enum: SpaceState3
  })
], DataSpace.prototype, "state", null);
_ts_decorate6([
  trace4.info({
    depth: null
  })
], DataSpace.prototype, "_automergeInfo", null);
_ts_decorate6([
  synchronized2,
  trace4.span({
    showInBrowserTimeline: true,
    op: "lifecycle"
  })
], DataSpace.prototype, "open", null);
_ts_decorate6([
  synchronized2,
  trace4.span({
    showInBrowserTimeline: true,
    op: "lifecycle"
  })
], DataSpace.prototype, "close", null);
_ts_decorate6([
  trace4.span({
    showInBrowserTimeline: true,
    op: "lifecycle"
  })
], DataSpace.prototype, "initializeDataPipeline", null);
_ts_decorate6([
  trace4.span({
    showInBrowserTimeline: true,
    op: "lifecycle"
  })
], DataSpace.prototype, "_initializeAndReadControlPipeline", null);
_ts_decorate6([
  timed(1e4)
], DataSpace.prototype, "_createWritableFeeds", null);
_ts_decorate6([
  synchronized2
], DataSpace.prototype, "activate", null);
_ts_decorate6([
  synchronized2
], DataSpace.prototype, "deactivate", null);
_ts_decorate6([
  synchronized2
], DataSpace.prototype, "delete", null);
DataSpace = _ts_decorate6([
  trackLeaks2("open", "close"),
  trace4.resource()
], DataSpace);

// src/packlets/invitations/device-invitation-protocol.ts
import { getCredentialAssertion as getCredentialAssertion3 } from "@dxos/credentials";
import { invariant as invariant13 } from "@dxos/invariant";
import { AlreadyJoinedError as AlreadyJoinedError2 } from "@dxos/protocols";
import { Invitation as Invitation2 } from "@dxos/protocols/proto/dxos/client/services";
var __dxlog_file16 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/device-invitation-protocol.ts";
var DeviceInvitationProtocol = class {
  _keyring;
  _getIdentity;
  _acceptIdentity;
  constructor(_keyring, _getIdentity, _acceptIdentity) {
    this._keyring = _keyring;
    this._getIdentity = _getIdentity;
    this._acceptIdentity = _acceptIdentity;
  }
  toJSON() {
    return {
      kind: "device"
    };
  }
  checkCanInviteNewMembers() {
    return void 0;
  }
  getInvitationContext() {
    return {
      kind: Invitation2.Kind.DEVICE
    };
  }
  async delegate() {
    throw new Error("delegation not supported");
  }
  async cancelDelegation() {
    throw new Error("delegation not supported");
  }
  async admit(_, request) {
    invariant13(request.device, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file16, L: 37, S: this, A: ["request.device", ""] });
    const identity = this._getIdentity();
    const credential = await identity.admitDevice(request.device);
    invariant13(getCredentialAssertion3(credential)["@type"] === "dxos.halo.credentials.AuthorizedDevice", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file16, L: 40, S: this, A: ["getCredentialAssertion(credential)['@type'] === 'dxos.halo.credentials.AuthorizedDevice'", ""] });
    return {
      device: {
        identityKey: identity.identityKey,
        haloSpaceKey: identity.haloSpaceKey,
        genesisFeedKey: identity.haloGenesisFeedKey,
        controlTimeframe: identity.controlPipeline.state.timeframe,
        credential
      }
    };
  }
  checkInvitation(invitation) {
    try {
      const identity = this._getIdentity();
      if (identity) {
        return new AlreadyJoinedError2({
          message: "Currently only one identity per client is supported."
        });
      }
    } catch {
    }
  }
  createIntroduction() {
    return {};
  }
  async createAdmissionRequest(deviceProfile) {
    const deviceKey = await this._keyring.createKey();
    const controlFeedKey = await this._keyring.createKey();
    const dataFeedKey = await this._keyring.createKey();
    return {
      device: {
        deviceKey,
        controlFeedKey,
        dataFeedKey,
        profile: deviceProfile
      }
    };
  }
  async accept(_ctx, response, request) {
    invariant13(response.device, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file16, L: 80, S: this, A: ["response.device", ""] });
    const { identityKey, haloSpaceKey, genesisFeedKey, controlTimeframe } = response.device;
    invariant13(request.device, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file16, L: 82, S: this, A: ["request.device", ""] });
    const { deviceKey, controlFeedKey, dataFeedKey, profile } = request.device;
    await this._acceptIdentity({
      identityKey,
      deviceKey,
      haloSpaceKey,
      haloGenesisFeedKey: genesisFeedKey,
      controlFeedKey,
      dataFeedKey,
      controlTimeframe,
      deviceProfile: profile,
      authorizedDeviceCredential: response.device.credential
    });
    return {
      identityKey
    };
  }
};

// src/packlets/invitations/invitations-handler.ts
import { TimeoutError as TimeoutError2, scheduleTask as scheduleTask7 } from "@dxos/async";
import { INVITATION_TIMEOUT, getExpirationTime } from "@dxos/client-protocol";
import { ContextDisposedError as ContextDisposedError3 } from "@dxos/context";
import { createKeyPair, sign as sign2 } from "@dxos/crypto";
import { invariant as invariant18 } from "@dxos/invariant";
import { PublicKey as PublicKey9 } from "@dxos/keys";
import { log as log17 } from "@dxos/log";
import { createTeleportProtocolFactory } from "@dxos/network-manager";
import { InvalidInvitationError, InvalidInvitationExtensionRoleError as InvalidInvitationExtensionRoleError3 } from "@dxos/protocols";
import { Invitation as Invitation7 } from "@dxos/protocols/proto/dxos/client/services";
import { AuthenticationResponse as AuthenticationResponse2 } from "@dxos/protocols/proto/dxos/halo/invitations";
import { InvitationOptions as InvitationOptions4 } from "@dxos/protocols/proto/dxos/halo/invitations";
import { trace as _trace } from "@dxos/tracing";
import { ComplexSet as ComplexSet5 } from "@dxos/util";

// src/packlets/invitations/edge-invitation-handler.ts
import { scheduleMicroTask as scheduleMicroTask3, scheduleTask as scheduleTask5 } from "@dxos/async";
import { sign } from "@dxos/crypto";
import { invariant as invariant14 } from "@dxos/invariant";
import { SpaceId as SpaceId2 } from "@dxos/keys";
import { log as log12 } from "@dxos/log";
import { EdgeAuthChallengeError, EdgeCallFailedError as EdgeCallFailedError2 } from "@dxos/protocols";
import { schema as schema3 } from "@dxos/protocols/proto";
import { Invitation as Invitation4 } from "@dxos/protocols/proto/dxos/client/services";
import { trace as trace5 } from "@dxos/tracing";

// src/packlets/invitations/utils.ts
import { ContextDisposedError as ContextDisposedError2, cancelWithContext as cancelWithContext3 } from "@dxos/context";
import { Invitation as Invitation3 } from "@dxos/protocols/proto/dxos/client/services";
var stateToString = (state) => {
  return Object.entries(Invitation3.State).find(([key, val]) => val === state)?.[0] ?? "unknown";
};
var computeExpirationTime = (invitation) => {
  if (!invitation.lifetime) {
    return;
  }
  return new Date((invitation.created?.getTime() ?? Date.now()) + invitation.lifetime * 1e3);
};
var tryAcquireBeforeContextDisposed = async (ctx, mutex) => {
  let guard;
  return cancelWithContext3(ctx, (async () => {
    guard = await mutex.acquire();
    if (ctx.disposed) {
      guard.release();
      guard = void 0;
      throw new ContextDisposedError2();
    }
    return guard;
  })());
};

// src/packlets/invitations/edge-invitation-handler.ts
var __dxlog_file17 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/edge-invitation-handler.ts";
function _ts_decorate7(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var MAX_RETRIES_PER_INVITATION = 5;
var DEFAULT_REQUEST_RETRY_INTERVAL_MS = 3e3;
var DEFAULT_REQUEST_RETRY_JITTER_MS = 500;
var EdgeInvitationHandler = class {
  _client;
  _callbacks;
  _flowLock;
  _retryInterval;
  _retryJitter;
  constructor(config, _client, _callbacks) {
    this._client = _client;
    this._callbacks = _callbacks;
    this._retryInterval = config?.retryInterval ?? DEFAULT_REQUEST_RETRY_INTERVAL_MS;
    this._retryJitter = config?.retryJitter ?? DEFAULT_REQUEST_RETRY_JITTER_MS;
  }
  handle(ctx, guardedState, protocol, deviceProfile) {
    if (!this._client) {
      log12("edge disabled", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file17, L: 37, S: this });
      return;
    }
    const invitation = guardedState.current;
    const spaceId = invitation.spaceId;
    const canBeHandledByEdge = invitation.authMethod !== Invitation4.AuthMethod.SHARED_SECRET && invitation.type === Invitation4.Type.DELEGATED && invitation.kind === Invitation4.Kind.SPACE && spaceId != null && SpaceId2.isValid(spaceId);
    if (!canBeHandledByEdge) {
      log12("invitation could not be handled by edge", {
        invitation
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file17, L: 44, S: this });
      return;
    }
    ctx.onDispose(() => {
      this._flowLock?.release();
      this._flowLock = void 0;
    });
    let requestCount = 0;
    const tryHandleInvitation = async () => {
      requestCount++;
      const admissionRequest = await protocol.createAdmissionRequest(deviceProfile);
      if (admissionRequest.space) {
        try {
          await this._handleSpaceInvitationFlow(ctx, guardedState, admissionRequest.space, spaceId);
        } catch (error) {
          if (error instanceof EdgeCallFailedError2) {
            log12.info("join space with edge unsuccessful", {
              reason: error.message,
              retryable: error.isRetryable,
              after: error.retryAfterMs ?? this._calculateNextRetryMs()
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file17, L: 62, S: this });
            if (error.isRetryable && requestCount < MAX_RETRIES_PER_INVITATION) {
              scheduleTask5(ctx, tryHandleInvitation, error.retryAfterMs ?? this._calculateNextRetryMs());
            }
          } else if (requestCount < MAX_RETRIES_PER_INVITATION) {
            log12.info("failed to handle invitation with edge", {
              error
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file17, L: 71, S: this });
            scheduleTask5(ctx, tryHandleInvitation, this._calculateNextRetryMs());
          }
        }
      }
    };
    scheduleMicroTask3(ctx, tryHandleInvitation);
  }
  async _handleSpaceInvitationFlow(ctx, guardedState, admissionRequest, spaceId) {
    try {
      log12("edge invitation flow", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file17, L: 83, S: this });
      this._flowLock = await tryAcquireBeforeContextDisposed(ctx, guardedState.mutex);
      log12.verbose("edge invitation flow acquired the lock", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file17, L: 85, S: this });
      guardedState.set(this, Invitation4.State.CONNECTING);
      const response = await this._joinSpaceByInvitation(ctx, guardedState, spaceId, {
        identityKey: admissionRequest.identityKey.toHex(),
        invitationId: guardedState.current.invitationId
      });
      const admissionResponse = await this._mapToAdmissionResponse(response);
      await this._callbacks.onInvitationSuccess(ctx, admissionResponse, {
        space: admissionRequest
      });
    } catch (error) {
      guardedState.set(this, Invitation4.State.ERROR);
      throw error;
    } finally {
      this._flowLock?.release();
      this._flowLock = void 0;
    }
  }
  async _mapToAdmissionResponse(edgeResponse) {
    const credentialBytes = Buffer.from(edgeResponse.spaceMemberCredential, "base64");
    const codec = schema3.getCodecForType("dxos.halo.credentials.Credential");
    return {
      space: {
        credential: codec.decode(credentialBytes)
      }
    };
  }
  async _joinSpaceByInvitation(ctx, guardedState, spaceId, request) {
    invariant14(this._client, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file17, L: 113, S: this, A: ["this._client", ""] });
    try {
      return await this._client.joinSpaceByInvitation(ctx, spaceId, request);
    } catch (error) {
      if (error instanceof EdgeAuthChallengeError) {
        const publicKey = guardedState.current.guestKeypair?.publicKey;
        const privateKey = guardedState.current.guestKeypair?.privateKey;
        if (!privateKey || !publicKey) {
          throw error;
        }
        const signature = sign(Buffer.from(error.challenge, "base64"), privateKey);
        return this._client.joinSpaceByInvitation(ctx, spaceId, {
          ...request,
          signature: Buffer.from(signature).toString("base64")
        });
      } else {
        throw error;
      }
    }
  }
  hasFlowLock() {
    return this._flowLock != null;
  }
  _calculateNextRetryMs() {
    return this._retryInterval + Math.random() * this._retryJitter;
  }
};
_ts_decorate7([
  trace5.span({
    op: "invitation.edge"
  })
], EdgeInvitationHandler.prototype, "_handleSpaceInvitationFlow", null);
EdgeInvitationHandler = _ts_decorate7([
  trace5.resource()
], EdgeInvitationHandler);

// src/packlets/invitations/invitation-guest-extenstion.ts
import { Trigger as Trigger3 } from "@dxos/async";
import { Context as Context9, cancelWithContext as cancelWithContext4 } from "@dxos/context";
import { invariant as invariant15 } from "@dxos/invariant";
import { log as log13 } from "@dxos/log";
import { InvalidInvitationExtensionRoleError } from "@dxos/protocols";
import { schema as schema4 } from "@dxos/protocols/proto";
import { InvitationOptions } from "@dxos/protocols/proto/dxos/halo/invitations";
import { RpcExtension as RpcExtension2 } from "@dxos/teleport";
var __dxlog_file18 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/invitation-guest-extenstion.ts";
var OPTIONS_TIMEOUT = 1e4;
var InvitationGuestExtension = class extends RpcExtension2 {
  _invitationFlowMutex;
  _callbacks;
  _ctx = new Context9(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file18, L: 19 });
  _remoteOptions;
  _remoteOptionsTrigger = new Trigger3();
  /**
  * Held to allow only one invitation flow at a time to be active.
  */
  _invitationFlowLock = null;
  constructor(_invitationFlowMutex, _callbacks) {
    super({
      requested: {
        InvitationHostService: schema4.getService("dxos.halo.invitations.InvitationHostService")
      },
      exposed: {
        InvitationHostService: schema4.getService("dxos.halo.invitations.InvitationHostService")
      }
    }), this._invitationFlowMutex = _invitationFlowMutex, this._callbacks = _callbacks;
  }
  hasFlowLock() {
    return this._invitationFlowLock != null;
  }
  async getHandlers() {
    return {
      InvitationHostService: {
        options: async (options) => {
          invariant15(!this._remoteOptions, "Remote options already set.", { "~LogMeta": "~LogMeta", F: __dxlog_file18, L: 42, S: this, A: ["!this._remoteOptions", "'Remote options already set.'"] });
          this._remoteOptions = options;
          this._remoteOptionsTrigger.wake();
        },
        introduce: () => {
          throw new Error("Method not allowed.");
        },
        authenticate: () => {
          throw new Error("Method not allowed.");
        },
        admit: () => {
          throw new Error("Method not allowed.");
        }
      }
    };
  }
  async onOpen(context) {
    await super.onOpen(context);
    try {
      log13.verbose("guest acquire lock", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file18, L: 61, S: this });
      this._invitationFlowLock = await tryAcquireBeforeContextDisposed(this._ctx, this._invitationFlowMutex);
      log13.verbose("guest lock acquired", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file18, L: 63, S: this });
      await cancelWithContext4(this._ctx, this.rpc.InvitationHostService.options({
        role: InvitationOptions.Role.GUEST
      }));
      log13.verbose("options sent", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file18, L: 67, S: this });
      await cancelWithContext4(this._ctx, this._remoteOptionsTrigger.wait({
        timeout: OPTIONS_TIMEOUT
      }));
      log13.verbose("options received", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file18, L: 71, S: this });
      if (this._remoteOptions?.role !== InvitationOptions.Role.HOST) {
        throw new InvalidInvitationExtensionRoleError({
          context: {
            expected: InvitationOptions.Role.HOST,
            remoteOptions: this._remoteOptions,
            remotePeerId: context.remotePeerId
          }
        });
      }
      this._callbacks.onOpen(this._ctx, context);
    } catch (err) {
      if (this._invitationFlowLock != null) {
        this._callbacks.onError(err);
      }
      if (!this._ctx.disposed) {
        context.close(err);
      }
    }
  }
  async onClose() {
    await this._destroy();
  }
  async onAbort() {
    await this._destroy();
  }
  async _destroy() {
    await this._ctx.dispose();
    if (this._invitationFlowLock != null) {
      this._invitationFlowLock.release();
      this._invitationFlowLock = null;
      log13.verbose("invitation flow lock released", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file18, L: 102, S: this });
    }
  }
};

// src/packlets/invitations/invitation-host-extension.ts
import { Trigger as Trigger4, scheduleTask as scheduleTask6 } from "@dxos/async";
import { Context as Context10, cancelWithContext as cancelWithContext5 } from "@dxos/context";
import { randomBytes, verify } from "@dxos/crypto";
import { InvariantViolation, invariant as invariant16 } from "@dxos/invariant";
import { log as log14 } from "@dxos/log";
import { InvalidInvitationExtensionRoleError as InvalidInvitationExtensionRoleError2 } from "@dxos/protocols";
import { schema as schema5 } from "@dxos/protocols/proto";
import { Invitation as Invitation5 } from "@dxos/protocols/proto/dxos/client/services";
import { AuthenticationResponse, InvitationOptions as InvitationOptions2 } from "@dxos/protocols/proto/dxos/halo/invitations";
import { RpcExtension as RpcExtension3 } from "@dxos/teleport";
var __dxlog_file19 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/invitation-host-extension.ts";
var OPTIONS_TIMEOUT2 = 1e4;
var MAX_OTP_ATTEMPTS = 3;
var InvitationHostExtension = class extends RpcExtension3 {
  _invitationFlowMutex;
  _callbacks;
  /**
  * @internal
  */
  _ctx = new Context10(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 25 });
  _remoteOptions;
  _remoteOptionsTrigger = new Trigger4();
  _challenge = void 0;
  guestProfile = void 0;
  authenticationPassed = false;
  /**
  * Retry counter for SHARED_SECRET authentication method.
  */
  authenticationRetry = 0;
  /**
  * Resolved when admission is completed.
  */
  completedTrigger = new Trigger4();
  /**
  * Held to allow only one invitation flow at a time to be active.
  */
  _invitationFlowLock = null;
  constructor(_invitationFlowMutex, _callbacks) {
    super({
      requested: {
        InvitationHostService: schema5.getService("dxos.halo.invitations.InvitationHostService")
      },
      exposed: {
        InvitationHostService: schema5.getService("dxos.halo.invitations.InvitationHostService")
      }
    }), this._invitationFlowMutex = _invitationFlowMutex, this._callbacks = _callbacks;
  }
  hasFlowLock() {
    return this._invitationFlowLock != null;
  }
  async getHandlers() {
    return {
      // TODO(dmaretskyi): For now this is just forwarding the data to callbacks since we don't have session-specific logic.
      // Perhaps in the future we will have more complex logic here.
      InvitationHostService: {
        options: async (options) => {
          invariant16(!this._remoteOptions, "Remote options already set.", { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 59, S: this, A: ["!this._remoteOptions", "'Remote options already set.'"] });
          this._remoteOptions = options;
          this._remoteOptionsTrigger.wake();
        },
        introduce: async (request) => {
          const { profile, invitationId } = request;
          log14("introducing host invitation", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 65, S: this });
          const invitation = this._requireActiveInvitation();
          this._assertInvitationState(Invitation5.State.CONNECTED);
          if (invitationId !== invitation?.invitationId) {
            log14.warn("incorrect invitationId", {
              expected: invitation.invitationId,
              actual: invitationId
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 69, S: this });
            this._callbacks.onError(new Error("Incorrect invitationId."));
            scheduleTask6(this._ctx, () => this.close());
            return {
              authMethod: Invitation5.AuthMethod.NONE
            };
          }
          log14.verbose("guest introduced themselves", {
            guestProfile: profile
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 80, S: this });
          this.guestProfile = profile;
          this._callbacks.onStateUpdate(Invitation5.State.READY_FOR_AUTHENTICATION);
          this._challenge = invitation.authMethod === Invitation5.AuthMethod.KNOWN_PUBLIC_KEY ? randomBytes(32) : void 0;
          log14("introduced host invitation", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 86, S: this });
          return {
            authMethod: invitation.authMethod,
            challenge: this._challenge
          };
        },
        authenticate: async ({ authCode: code, signedChallenge }) => {
          log14("authenticating host invitation", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 93, S: this });
          const invitation = this._requireActiveInvitation();
          log14.verbose("received authentication request", {
            authCode: code
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 95, S: this });
          let status = AuthenticationResponse.Status.OK;
          this._assertInvitationState([
            Invitation5.State.AUTHENTICATING,
            Invitation5.State.READY_FOR_AUTHENTICATION
          ]);
          this._callbacks.onStateUpdate(Invitation5.State.AUTHENTICATING);
          switch (invitation.authMethod) {
            case Invitation5.AuthMethod.NONE: {
              log14("authentication not required", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 107, S: this });
              return {
                status: AuthenticationResponse.Status.OK
              };
            }
            case Invitation5.AuthMethod.SHARED_SECRET: {
              if (invitation.authCode) {
                if (this.authenticationRetry++ > MAX_OTP_ATTEMPTS) {
                  status = AuthenticationResponse.Status.INVALID_OPT_ATTEMPTS;
                } else if (code !== invitation.authCode) {
                  status = AuthenticationResponse.Status.INVALID_OTP;
                } else {
                  this.authenticationPassed = true;
                }
              }
              break;
            }
            case Invitation5.AuthMethod.KNOWN_PUBLIC_KEY: {
              if (!invitation.guestKeypair) {
                status = AuthenticationResponse.Status.INTERNAL_ERROR;
                break;
              }
              const isSignatureValid = this._challenge && verify(this._challenge, Buffer.from(signedChallenge ?? []), invitation.guestKeypair.publicKey.asBuffer());
              if (isSignatureValid) {
                this.authenticationPassed = true;
              } else {
                status = AuthenticationResponse.Status.INVALID_SIGNATURE;
              }
              break;
            }
            default: {
              log14.error("invalid authentication method", {
                authMethod: invitation.authMethod
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 141, S: this });
              status = AuthenticationResponse.Status.INTERNAL_ERROR;
              break;
            }
          }
          if (![
            AuthenticationResponse.Status.OK,
            AuthenticationResponse.Status.INVALID_OTP
          ].includes(status)) {
            this._callbacks.onError(new Error(`Authentication failed, with status=${status}`));
            scheduleTask6(this._ctx, () => this.close());
            return {
              status
            };
          }
          log14("authenticated host invitation", {
            status
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 158, S: this });
          return {
            status
          };
        },
        admit: async (request) => {
          log14("admitting guest", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 166, S: this });
          const invitation = this._requireActiveInvitation();
          try {
            if (isAuthenticationRequired(invitation)) {
              this._assertInvitationState(Invitation5.State.AUTHENTICATING);
              if (!this.authenticationPassed) {
                throw new Error("Not authenticated");
              }
            }
            const response = await this._callbacks.admit(request);
            log14("admitted guest", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 177, S: this });
            return response;
          } catch (err) {
            this._callbacks.onError(err);
            throw err;
          }
        }
      }
    };
  }
  async onOpen(context) {
    await super.onOpen(context);
    try {
      log14.verbose("host acquire lock", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 190, S: this });
      this._invitationFlowLock = await tryAcquireBeforeContextDisposed(this._ctx, this._invitationFlowMutex);
      log14.verbose("host lock acquired", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 192, S: this });
      this._callbacks.onStateUpdate(Invitation5.State.CONNECTING);
      await this.rpc.InvitationHostService.options({
        role: InvitationOptions2.Role.HOST
      });
      log14.verbose("options sent", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 197, S: this });
      await cancelWithContext5(this._ctx, this._remoteOptionsTrigger.wait({
        timeout: OPTIONS_TIMEOUT2
      }));
      log14.verbose("options received", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 201, S: this });
      if (this._remoteOptions?.role !== InvitationOptions2.Role.GUEST) {
        throw new InvalidInvitationExtensionRoleError2({
          context: {
            expected: InvitationOptions2.Role.GUEST,
            remoteOptions: this._remoteOptions,
            remotePeerId: context.remotePeerId
          }
        });
      }
      this._callbacks.onStateUpdate(Invitation5.State.CONNECTED);
      this._callbacks.onOpen(this._ctx, context);
    } catch (err) {
      if (this._invitationFlowLock != null) {
        this._callbacks.onError(err);
      }
      if (!this._ctx.disposed) {
        context.close(err);
      }
    }
  }
  _requireActiveInvitation() {
    const invitation = this._callbacks.activeInvitation;
    if (invitation == null) {
      scheduleTask6(this._ctx, () => this.close());
      throw new Error("Active invitation not found");
    }
    return invitation;
  }
  _assertInvitationState(stateOrMany) {
    const invitation = this._requireActiveInvitation();
    const validStates = Array.isArray(stateOrMany) ? stateOrMany : [
      stateOrMany
    ];
    if (!validStates.includes(invitation.state)) {
      scheduleTask6(this._ctx, () => this.close());
      throw new InvariantViolation(`Expected ${stateToString(invitation.state)} to be one of [${validStates.map(stateToString).join(", ")}]`);
    }
  }
  async onClose() {
    await this._destroy();
  }
  async onAbort() {
    await this._destroy();
  }
  async _destroy() {
    await this._ctx.dispose();
    if (this._invitationFlowLock != null) {
      this._invitationFlowLock?.release();
      this._invitationFlowLock = null;
      log14.verbose("invitation flow lock released", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file19, L: 251, S: this });
    }
  }
};
var isAuthenticationRequired = (invitation) => invitation.authMethod !== Invitation5.AuthMethod.NONE;

// src/packlets/invitations/invitation-state.ts
import { Mutex as Mutex3 } from "@dxos/async";
import { log as log15 } from "@dxos/log";
import { Invitation as Invitation6 } from "@dxos/protocols/proto/dxos/client/services";
var __dxlog_file20 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/invitation-state.ts";
var createGuardedInvitationState = (ctx, invitation, stream) => {
  const mutex = new Mutex3();
  let lastActiveLockHolder = null;
  let currentInvitation = {
    ...invitation
  };
  const isStateChangeAllowed = (lockHolder) => {
    if (ctx.disposed || lockHolder !== null && mutex.isLocked() && !lockHolder.hasFlowLock()) {
      return false;
    }
    return lockHolder == null || lastActiveLockHolder !== lockHolder || isNonTerminalState(currentInvitation.state);
  };
  return {
    mutex,
    get current() {
      return currentInvitation;
    },
    complete: (newState) => {
      logStateUpdate(currentInvitation, void 0, invitation.state);
      currentInvitation = {
        ...currentInvitation,
        ...newState
      };
      stream.next(currentInvitation);
      return ctx.dispose();
    },
    set: (lockHolder, newState) => {
      if (isStateChangeAllowed(lockHolder)) {
        logStateUpdate(currentInvitation, lockHolder, newState);
        currentInvitation = {
          ...currentInvitation,
          state: newState
        };
        stream.next(currentInvitation);
        lastActiveLockHolder = lockHolder;
        return true;
      }
      return false;
    },
    error: (lockHolder, error) => {
      if (isStateChangeAllowed(lockHolder)) {
        logStateUpdate(currentInvitation, lockHolder, Invitation6.State.ERROR, error);
        currentInvitation = {
          ...currentInvitation,
          state: Invitation6.State.ERROR
        };
        stream.next(currentInvitation);
        stream.error(error);
        lastActiveLockHolder = lockHolder;
        return true;
      }
      return false;
    }
  };
};
var logStateUpdate = (invitation, actor, newState, error) => {
  const logContext = {
    invitationId: invitation.invitationId,
    actor: actor?.constructor.name,
    newState: stateToString(newState),
    oldState: stateToString(invitation.state),
    error: error?.message,
    errorStack: error?.stack
  };
  log15.verbose("dxos.sdk.invitations-handler.state.update", logContext, { "~LogMeta": "~LogMeta", F: __dxlog_file20, L: 75, S: void 0 });
};
var isNonTerminalState = (currentState) => {
  return ![
    Invitation6.State.SUCCESS,
    Invitation6.State.ERROR,
    Invitation6.State.CANCELLED,
    Invitation6.State.TIMEOUT,
    Invitation6.State.EXPIRED
  ].includes(currentState);
};

// src/packlets/invitations/invitation-topology.ts
import { invariant as invariant17 } from "@dxos/invariant";
import { PublicKey as PublicKey8 } from "@dxos/keys";
import { log as log16 } from "@dxos/log";
import { InvitationOptions as InvitationOptions3 } from "@dxos/protocols/proto/dxos/halo/invitations";
import { ComplexSet as ComplexSet4 } from "@dxos/util";
var __dxlog_file21 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/invitation-topology.ts";
var InvitationTopology = class {
  _role;
  _controller;
  /**
  * Peers we tried to establish a connection with.
  * In invitation flow peers are assigned random ids when they join the swarm, so we'll retry
  * a peer if they reload an invitation.
  *
  * Consider keeping a separate set for peers we know are hosts and have some retry timeout
  * for guests we failed an invitation flow with (potentially due to a network error).
  */
  _seenPeers = new ComplexSet4(PublicKey8.hash);
  constructor(_role) {
    this._role = _role;
  }
  init(controller) {
    invariant17(!this._controller, "Already initialized.", { "~LogMeta": "~LogMeta", F: __dxlog_file21, L: 37, S: this, A: ["!this._controller", "'Already initialized.'"] });
    this._controller = controller;
  }
  update() {
    invariant17(this._controller, "Not initialized.", { "~LogMeta": "~LogMeta", F: __dxlog_file21, L: 41, S: this, A: ["this._controller", "'Not initialized.'"] });
    const { ownPeerId, candidates, connected, allPeers } = this._controller.getState();
    if (this._role === InvitationOptions3.Role.GUEST) {
      return;
    }
    if (connected.length > 0) {
      connected.forEach((c) => this._seenPeers.add(c));
      return;
    }
    const firstUnknownPeer = candidates.find((peerId) => !this._seenPeers.has(peerId));
    this._seenPeers = new ComplexSet4(PublicKey8.hash, allPeers.filter((peerId) => this._seenPeers.has(peerId)));
    if (firstUnknownPeer != null) {
      log16("invitation connect", {
        ownPeerId,
        remotePeerId: firstUnknownPeer
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file21, L: 57, S: this });
      this._controller.connect(firstUnknownPeer);
      this._seenPeers.add(firstUnknownPeer);
    }
  }
  async onOffer(peer) {
    invariant17(this._controller, "Not initialized.", { "~LogMeta": "~LogMeta", F: __dxlog_file21, L: 66, S: this, A: ["this._controller", "'Not initialized.'"] });
    return !this._seenPeers.has(peer);
  }
  async destroy() {
    this._seenPeers.clear();
  }
  toString() {
    return `InvitationTopology(${this._role === InvitationOptions3.Role.GUEST ? "guest" : "host"})`;
  }
};

// src/packlets/invitations/invitations-handler.ts
var __dxlog_file22 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/invitations-handler.ts";
function _ts_decorate8(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var metrics = _trace.metrics;
var MAX_DELEGATED_INVITATION_HOST_TRIES = 3;
var InvitationsHandler = class {
  _networkManager;
  _edgeClient;
  _connectionProps;
  /**
  * @internal
  */
  constructor(_networkManager, _edgeClient, _connectionProps) {
    this._networkManager = _networkManager;
    this._edgeClient = _edgeClient;
    this._connectionProps = _connectionProps;
  }
  handleInvitationFlow(ctx, stream, protocol, invitation) {
    log17.verbose("dxos.sdk.invitations-handler.handleInvitationFlow", {
      state: invitation.state,
      invitationId: invitation.invitationId,
      kind: invitation.kind,
      type: invitation.type
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 43, S: this });
    metrics.increment("dxos.invitation.host");
    const hostSpanId = `invitation-host-${invitation.invitationId}`;
    const invitationCtx = ctx;
    ctx = _trace.spanStart({
      id: hostSpanId,
      instance: this,
      methodName: "handleInvitationFlow",
      parentCtx: ctx,
      op: "invitation.host",
      attributes: {
        "ctx.dxos.invitation.id": invitation.invitationId,
        "ctx.dxos.invitation.kind": Invitation7.Kind[invitation.kind]
      }
    }) ?? ctx;
    if (ctx !== invitationCtx) {
      ctx.onDispose(() => {
        void invitationCtx.dispose();
      });
    }
    ctx.onDispose(() => _trace.spanEnd(hostSpanId));
    const guardedState = createGuardedInvitationState(ctx, invitation, stream);
    const createExtension = () => {
      const extension = new InvitationHostExtension(guardedState.mutex, {
        get activeInvitation() {
          return ctx.disposed ? null : guardedState.current;
        },
        onStateUpdate: (newState) => {
          if (newState !== Invitation7.State.ERROR && newState !== Invitation7.State.TIMEOUT) {
            guardedState.set(extension, newState);
          }
          return guardedState.current;
        },
        admit: async (admissionRequest) => {
          try {
            log17.verbose("dxos.sdk.invitations-handler.host.admit", {
              invitationId: invitation.invitationId,
              ...protocol.toJSON()
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 90, S: this });
            const deviceKey = admissionRequest.device?.deviceKey ?? admissionRequest.space?.deviceKey;
            invariant18(deviceKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 95, S: this, A: ["deviceKey", ""] });
            const admissionResponse = await protocol.admit(invitation, admissionRequest, extension.guestProfile);
            extension.completedTrigger.wake(deviceKey);
            return admissionResponse;
          } catch (err) {
            guardedState.error(extension, err);
            throw err;
          }
        },
        onOpen: (connectionCtx, extensionsCtx) => {
          let admitted = false;
          connectionCtx.onDispose(() => {
            if (!admitted) {
              guardedState.set(extension, Invitation7.State.CONNECTING);
            }
          });
          scheduleTask7(connectionCtx, async () => {
            try {
              log17("opening host invitation handler", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 115, S: this });
              log17.verbose("connected", {
                ...protocol.toJSON()
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 116, S: this });
              const deviceKey = await extension.completedTrigger.wait({
                timeout: invitation.timeout
              });
              log17.verbose("admitted guest", {
                guest: deviceKey,
                ...protocol.toJSON()
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 122, S: this });
              guardedState.set(extension, Invitation7.State.SUCCESS);
              metrics.increment("dxos.invitation.success");
              log17("host invitation handler opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 128, S: this });
              admitted = true;
              if (!invitation.multiUse) {
                await ctx.dispose();
              }
            } catch (err) {
              const stateChanged = guardedState.set(extension, Invitation7.State.CONNECTING);
              if (err instanceof TimeoutError2) {
                if (stateChanged) {
                  metrics.increment("dxos.invitation.timeout");
                  log17.verbose("timeout", {
                    ...protocol.toJSON()
                  }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 138, S: this });
                }
              } else {
                if (stateChanged) {
                  metrics.increment("dxos.invitation.failed");
                  log17.error("failed", err, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 145, S: this });
                }
              }
              extensionsCtx.close(err);
            }
          });
        },
        onError: (err) => {
          const stateChanged = guardedState.set(extension, Invitation7.State.CONNECTING);
          if (err instanceof InvalidInvitationExtensionRoleError3) {
            log17("invalid role", {
              ...err.context
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 156, S: this });
            return;
          }
          if (err instanceof TimeoutError2) {
            if (stateChanged) {
              metrics.increment("dxos.invitation.timeout");
              log17.verbose("timeout", {
                err
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 164, S: this });
            }
          } else {
            if (stateChanged) {
              metrics.increment("dxos.invitation.failed");
              log17.error("failed", err, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 171, S: this });
            }
          }
        }
      });
      return extension;
    };
    const expiresOn = getExpirationTime(invitation);
    if (expiresOn) {
      if (expiresOn.getTime() < Date.now()) {
        log17.warn("invitation has already expired", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 181, S: this });
        guardedState.set(null, Invitation7.State.EXPIRED);
        void ctx.dispose().catch((err) => log17.catch(err, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 183, S: this }));
        return;
      }
      scheduleTask7(ctx, async () => {
        await swarmConnection.close(ctx);
        guardedState.set(null, Invitation7.State.EXPIRED);
        metrics.increment("dxos.invitation.expired");
        await ctx.dispose();
      }, expiresOn.getTime() - Date.now());
    }
    let swarmConnection;
    scheduleTask7(ctx, async () => {
      swarmConnection = await this._joinSwarm(ctx, invitation, InvitationOptions4.Role.HOST, createExtension);
      guardedState.set(null, Invitation7.State.CONNECTING);
    });
  }
  acceptInvitation(ctx, stream, protocol, invitation, otpEnteredTrigger, deviceProfile) {
    log17.verbose("dxos.sdk.invitations-handler.acceptInvitation", {
      state: invitation.state,
      invitationId: invitation.invitationId,
      kind: invitation.kind,
      type: invitation.type
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 201, S: this });
    const { timeout = INVITATION_TIMEOUT } = invitation;
    const guestSpanId = `invitation-guest-${invitation.invitationId}`;
    const invitationCtx = ctx;
    ctx = _trace.spanStart({
      id: guestSpanId,
      instance: this,
      methodName: "acceptInvitation",
      parentCtx: ctx,
      op: "invitation.guest",
      attributes: {
        "ctx.dxos.invitation.id": invitation.invitationId,
        "ctx.dxos.invitation.kind": Invitation7.Kind[invitation.kind]
      }
    }) ?? ctx;
    if (ctx !== invitationCtx) {
      ctx.onDispose(() => {
        void invitationCtx.dispose();
      });
    }
    ctx.onDispose(() => _trace.spanEnd(guestSpanId));
    if (deviceProfile) {
      invariant18(invitation.kind === Invitation7.Kind.DEVICE, "deviceProfile provided for non-device invitation", { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 235, S: this, A: ["invitation.kind === Invitation.Kind.DEVICE", "'deviceProfile provided for non-device invitation'"] });
    }
    const triedPeersIds = new ComplexSet5(PublicKey9.hash);
    const guardedState = createGuardedInvitationState(ctx, invitation, stream);
    const shouldCancelInvitationFlow = (extension) => {
      const isLockedByAnotherConnection = guardedState.mutex.isLocked() && !extension.hasFlowLock();
      log17("should cancel invitation flow", {
        isLockedByAnotherConnection,
        invitationType: Invitation7.Type.DELEGATED,
        triedPeers: triedPeersIds.size
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 241, S: this });
      if (isLockedByAnotherConnection) {
        return false;
      }
      return invitation.type !== Invitation7.Type.DELEGATED || triedPeersIds.size >= MAX_DELEGATED_INVITATION_HOST_TRIES;
    };
    let admitted = false;
    const createExtension = () => {
      const extension = new InvitationGuestExtension(guardedState.mutex, {
        onStateUpdate: (newState) => {
          guardedState.set(extension, newState);
        },
        onOpen: (connectionCtx, extensionCtx) => {
          triedPeersIds.add(extensionCtx.remotePeerId);
          if (admitted) {
            extensionCtx.close();
            return;
          }
          connectionCtx.onDispose(async () => {
            log17.verbose("extension disposed", {
              admitted,
              currentState: guardedState.current.state
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 266, S: this });
            if (!admitted) {
              guardedState.error(extension, new ContextDisposedError3());
              if (shouldCancelInvitationFlow(extension)) {
                await ctx.dispose();
              }
            }
          });
          scheduleTask7(connectionCtx, async () => {
            try {
              log17("opening guest invitation handler", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 279, S: this });
              scheduleTask7(connectionCtx, () => {
                guardedState.set(extension, Invitation7.State.TIMEOUT);
                extensionCtx.close();
              }, timeout);
              log17.verbose("dxos.sdk.invitations-handler.guest.connected", {
                ...protocol.toJSON()
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 284, S: this });
              guardedState.set(extension, Invitation7.State.CONNECTED);
              log17.verbose("dxos.sdk.invitations-handler.guest.introduce", {
                invitationId: invitation.invitationId,
                ...protocol.toJSON()
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 289, S: this });
              const introductionResponse = await extension.rpc.InvitationHostService.introduce({
                invitationId: invitation.invitationId,
                ...protocol.createIntroduction()
              });
              log17.verbose("dxos.sdk.invitations-handler.guest.introduce-response", {
                invitationId: invitation.invitationId,
                ...protocol.toJSON(),
                authMethod: introductionResponse.authMethod
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 297, S: this });
              invitation.authMethod = introductionResponse.authMethod;
              if (isAuthenticationRequired(invitation)) {
                switch (invitation.authMethod) {
                  case Invitation7.AuthMethod.SHARED_SECRET:
                    await this._handleGuestOtpAuth(extension, (state) => guardedState.set(extension, state), otpEnteredTrigger, {
                      timeout
                    });
                    break;
                  case Invitation7.AuthMethod.KNOWN_PUBLIC_KEY:
                    await this._handleGuestKpkAuth(extension, (state) => guardedState.set(extension, state), invitation, introductionResponse);
                    break;
                }
              }
              log17.verbose("dxos.sdk.invitations-handler.guest.request-admission", {
                invitationId: invitation.invitationId,
                ...protocol.toJSON()
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 317, S: this });
              const admissionRequest = await protocol.createAdmissionRequest(deviceProfile);
              const admissionResponse = await extension.rpc.InvitationHostService.admit(admissionRequest);
              admitted = true;
              const result = await protocol.accept(ctx, admissionResponse, admissionRequest);
              log17.verbose("dxos.sdk.invitations-handler.guest.admitted-by-host", {
                invitationId: invitation.invitationId,
                ...protocol.toJSON()
              }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 328, S: this });
              guardedState.complete({
                ...guardedState.current,
                ...result,
                state: Invitation7.State.SUCCESS
              });
              log17("guest invitation handler opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 337, S: this });
            } catch (err) {
              if (err instanceof TimeoutError2) {
                log17.verbose("timeout", {
                  ...protocol.toJSON()
                }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 340, S: this });
                guardedState.set(extension, Invitation7.State.TIMEOUT);
              } else {
                log17.verbose("auth failed", err, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 345, S: this });
                guardedState.error(extension, err);
              }
              extensionCtx.close(err);
            }
          });
        },
        onError: (err) => {
          if (err instanceof InvalidInvitationExtensionRoleError3) {
            return;
          }
          if (err instanceof TimeoutError2) {
            log17.verbose("timeout", {
              ...protocol.toJSON()
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 357, S: this });
            guardedState.set(extension, Invitation7.State.TIMEOUT);
          } else {
            log17.verbose("auth failed", err, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 362, S: this });
            guardedState.error(extension, err);
          }
        }
      });
      return extension;
    };
    const edgeInvitationHandler = new EdgeInvitationHandler(this._connectionProps?.edgeInvitations, this._edgeClient, {
      onInvitationSuccess: async (edgeCtx, admissionResponse, admissionRequest) => {
        const result = await protocol.accept(edgeCtx, admissionResponse, admissionRequest);
        log17.info("admitted by edge", {
          ...protocol.toJSON()
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 372, S: this });
        guardedState.complete({
          ...guardedState.current,
          ...result,
          state: Invitation7.State.SUCCESS
        });
      }
    });
    edgeInvitationHandler.handle(ctx, guardedState, protocol, deviceProfile);
    scheduleTask7(ctx, async () => {
      const error = checkInvitation(protocol, invitation);
      if (error) {
        stream.error(error);
        await ctx.dispose();
      } else {
        invariant18(invitation.swarmKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 389, S: this, A: ["invitation.swarmKey", ""] });
        const timeoutInactive = () => {
          if (guardedState.mutex.isLocked()) {
            scheduleTask7(ctx, timeoutInactive, timeout);
          } else {
            guardedState.set(null, Invitation7.State.TIMEOUT);
          }
        };
        scheduleTask7(ctx, timeoutInactive, timeout);
        await this._joinSwarm(ctx, invitation, InvitationOptions4.Role.GUEST, createExtension);
        guardedState.set(null, Invitation7.State.CONNECTING);
      }
    });
  }
  async _joinSwarm(ctx, invitation, role, extensionFactory) {
    let label;
    if (role === InvitationOptions4.Role.GUEST) {
      label = "invitation guest";
    } else if (invitation.kind === Invitation7.Kind.DEVICE) {
      label = "invitation host for device";
    } else {
      label = `invitation host for space ${invitation.spaceKey?.truncate()}`;
    }
    const swarmConnection = await this._networkManager.joinSwarm(ctx, {
      topic: invitation.swarmKey,
      protocolProvider: createTeleportProtocolFactory(async (teleport) => {
        teleport.addExtension("dxos.halo.invitations", extensionFactory());
      }, this._connectionProps?.teleport),
      topology: new InvitationTopology(role),
      label
    });
    ctx.onDispose(() => swarmConnection.close(ctx));
    return swarmConnection;
  }
  async _handleGuestOtpAuth(extension, setState, authenticated, options) {
    for (let attempt = 1; attempt <= MAX_OTP_ATTEMPTS; attempt++) {
      log17("guest waiting for authentication code...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 426, S: this });
      setState(Invitation7.State.READY_FOR_AUTHENTICATION);
      const authCode = await authenticated.wait(options);
      log17("sending authentication request", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 429, S: this });
      setState(Invitation7.State.AUTHENTICATING);
      const response = await extension.rpc.InvitationHostService.authenticate({
        authCode
      });
      if (response.status === void 0 || response.status === AuthenticationResponse2.Status.OK) {
        break;
      }
      if (response.status === AuthenticationResponse2.Status.INVALID_OTP) {
        if (attempt === MAX_OTP_ATTEMPTS) {
          throw new Error(`Maximum retry attempts: ${MAX_OTP_ATTEMPTS}`);
        } else {
          log17("retrying invalid code", {
            attempt
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 441, S: this });
          authenticated.reset();
        }
      }
    }
  }
  async _handleGuestKpkAuth(extension, setState, invitation, introductionResponse) {
    if (invitation.guestKeypair?.privateKey == null) {
      throw new Error("keypair missing in the invitation");
    }
    if (introductionResponse.challenge == null) {
      throw new Error("challenge missing in the introduction");
    }
    log17("sending authentication request", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file22, L: 456, S: this });
    const signature = sign2(Buffer.from(introductionResponse.challenge), invitation.guestKeypair.privateKey);
    const response = await extension.rpc.InvitationHostService.authenticate({
      signedChallenge: signature
    });
    if (response.status !== AuthenticationResponse2.Status.OK) {
      throw new Error(`Authentication failed with code: ${response.status}`);
    }
  }
};
InvitationsHandler = _ts_decorate8([
  _trace.resource()
], InvitationsHandler);
var checkInvitation = (protocol, invitation) => {
  const expiresOn = getExpirationTime(invitation);
  if (expiresOn && expiresOn.getTime() < Date.now()) {
    return new InvalidInvitationError({
      message: "Invitation already expired."
    });
  }
  return protocol.checkInvitation(invitation);
};
var createAdmissionKeypair = () => {
  const keypair = createKeyPair();
  return {
    publicKey: PublicKey9.from(keypair.publicKey),
    privateKey: keypair.secretKey
  };
};

// src/packlets/invitations/invitations-service.ts
import { Stream as Stream9 } from "@dxos/codec-protobuf";
import { QueryInvitationsResponse } from "@dxos/protocols/proto/dxos/client/services";
import { trace as trace6 } from "@dxos/tracing";
var InvitationsServiceImpl = class {
  _invitationsManager;
  constructor(_invitationsManager) {
    this._invitationsManager = _invitationsManager;
  }
  // TODO(burdon): Guest/host label.
  getLoggingContext() {
    return {};
  }
  createInvitation(request, options) {
    return new Stream9(({ ctx, next, close }) => {
      void this._invitationsManager.createInvitation(ctx, request).then((invitation) => {
        trace6.metrics.increment("dxos.invitation.created");
        invitation.subscribe(next, close, close);
      }).catch(close);
    }, options?.ctx);
  }
  acceptInvitation(request, options) {
    return new Stream9(({ ctx, next, close }) => {
      const invitation = this._invitationsManager.acceptInvitation(ctx, request);
      invitation.subscribe(next, close, close);
    }, options?.ctx);
  }
  async authenticate(request) {
    return this._invitationsManager.authenticate(request);
  }
  async cancelInvitation(request) {
    return this._invitationsManager.cancelInvitation(request);
  }
  queryInvitations() {
    return new Stream9(({ next, ctx }) => {
      this._invitationsManager.invitationCreated.on(ctx, (invitation) => {
        next({
          action: QueryInvitationsResponse.Action.ADDED,
          type: QueryInvitationsResponse.Type.CREATED,
          invitations: [
            invitation
          ]
        });
      });
      this._invitationsManager.invitationAccepted.on(ctx, (invitation) => {
        next({
          action: QueryInvitationsResponse.Action.ADDED,
          type: QueryInvitationsResponse.Type.ACCEPTED,
          invitations: [
            invitation
          ]
        });
      });
      this._invitationsManager.removedCreated.on(ctx, (invitation) => {
        next({
          action: QueryInvitationsResponse.Action.REMOVED,
          type: QueryInvitationsResponse.Type.CREATED,
          invitations: [
            invitation
          ]
        });
      });
      this._invitationsManager.removedAccepted.on(ctx, (invitation) => {
        next({
          action: QueryInvitationsResponse.Action.REMOVED,
          type: QueryInvitationsResponse.Type.ACCEPTED,
          invitations: [
            invitation
          ]
        });
      });
      this._invitationsManager.saved.on(ctx, (invitation) => {
        next({
          action: QueryInvitationsResponse.Action.SAVED,
          type: QueryInvitationsResponse.Type.CREATED,
          invitations: [
            invitation
          ]
        });
      });
      next({
        action: QueryInvitationsResponse.Action.ADDED,
        type: QueryInvitationsResponse.Type.CREATED,
        invitations: this._invitationsManager.getCreatedInvitations(),
        existing: true
      });
      next({
        action: QueryInvitationsResponse.Action.ADDED,
        type: QueryInvitationsResponse.Type.ACCEPTED,
        invitations: this._invitationsManager.getAcceptedInvitations(),
        existing: true
      });
      this._invitationsManager.onPersistentInvitationsLoaded(ctx, () => {
        next({
          action: QueryInvitationsResponse.Action.LOAD_COMPLETE,
          type: QueryInvitationsResponse.Type.CREATED
        });
      });
    });
  }
};

// src/packlets/invitations/space-invitation-protocol.ts
import { createCancelDelegatedSpaceInvitationCredential, createDelegatedSpaceInvitationCredential, getCredentialAssertion as getCredentialAssertion4 } from "@dxos/credentials";
import { writeMessages as writeMessages4 } from "@dxos/feed-store";
import { invariant as invariant19 } from "@dxos/invariant";
import { log as log18 } from "@dxos/log";
import { AlreadyJoinedError as AlreadyJoinedError3, AuthorizationError as AuthorizationError2, InvalidInvitationError as InvalidInvitationError2, SpaceNotFoundError as SpaceNotFoundError2 } from "@dxos/protocols";
import { Invitation as Invitation8 } from "@dxos/protocols/proto/dxos/client/services";
import { SpaceMember as SpaceMember6 } from "@dxos/protocols/proto/dxos/halo/credentials";
var __dxlog_file23 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/space-invitation-protocol.ts";
var SpaceInvitationProtocol = class {
  _spaceManager;
  _signingContext;
  _keyring;
  _spaceKey;
  constructor(_spaceManager, _signingContext, _keyring, _spaceKey) {
    this._spaceManager = _spaceManager;
    this._signingContext = _signingContext;
    this._keyring = _keyring;
    this._spaceKey = _spaceKey;
  }
  toJSON() {
    return {
      kind: "space",
      deviceKey: this._signingContext.deviceKey,
      spaceKey: this._spaceKey
    };
  }
  checkCanInviteNewMembers() {
    if (this._spaceKey == null) {
      return new InvalidInvitationError2({
        message: "No spaceKey was provided for a space invitation."
      });
    }
    const space = this._spaceManager.spaces.get(this._spaceKey);
    if (space == null) {
      return new SpaceNotFoundError2(this._spaceKey);
    }
    if (!space?.inner.spaceState.hasMembershipManagementPermission(this._signingContext.identityKey)) {
      return new AuthorizationError2({
        message: "No member management permission."
      });
    }
    return void 0;
  }
  getInvitationContext() {
    invariant19(this._spaceKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 48, S: this, A: ["this._spaceKey", ""] });
    const space = this._spaceManager.spaces.get(this._spaceKey);
    invariant19(space, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 50, S: this, A: ["space", ""] });
    return {
      kind: Invitation8.Kind.SPACE,
      spaceKey: this._spaceKey,
      spaceId: space.id
    };
  }
  async admit(invitation, request, guestProfile) {
    invariant19(this._spaceKey && request.space, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 58, S: this, A: ["this._spaceKey && request.space", ""] });
    log18("writing guest credentials", {
      host: this._signingContext.deviceKey,
      guest: request.space.deviceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 59, S: this });
    const spaceMemberCredential = await this._spaceManager.admitMember({
      spaceKey: this._spaceKey,
      identityKey: request.space.identityKey,
      role: invitation.role ?? SpaceMember6.Role.ADMIN,
      profile: guestProfile,
      delegationCredentialId: invitation.delegationCredentialId
    });
    const space = this._spaceManager.spaces.get(this._spaceKey);
    return {
      space: {
        credential: spaceMemberCredential,
        controlTimeframe: space?.inner.controlPipeline.state.timeframe
      }
    };
  }
  async delegate(invitation) {
    invariant19(this._spaceKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 79, S: this, A: ["this._spaceKey", ""] });
    const space = this._spaceManager.spaces.get(this._spaceKey);
    invariant19(space, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 81, S: this, A: ["space", ""] });
    if (invitation.authMethod === Invitation8.AuthMethod.KNOWN_PUBLIC_KEY) {
      invariant19(invitation.guestKeypair?.publicKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 83, S: this, A: ["invitation.guestKeypair?.publicKey", ""] });
    }
    log18("writing delegate space invitation", {
      host: this._signingContext.deviceKey,
      id: invitation.invitationId
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 85, S: this });
    const credential = await createDelegatedSpaceInvitationCredential(this._signingContext.credentialSigner, space.key, {
      invitationId: invitation.invitationId,
      authMethod: invitation.authMethod,
      swarmKey: invitation.swarmKey,
      role: invitation.role ?? SpaceMember6.Role.ADMIN,
      expiresOn: computeExpirationTime(invitation),
      multiUse: invitation.multiUse ?? false,
      guestKey: invitation.authMethod === Invitation8.AuthMethod.KNOWN_PUBLIC_KEY ? invitation.guestKeypair.publicKey : void 0
    });
    invariant19(credential.credential, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 98, S: this, A: ["credential.credential", ""] });
    await writeMessages4(space.inner.controlPipeline.writer, [
      credential
    ]);
    return credential.credential.credential.id;
  }
  async cancelDelegation(invitation) {
    invariant19(this._spaceKey, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 105, S: this, A: ["this._spaceKey", ""] });
    invariant19(invitation.type === Invitation8.Type.DELEGATED && invitation.delegationCredentialId, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 106, S: this, A: ["invitation.type === Invitation.Type.DELEGATED && invitation.delegationCredentialId", ""] });
    const space = this._spaceManager.spaces.get(this._spaceKey);
    invariant19(space, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 108, S: this, A: ["space", ""] });
    log18("cancelling delegated space invitation", {
      host: this._signingContext.deviceKey,
      id: invitation.invitationId
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 109, S: this });
    const credential = await createCancelDelegatedSpaceInvitationCredential(this._signingContext.credentialSigner, space.key, invitation.delegationCredentialId);
    invariant19(credential.credential, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 114, S: this, A: ["credential.credential", ""] });
    await writeMessages4(space.inner.controlPipeline.writer, [
      credential
    ]);
  }
  checkInvitation(invitation) {
    if (invitation.spaceKey == null) {
      return new InvalidInvitationError2({
        message: "No spaceKey was provided for a space invitation."
      });
    }
    if (this._spaceManager.spaces.has(invitation.spaceKey)) {
      return new AlreadyJoinedError3({
        message: "Already joined space."
      });
    }
  }
  createIntroduction() {
    return {
      profile: this._signingContext.getProfile()
    };
  }
  async createAdmissionRequest() {
    const controlFeedKey = await this._keyring.createKey();
    const dataFeedKey = await this._keyring.createKey();
    return {
      space: {
        identityKey: this._signingContext.identityKey,
        deviceKey: this._signingContext.deviceKey,
        controlFeedKey,
        dataFeedKey
      }
    };
  }
  async accept(ctx, response) {
    invariant19(response.space, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 150, S: this, A: ["response.space", ""] });
    const { credential, controlTimeframe, dataTimeframe } = response.space;
    const assertion = getCredentialAssertion4(credential);
    invariant19(assertion["@type"] === "dxos.halo.credentials.SpaceMember", "Invalid credential", { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 153, S: this, A: ["assertion['@type'] === 'dxos.halo.credentials.SpaceMember'", "'Invalid credential'"] });
    invariant19(credential.subject.id.equals(this._signingContext.identityKey), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file23, L: 154, S: this, A: ["credential.subject.id.equals(this._signingContext.identityKey)", ""] });
    if (this._spaceManager.spaces.has(assertion.spaceKey)) {
      throw new AlreadyJoinedError3({
        message: "Already joined space."
      });
    }
    await this._spaceManager.acceptSpace(ctx, {
      spaceKey: assertion.spaceKey,
      genesisFeedKey: assertion.genesisFeedKey,
      controlTimeframe,
      dataTimeframe,
      tags: assertion.tags
    });
    await this._signingContext.recordCredential(credential);
    return {
      spaceKey: assertion.spaceKey
    };
  }
};

// src/packlets/invitations/invitations-manager.ts
import { Event as Event8, PushStream, TimeoutError as TimeoutError3, Trigger as Trigger5 } from "@dxos/async";
import { AuthenticatingInvitation, AUTHENTICATION_CODE_LENGTH, CancellableInvitation, INVITATION_TIMEOUT as INVITATION_TIMEOUT2 } from "@dxos/client-protocol";
import { generatePasscode } from "@dxos/credentials";
import { hasInvitationExpired } from "@dxos/echo-host";
import { invariant as invariant20 } from "@dxos/invariant";
import { PublicKey as PublicKey10 } from "@dxos/keys";
import { log as log19 } from "@dxos/log";
import { Invitation as Invitation9 } from "@dxos/protocols/proto/dxos/client/services";
import { SpaceMember as SpaceMember7 } from "@dxos/protocols/proto/dxos/halo/credentials";
import { trace as trace7 } from "@dxos/tracing";
var __dxlog_file24 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/invitations/invitations-manager.ts";
function _ts_decorate9(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var InvitationsManager = class {
  _invitationsHandler;
  _getHandler;
  _metadataStore;
  _createInvitations = /* @__PURE__ */ new Map();
  _acceptInvitations = /* @__PURE__ */ new Map();
  invitationCreated = new Event8();
  invitationAccepted = new Event8();
  removedCreated = new Event8();
  removedAccepted = new Event8();
  saved = new Event8();
  _persistentInvitationsLoadedEvent = new Event8();
  _persistentInvitationsLoaded = false;
  constructor(_invitationsHandler, _getHandler, _metadataStore) {
    this._invitationsHandler = _invitationsHandler;
    this._getHandler = _getHandler;
    this._metadataStore = _metadataStore;
  }
  async createInvitation(ctx, options) {
    if (options.invitationId) {
      const existingInvitation = this._createInvitations.get(options.invitationId);
      if (existingInvitation) {
        return existingInvitation;
      }
    }
    const handler = this._getHandler(options);
    const invitationError = handler.checkCanInviteNewMembers();
    if (invitationError != null) {
      throw invitationError;
    }
    const invitation = this._createInvitation(handler, options);
    const { ctx: invitationCtx, stream, observableInvitation } = this._createObservableInvitation(ctx, handler, invitation);
    this._createInvitations.set(invitation.invitationId, observableInvitation);
    this.invitationCreated.emit(invitation);
    this._onInvitationComplete(observableInvitation, async () => {
      this._createInvitations.delete(observableInvitation.get().invitationId);
      this.removedCreated.emit(observableInvitation.get());
      if (observableInvitation.get().persistent) {
        await this._safeDeleteInvitation(observableInvitation.get());
      }
    });
    try {
      await this._persistIfRequired(handler, stream, invitation);
    } catch (err) {
      log19.catch(err, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 66, S: this });
      await observableInvitation.cancel();
      return observableInvitation;
    }
    this._invitationsHandler.handleInvitationFlow(invitationCtx, stream, handler, observableInvitation.get());
    return observableInvitation;
  }
  async loadPersistentInvitations(ctx) {
    if (this._persistentInvitationsLoaded) {
      const invitations = this.getCreatedInvitations().filter((i) => i.persistent);
      return {
        invitations
      };
    }
    try {
      const persistentInvitations = this._metadataStore.getInvitations();
      const freshInvitations = persistentInvitations.filter((invitation) => !hasInvitationExpired(invitation));
      const loadTasks = freshInvitations.map((persistentInvitation) => {
        invariant20(!this._createInvitations.get(persistentInvitation.invitationId), "invitation already exists", { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 85, S: this, A: ["!this._createInvitations.get(persistentInvitation.invitationId)", "'invitation already exists'"] });
        return this.createInvitation(ctx, {
          ...persistentInvitation,
          persistent: false
        });
      });
      const cInvitations = await Promise.all(loadTasks);
      return {
        invitations: cInvitations.map((invitation) => invitation.get())
      };
    } catch (err) {
      log19.catch(err, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 96, S: this });
      return {
        invitations: []
      };
    } finally {
      this._persistentInvitationsLoadedEvent.emit();
      this._persistentInvitationsLoaded = true;
    }
  }
  acceptInvitation(ctx, request) {
    const options = request.invitation;
    const existingInvitation = this._acceptInvitations.get(options.invitationId);
    if (existingInvitation) {
      return existingInvitation;
    }
    const handler = this._getHandler(options);
    const { ctx: invitationCtx, invitation, stream, otpEnteredTrigger } = this._createObservableAcceptingInvitation(ctx, handler, options);
    this._invitationsHandler.acceptInvitation(invitationCtx, stream, handler, options, otpEnteredTrigger, request.deviceProfile);
    this._acceptInvitations.set(invitation.get().invitationId, invitation);
    this.invitationAccepted.emit(invitation.get());
    this._onInvitationComplete(invitation, () => {
      this._acceptInvitations.delete(invitation.get().invitationId);
      this.removedAccepted.emit(invitation.get());
    });
    return invitation;
  }
  async authenticate({ invitationId, authCode }) {
    log19("authenticating...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 123, S: this });
    invariant20(invitationId, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 124, S: this, A: ["invitationId", ""] });
    const observable = this._acceptInvitations.get(invitationId);
    if (!observable) {
      log19.warn("invalid invitation", {
        invitationId
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 127, S: this });
    } else {
      await observable.authenticate(authCode);
    }
  }
  async cancelInvitation({ invitationId }) {
    log19("cancelInvitation...", {
      invitationId
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 135, S: this });
    invariant20(invitationId, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 138, S: this, A: ["invitationId", ""] });
    const created = this._createInvitations.get(invitationId);
    if (created) {
      if (created.get().persistent) {
        await this._metadataStore.removeInvitation(invitationId);
      }
      if (created.get().type === Invitation9.Type.DELEGATED) {
        const handler = this._getHandler(created.get());
        await handler.cancelDelegation(created.get());
      }
      await created.cancel();
      this._createInvitations.delete(invitationId);
      this.removedCreated.emit(created.get());
      return;
    }
    const accepted = this._acceptInvitations.get(invitationId);
    if (accepted) {
      await accepted.cancel();
      this._acceptInvitations.delete(invitationId);
      this.removedAccepted.emit(accepted.get());
    }
  }
  getCreatedInvitations() {
    return [
      ...this._createInvitations.values()
    ].map((i) => i.get());
  }
  getAcceptedInvitations() {
    return [
      ...this._acceptInvitations.values()
    ].map((i) => i.get());
  }
  onPersistentInvitationsLoaded(ctx, callback) {
    if (this._persistentInvitationsLoaded) {
      callback();
    } else {
      this._persistentInvitationsLoadedEvent.once(ctx, () => callback());
    }
  }
  _createInvitation(protocol, _options) {
    const { invitationId = PublicKey10.random().toHex(), type = Invitation9.Type.INTERACTIVE, authMethod = Invitation9.AuthMethod.SHARED_SECRET, state = Invitation9.State.INIT, timeout = INVITATION_TIMEOUT2, swarmKey = PublicKey10.random(), persistent = _options?.authMethod !== Invitation9.AuthMethod.KNOWN_PUBLIC_KEY, created = /* @__PURE__ */ new Date(), guestKeypair = void 0, role = SpaceMember7.Role.ADMIN, lifetime = 86400 * 7, multiUse = false, ...options } = _options ?? {};
    const authCode = options?.authCode ?? (authMethod === Invitation9.AuthMethod.SHARED_SECRET ? generatePasscode(AUTHENTICATION_CODE_LENGTH) : void 0);
    return {
      invitationId,
      type,
      authMethod,
      state,
      swarmKey,
      authCode,
      timeout,
      persistent: persistent && type !== Invitation9.Type.DELEGATED,
      guestKeypair: guestKeypair ?? (authMethod === Invitation9.AuthMethod.KNOWN_PUBLIC_KEY ? createAdmissionKeypair() : void 0),
      created,
      lifetime,
      role,
      multiUse,
      delegationCredentialId: options?.delegationCredentialId,
      ...options,
      ...protocol.getInvitationContext()
    };
  }
  _createObservableInvitation(ctx, handler, invitation) {
    const stream = new PushStream();
    const invitationCtx = ctx.derive({
      onError: (err) => {
        stream.error(err);
        void invitationCtx.dispose();
      }
    });
    invitationCtx.onDispose(() => {
      log19("complete", {
        ...handler.toJSON()
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 209, S: this });
      stream.complete();
    });
    const observableInvitation = new CancellableInvitation({
      initialInvitation: invitation,
      subscriber: stream.observable,
      onCancel: async () => {
        stream.next({
          ...invitation,
          state: Invitation9.State.CANCELLED
        });
        await invitationCtx.dispose();
      }
    });
    return {
      ctx: invitationCtx,
      stream,
      observableInvitation
    };
  }
  _createObservableAcceptingInvitation(parentCtx, handler, initialState) {
    const otpEnteredTrigger = new Trigger5();
    const stream = new PushStream();
    const ctx = parentCtx.derive({
      onError: (err) => {
        if (err instanceof TimeoutError3) {
          log19("timeout", {
            ...handler.toJSON()
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 238, S: this });
          stream.next({
            ...initialState,
            state: Invitation9.State.TIMEOUT
          });
        } else {
          log19.warn("auth failed", err, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 246, S: this });
          stream.next({
            ...initialState,
            state: Invitation9.State.ERROR
          });
        }
        void ctx.dispose();
      }
    });
    ctx.onDispose(() => {
      log19("complete", {
        ...handler.toJSON()
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 256, S: this });
      stream.complete();
    });
    const invitation = new AuthenticatingInvitation({
      initialInvitation: initialState,
      subscriber: stream.observable,
      onCancel: async () => {
        stream.next({
          ...initialState,
          state: Invitation9.State.CANCELLED
        });
        await ctx.dispose();
      },
      onAuthenticate: async (code) => {
        otpEnteredTrigger.wake(code);
      }
    });
    return {
      ctx,
      invitation,
      stream,
      otpEnteredTrigger
    };
  }
  async _persistIfRequired(handler, changeStream, invitation) {
    if (invitation.type === Invitation9.Type.DELEGATED && invitation.delegationCredentialId == null) {
      const delegationCredentialId = await handler.delegate(invitation);
      changeStream.next({
        ...invitation,
        delegationCredentialId
      });
    } else if (invitation.persistent) {
      await this._metadataStore.addInvitation(invitation);
      this.saved.emit(invitation);
    }
  }
  async _safeDeleteInvitation(invitation) {
    try {
      await this._metadataStore.removeInvitation(invitation.invitationId);
    } catch (err) {
      log19.catch(err, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file24, L: 299, S: this });
    }
  }
  _onInvitationComplete(invitation, callback) {
    invitation.subscribe(() => {
    }, () => {
    }, callback);
  }
};
_ts_decorate9([
  trace7.span({
    showInBrowserTimeline: true,
    op: "lifecycle"
  })
], InvitationsManager.prototype, "createInvitation", null);
InvitationsManager = _ts_decorate9([
  trace7.resource()
], InvitationsManager);

// src/packlets/locks/index.ts
var locks_exports = {};
__reExport(locks_exports, locks_platform_star);
import * as locks_platform_star from "#locks-platform";

// src/packlets/services/feed-syncer.ts
import { Encoder, decode as cborXdecode } from "cbor-x";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncTask, Mutex as Mutex4, scheduleTask as scheduleTask8 } from "@dxos/async";
import { Resource as Resource7 } from "@dxos/context";
import { MessageSchema } from "@dxos/edge-client";
import { RuntimeProvider } from "@dxos/effect";
import { SyncClient } from "@dxos/feed";
import { invariant as invariant21 } from "@dxos/invariant";
import { SpaceId as SpaceId3 } from "@dxos/keys";
import { log as log20 } from "@dxos/log";
import { FeedProtocol as FeedProtocol3 } from "@dxos/protocols";
import { EdgeService as EdgeService2 } from "@dxos/protocols";
import { createBuf } from "@dxos/protocols/buf";
import { bufferToArray as bufferToArray2 } from "@dxos/util";
var __dxlog_file25 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/services/feed-syncer.ts";
function _ts_add_disposable_resource4(env, value, async) {
  if (value !== null && value !== void 0) {
    if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
    var dispose, inner;
    if (async) {
      if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
      dispose = value[Symbol.asyncDispose];
    }
    if (dispose === void 0) {
      if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
      dispose = value[Symbol.dispose];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    env.stack.push({
      value,
      dispose,
      async
    });
  } else if (async) {
    env.stack.push({
      async: true
    });
  }
  return value;
}
function _ts_dispose_resources4(env) {
  var _SuppressedError = typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
  };
  return (_ts_dispose_resources4 = function _ts_dispose_resources6(env2) {
    function fail(e) {
      env2.error = env2.hasError ? new _SuppressedError(e, env2.error, "An error was suppressed during disposal.") : e;
      env2.hasError = true;
    }
    var r, s = 0;
    function next() {
      while (r = env2.stack.pop()) {
        try {
          if (!r.async && s === 1) return s = 0, env2.stack.push(r), Promise.resolve().then(next);
          if (r.dispose) {
            var result = r.dispose.call(r.value);
            if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) {
              fail(e);
              return next();
            });
          } else s |= 1;
        } catch (e) {
          fail(e);
        }
      }
      if (s === 1) return env2.hasError ? Promise.reject(env2.error) : Promise.resolve();
      if (env2.hasError) throw env2.error;
    }
    return next();
  })(env);
}
var encoder = new Encoder({
  tagUint8Array: false,
  useRecords: false
});
var DEFAULT_MESSAGE_BLOCKS_LIMIT = 50;
var DEFAULT_SYNC_CONCURRENCY = 5;
var DEFAULT_POLLING_INTERVAL = 5e3;
var DEFAULT_POLL_REQUEST_THROTTLE_MS = 250;
var DEFAULT_PUSH_FAILURE_BACKOFF_MS = 250;
var MAX_PUSH_FAILURE_BACKOFF_MS = 3e4;
var MAX_BLOCKING_SYNC_ITERATIONS = 100;
var FeedSyncer = class extends Resource7 {
  #syncNamespaces;
  #messageBlocksLimit;
  #syncConcurrency;
  #pollingInterval;
  #pollRequestThrottleMs;
  #backgroundSync;
  #runtime;
  #feedStore;
  #edgeClient;
  #syncClient;
  #getSpaceIds;
  #spacesToPoll = /* @__PURE__ */ new Set();
  /** Last time full poll was completed. */
  #lastFullPoll = null;
  #throttledPollScheduled = false;
  #lastRequestedPollAt = null;
  #feedStoreMutex = new Mutex4();
  #pushFailureBackoffMs = DEFAULT_PUSH_FAILURE_BACKOFF_MS;
  constructor(options) {
    super();
    this.#runtime = options.runtime;
    this.#feedStore = options.feedStore;
    this.#edgeClient = options.edgeClient;
    this.#syncClient = new SyncClient({
      peerId: options.peerId,
      feedStore: options.feedStore,
      sendMessage: this.#sendMessage.bind(this),
      rpcTimeoutMs: options.syncRpcTimeoutMs
    });
    this.#getSpaceIds = options.getSpaceIds;
    this.#syncNamespaces = options.syncNamespaces;
    this.#messageBlocksLimit = options.messageBlocksLimit ?? DEFAULT_MESSAGE_BLOCKS_LIMIT;
    this.#syncConcurrency = options.syncConcurrency ?? DEFAULT_SYNC_CONCURRENCY;
    this.#pollingInterval = options.pollingInterval ?? DEFAULT_POLLING_INTERVAL;
    this.#pollRequestThrottleMs = options.pollRequestThrottleMs ?? DEFAULT_POLL_REQUEST_THROTTLE_MS;
    this.#backgroundSync = options.backgroundSync ?? true;
  }
  async _open() {
    this._ctx.onDispose(this.#edgeClient.onMessage((msg) => {
      if (!msg.serviceId) {
        return;
      }
      const service = msg.serviceId.split(":")[0];
      if (service !== EdgeService2.QUEUE_REPLICATOR) {
        return;
      }
      log20("feed sync edge ingress", {
        serviceId: msg.serviceId,
        payloadByteLength: msg.payload?.value?.byteLength
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 141, S: this });
      const handleMessageEffect = Effect.gen(this, function* () {
        const decoded = yield* Effect.try({
          try: () => cborXdecode(msg.payload.value),
          catch: (error) => new Error(`Failed to decode feed sync message: ${error}`)
        });
        const payload = yield* Schema.validate(FeedProtocol3.ProtocolMessage)(decoded);
        yield* this.#syncClient.handleMessage(payload);
      }).pipe(Effect.tapError((cause) => Effect.sync(() => log20("feed sync edge message handling failed", {
        serviceId: msg.serviceId,
        payloadByteLength: msg.payload?.value?.byteLength,
        cause: cause instanceof Error ? cause.message : String(cause)
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 152, S: this }))));
      void this.#runSerialized(() => RuntimeProvider.runPromise(this.#runtime)(handleMessageEffect));
    }));
    if (this.#backgroundSync) {
      await this.#pollTask.open();
      await this.#pushTask.open();
      this.#feedStore.onNewBlocks.on(this._ctx, () => {
        this.#pushTask.schedule();
      });
    }
    this._ctx.onDispose(
      // NOTE: Fires immediately (as a microtask) if the connection is already open, and again
      // on every subsequent reconnect.
      this.#edgeClient.onReconnected(async () => {
        log20("feed sync edge reconnected", {
          peerKey: this.#edgeClient.peerKey,
          identityDid: this.#edgeClient.identityDid
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 174, S: this });
        if (this.#backgroundSync) {
          this.#resetSpacesToPoll();
          this.#pollTask.schedule();
          this.#pushTask.schedule();
        }
      })
    );
    if (this.#backgroundSync) {
      this.#resetSpacesToPoll();
      this.#pollTask.schedule();
      this.#pushTask.schedule();
    }
  }
  async _close() {
    if (this.#backgroundSync) {
      await this.#pollTask.close();
      await this.#pushTask.close();
    }
  }
  /**
  * Schedules a best-effort pull without blocking the caller.
  */
  schedulePoll() {
    if (!this.#backgroundSync) {
      return;
    }
    this.#resetSpacesToPoll();
    if (this.#throttledPollScheduled) {
      return;
    }
    const now = Date.now();
    const delay = this.#lastRequestedPollAt == null ? 0 : Math.max(this.#pollRequestThrottleMs - (now - this.#lastRequestedPollAt), 0);
    this.#throttledPollScheduled = true;
    scheduleTask8(this._ctx, () => {
      this.#throttledPollScheduled = false;
      this.#lastRequestedPollAt = Date.now();
      this.#pollTask.schedule();
    }, delay);
  }
  /**
  * Returns per-namespace queue sync backlog for a space.
  * `blocksToPull` and `blocksToPush` of 0 mean caught up for that namespace.
  */
  async getSyncState(ctx, request) {
    const spaceId = request.spaceId;
    invariant21(SpaceId3.isValid(spaceId), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 222, S: this, A: ["SpaceId.isValid(spaceId)", ""] });
    const namespaces = request.namespaces != null && request.namespaces.length > 0 ? request.namespaces : this.#syncNamespaces;
    for (const feedNamespace of namespaces) {
      invariant21(FeedProtocol3.isWellKnownNamespace(feedNamespace), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 225, S: this, A: ["FeedProtocol.isWellKnownNamespace(feedNamespace)", ""] });
    }
    return this.#runSerialized(() => RuntimeProvider.runPromise(this.#runtime)(Effect.gen(this, function* () {
      const namespaceStates = yield* Effect.forEach(namespaces, (feedNamespace) => Effect.gen(this, function* () {
        const blocksToPush = yield* this.#feedStore.countUnpositionedBlocks({
          spaceId,
          feedNamespace
        });
        const totalBlocks = yield* this.#feedStore.countNamespaceBlocks({
          spaceId,
          feedNamespace
        });
        const { blocksToPull } = yield* this.#syncClient.peekPull(ctx, {
          spaceId,
          feedNamespace,
          limit: this.#messageBlocksLimit
        }).pipe(Effect.catchAll((cause) => Effect.gen(this, function* () {
          this.#logSyncFailure("peekPull", {
            spaceId,
            feedNamespace,
            cause
          });
          return {
            blocksToPull: 0
          };
        })));
        return {
          namespace: feedNamespace,
          blocksToPull: String(blocksToPull),
          blocksToPush: String(blocksToPush),
          totalBlocks: String(totalBlocks)
        };
      }), {
        concurrency: "unbounded"
      });
      return {
        namespaces: namespaceStates
      };
    })));
  }
  /**
  * Performs queue sync and blocks until there are no pending sync batches.
  */
  async syncBlocking(ctx, { spaceId, subspaceTag, shouldPush = true, shouldPull = true }) {
    invariant21(SpaceId3.isValid(spaceId), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 268, S: this, A: ["SpaceId.isValid(spaceId)", ""] });
    invariant21(FeedProtocol3.isWellKnownNamespace(subspaceTag), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 269, S: this, A: ["FeedProtocol.isWellKnownNamespace(subspaceTag)", ""] });
    if (!shouldPush && !shouldPull) {
      return;
    }
    await this.#runSerialized(() => RuntimeProvider.runPromise(this.#runtime)(Effect.gen(this, function* () {
      let done = false;
      let iterations = 0;
      while (!done) {
        done = true;
        if (shouldPull) {
          const pullResult = yield* this.#syncClient.pull(ctx, {
            spaceId,
            feedNamespace: subspaceTag,
            limit: this.#messageBlocksLimit
          });
          done &&= pullResult.done;
        }
        if (shouldPush) {
          const pushResult = yield* this.#syncClient.push(ctx, {
            spaceId,
            feedNamespace: subspaceTag,
            limit: this.#messageBlocksLimit
          });
          done &&= pushResult.done;
        }
        iterations++;
        if (iterations > MAX_BLOCKING_SYNC_ITERATIONS) {
          throw new Error("Blocking sync exceeded max iterations.");
        }
      }
    })));
  }
  async #runSerialized(run) {
    const env = {
      stack: [],
      error: void 0,
      hasError: false
    };
    try {
      const _guard = _ts_add_disposable_resource4(env, await this.#feedStoreMutex.acquire("feed-sync"), false);
      return run();
    } catch (e) {
      env.error = e;
      env.hasError = true;
    } finally {
      _ts_dispose_resources4(env);
    }
  }
  #schedulePushRetry({ hadFailure, needsMore }) {
    if (!needsMore) {
      this.#pushFailureBackoffMs = DEFAULT_PUSH_FAILURE_BACKOFF_MS;
      return;
    }
    if (hadFailure) {
      const delayMs = this.#pushFailureBackoffMs;
      this.#pushFailureBackoffMs = Math.min(this.#pushFailureBackoffMs * 2, MAX_PUSH_FAILURE_BACKOFF_MS);
      log20.info("feed sync push retry scheduled with backoff", {
        delayMs
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 325, S: this });
      scheduleTask8(this._ctx, () => this.#pushTask.schedule(), delayMs);
      return;
    }
    this.#pushFailureBackoffMs = DEFAULT_PUSH_FAILURE_BACKOFF_MS;
    this.#pushTask.schedule();
  }
  #resetSpacesToPoll() {
    this.#spacesToPoll.clear();
    this.#getSpaceIds().forEach((spaceId) => {
      this.#spacesToPoll.add(spaceId);
    });
    this.#lastFullPoll = Date.now();
  }
  #sendMessage(ctx, message) {
    return Effect.gen(this, function* () {
      const encoded = encoder.encode(message);
      const serviceId = this.#getTargetServiceId(message);
      const rpcTag = "blocks" in message ? "AppendRequest" : "QueryRequest";
      log20("feed sync edge rpc outgoing", {
        tag: rpcTag,
        serviceId,
        payloadByteLength: encoded.byteLength,
        spaceId: message.spaceId,
        feedNamespace: message.feedNamespace,
        requestId: message.requestId
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 346, S: this });
      yield* Effect.tryPromise(async () => this.#edgeClient.send(ctx, createBuf(MessageSchema, {
        source: {
          identityDid: this.#edgeClient.identityDid,
          peerKey: this.#edgeClient.peerKey
        },
        serviceId,
        payload: {
          value: bufferToArray2(encoded)
        }
      }))).pipe(Effect.tapError((cause) => Effect.sync(() => log20("feed sync edge send failed", {
        serviceId,
        tag: rpcTag,
        cause: cause instanceof Error ? cause.message : String(cause)
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 363, S: this }))));
    });
  }
  #logSyncFailure(operation, { spaceId, feedNamespace, cause }) {
    log20("feed sync operation failed", {
      operation,
      spaceId,
      feedNamespace,
      cause: cause instanceof Error ? cause.message : String(cause),
      errorTag: cause instanceof Error ? cause.name : void 0
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file25, L: 371, S: this });
  }
  #getTargetServiceId(message) {
    return FeedProtocol3.encodeServiceId(message.feedNamespace, message.spaceId);
  }
  #pollTask = new AsyncTask(async () => Effect.gen(this, function* () {
    yield* Effect.forEach(this.#spacesToPoll, (spaceId) => Effect.gen(this, function* () {
      let doneForAllNamespaces = true;
      for (const feedNamespace of this.#syncNamespaces) {
        const { done } = yield* this.#syncClient.pull(this._ctx, {
          spaceId,
          feedNamespace,
          limit: this.#messageBlocksLimit
        }).pipe(Effect.catchAll((cause) => Effect.gen(this, function* () {
          this.#logSyncFailure("pull", {
            spaceId,
            feedNamespace,
            cause
          });
          return {
            done: false
          };
        })));
        if (!done) {
          doneForAllNamespaces = false;
        }
      }
      if (doneForAllNamespaces) {
        this.#spacesToPoll.delete(spaceId);
      }
    }), {
      concurrency: this.#syncConcurrency
    });
    if (this.#lastFullPoll == null || Date.now() - this.#lastFullPoll > this.#pollingInterval) {
      this.#resetSpacesToPoll();
      this.#pollTask.schedule();
    } else if (this.#spacesToPoll.size > 0) {
      this.#pollTask.schedule();
    } else {
      this.#resetSpacesToPoll();
      scheduleTask8(this._ctx, () => this.#pollTask.schedule(), Math.max(this.#pollingInterval - (Date.now() - (this.#lastFullPoll ?? 0)), 0));
    }
  }).pipe((effect) => this.#runSerialized(() => RuntimeProvider.runPromise(this.#runtime)(effect))));
  #pushTask = new AsyncTask(async () => Effect.gen(this, function* () {
    yield* Effect.forEach(this.#getSpaceIds(), (spaceId) => Effect.gen(this, function* () {
      let needsMorePush = false;
      let hadPushFailure = false;
      for (const feedNamespace of this.#syncNamespaces) {
        const { done } = yield* this.#syncClient.push(this._ctx, {
          spaceId,
          feedNamespace,
          limit: this.#messageBlocksLimit
        }).pipe(Effect.tap(() => Effect.sync(() => {
          this.#pushFailureBackoffMs = DEFAULT_PUSH_FAILURE_BACKOFF_MS;
        })), Effect.catchAll((cause) => Effect.gen(this, function* () {
          this.#logSyncFailure("push", {
            spaceId,
            feedNamespace,
            cause
          });
          hadPushFailure = true;
          return {
            done: false
          };
        })));
        if (!done) {
          needsMorePush = true;
        }
      }
      this.#schedulePushRetry({
        hadFailure: hadPushFailure,
        needsMore: needsMorePush
      });
    }), {
      concurrency: this.#syncConcurrency
    });
  }).pipe((effect) => this.#runSerialized(() => RuntimeProvider.runPromise(this.#runtime)(effect))));
};

// src/packlets/agents/edge-agent-manager.ts
import { DeferredTask as DeferredTask2, Event as Event9, scheduleTask as scheduleTask9, synchronized as synchronized3 } from "@dxos/async";
import { Resource as Resource8 } from "@dxos/context";
import { invariant as invariant22 } from "@dxos/invariant";
import { PublicKey as PublicKey11 } from "@dxos/keys";
import { log as log21 } from "@dxos/log";
import { EdgeAgentStatus, EdgeCallFailedError as EdgeCallFailedError3 } from "@dxos/protocols";
import { SpaceState as SpaceState4 } from "@dxos/protocols/proto/dxos/client/services";
import { EdgeReplicationSetting as EdgeReplicationSetting2 } from "@dxos/protocols/proto/dxos/echo/metadata";
var __dxlog_file26 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/agents/edge-agent-manager.ts";
function _ts_decorate10(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var AGENT_STATUS_QUERY_RETRY_INTERVAL = 5e3;
var AGENT_STATUS_QUERY_RETRY_JITTER = 1e3;
var AGENT_FEED_ADDED_CHECK_INTERVAL_MS = 3e3;
var EdgeAgentManager = class extends Resource8 {
  _edgeFeatures;
  _edgeHttpClient;
  _dataSpaceManager;
  _identity;
  agentStatusChanged = new Event9();
  _agentDeviceKey;
  _agentStatus;
  _lastKnownDeviceCount = 0;
  _fetchAgentStatusTask;
  constructor(_edgeFeatures, _edgeHttpClient, _dataSpaceManager, _identity) {
    super(), this._edgeFeatures = _edgeFeatures, this._edgeHttpClient = _edgeHttpClient, this._dataSpaceManager = _dataSpaceManager, this._identity = _identity;
  }
  get agentStatus() {
    return this._agentStatus;
  }
  get agentExists() {
    return this._agentStatus && this._agentStatus !== EdgeAgentStatus.NOT_FOUND;
  }
  async createAgent(ctx) {
    invariant22(this.isOpen, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 41, S: this, A: ["this.isOpen", ""] });
    invariant22(this._edgeHttpClient, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 42, S: this, A: ["this._edgeHttpClient", ""] });
    invariant22(this._edgeFeatures?.agents, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 43, S: this, A: ["this._edgeFeatures?.agents", ""] });
    const response = await this._edgeHttpClient.createAgent(ctx, {
      identityDid: this._identity.did,
      haloSpaceId: this._identity.haloSpaceId,
      haloSpaceKey: this._identity.haloSpaceKey.toHex()
    });
    const deviceKey = PublicKey11.fromHex(response.deviceKey);
    if (await this._identity.authorizedDeviceKeys.has(deviceKey)) {
      log21.info("agent was already added to HALO, ignoring response", {
        response
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 51, S: this });
      this._updateStatus(EdgeAgentStatus.ACTIVE, deviceKey);
      return;
    }
    await this._identity.admitDevice({
      deviceKey,
      controlFeedKey: PublicKey11.fromHex(response.feedKey),
      // TODO: agents don't have data feed, should be removed
      dataFeedKey: PublicKey11.random()
    });
    log21("agent created", response, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 63, S: this });
    this._updateStatus(EdgeAgentStatus.ACTIVE, deviceKey);
  }
  async _open() {
    const isEnabled = this._edgeHttpClient && this._edgeFeatures?.agents;
    log21("edge agent manager open", {
      isEnabled
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 68, S: this });
    if (!isEnabled) {
      return;
    }
    this._lastKnownDeviceCount = this._identity.authorizedDeviceKeys.size;
    this._fetchAgentStatusTask = new DeferredTask2(this._ctx, async () => {
      await this._fetchAgentStatus(this._ctx);
    });
    this._fetchAgentStatusTask.schedule();
    this._dataSpaceManager.updated.on(this._ctx, () => {
      if (this._agentDeviceKey) {
        this._ensureAgentIsInSpaces(this._agentDeviceKey);
      }
    });
    this._identity.stateUpdate.on(this._ctx, () => {
      const maybeAgentWasCreated = this._identity.authorizedDeviceKeys.size > this._lastKnownDeviceCount;
      if (this.agentExists || !maybeAgentWasCreated) {
        return;
      }
      this._lastKnownDeviceCount = this._identity.authorizedDeviceKeys.size;
      this._fetchAgentStatusTask?.schedule();
    });
  }
  async _close() {
    this._fetchAgentStatusTask = void 0;
    this._lastKnownDeviceCount = 0;
  }
  async _fetchAgentStatus(ctx) {
    invariant22(this._edgeHttpClient, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 98, S: this, A: ["this._edgeHttpClient", ""] });
    try {
      log21("fetching agent status", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 100, S: this });
      const { agent } = await this._edgeHttpClient.getAgentStatus(ctx, {
        ownerIdentityDid: this._identity.did
      });
      const wasAgentCreatedDuringQuery = this._agentStatus === EdgeAgentStatus.ACTIVE;
      if (!wasAgentCreatedDuringQuery) {
        const deviceKey = agent.deviceKey ? PublicKey11.fromHex(agent.deviceKey) : void 0;
        this._updateStatus(agent.status, deviceKey);
      }
    } catch (err) {
      if (err instanceof EdgeCallFailedError3) {
        if (!err.isRetryable) {
          log21.warn("non retryable error on agent status fetch attempt", {
            err
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 112, S: this });
          return;
        }
      }
      const retryAfterMs = AGENT_STATUS_QUERY_RETRY_INTERVAL + Math.random() * AGENT_STATUS_QUERY_RETRY_JITTER;
      log21.info("agent status fetching failed", {
        err,
        retryAfterMs
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 119, S: this });
      scheduleTask9(this._ctx, () => this._fetchAgentStatusTask?.schedule(), retryAfterMs);
    }
  }
  /**
  * We don't want notarization plugin to always actively poll edge looking for credentials to notarize,
  * because most of the time we'll be getting an empty response.
  * Instead, we stay in active polling mode while there are spaces where we don't see our agent's feed.
  */
  _ensureAgentIsInSpaces(agentDeviceKey) {
    let activePollingEnabled = false;
    for (const space of this._dataSpaceManager.spaces.values()) {
      if (space.getEdgeReplicationSetting() === EdgeReplicationSetting2.DISABLED) {
        space.notarizationPlugin.setActiveEdgePollingEnabled(false);
        continue;
      }
      if ([
        SpaceState4.SPACE_INACTIVE,
        SpaceState4.SPACE_CLOSED
      ].includes(space.state)) {
        space.notarizationPlugin.setActiveEdgePollingEnabled(false);
        continue;
      }
      const agentFeedNeedsNotarization = ![
        ...space.inner.spaceState.feeds.values()
      ].some((feed) => feed.assertion.deviceKey.equals(agentDeviceKey));
      space.notarizationPlugin.setActiveEdgePollingEnabled(agentFeedNeedsNotarization);
      activePollingEnabled = activePollingEnabled || agentFeedNeedsNotarization;
      log21.verbose("set active edge polling", {
        enabled: agentFeedNeedsNotarization,
        spaceId: space.id
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 149, S: this });
    }
    if (activePollingEnabled) {
      scheduleTask9(this._ctx, () => this._ensureAgentIsInSpaces(agentDeviceKey), AGENT_FEED_ADDED_CHECK_INTERVAL_MS);
    }
  }
  _updateStatus(status, deviceKey) {
    this._agentStatus = status;
    this._agentDeviceKey = deviceKey;
    this.agentStatusChanged.emit(status);
    if (deviceKey) {
      this._ensureAgentIsInSpaces(deviceKey);
    }
    log21.verbose("agent status update", {
      status
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file26, L: 166, S: this });
  }
};
_ts_decorate10([
  synchronized3
], EdgeAgentManager.prototype, "createAgent", null);

// src/packlets/agents/edge-agent-service.ts
import { Stream as Stream10 } from "@dxos/codec-protobuf/stream";
import { Context as Context11 } from "@dxos/context";
import { EdgeAgentStatus as EdgeAgentStatus2 } from "@dxos/protocols";
import { EdgeStatus as EdgeStatus2, QueryAgentStatusResponse } from "@dxos/protocols/proto/dxos/client/services";
var __dxlog_file27 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/agents/edge-agent-service.ts";
var EdgeAgentServiceImpl = class {
  _agentManagerProvider;
  _edgeConnection;
  constructor(_agentManagerProvider, _edgeConnection) {
    this._agentManagerProvider = _agentManagerProvider;
    this._edgeConnection = _edgeConnection;
  }
  // TODO(mykola): Reconcile with NetworkService.queryStatus.
  queryEdgeStatus() {
    return new Stream10(({ ctx, next }) => {
      const update = () => {
        next({
          status: this._edgeConnection?.status ?? {
            state: EdgeStatus2.ConnectionState.NOT_CONNECTED,
            rtt: 0,
            uptime: 0,
            rateBytesUp: 0,
            rateBytesDown: 0,
            messagesSent: 0,
            messagesReceived: 0
          }
        });
      };
      this._edgeConnection?.statusChanged.on(ctx, update);
      update();
    });
  }
  async createAgent(_request, options) {
    return (await this._agentManagerProvider()).createAgent(options?.ctx ?? Context11.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file27, L: 37 }));
  }
  queryAgentStatus() {
    return new Stream10(({ ctx, next }) => {
      next({
        status: QueryAgentStatusResponse.AgentStatus.UNKNOWN
      });
      void this._agentManagerProvider().then((agentManager) => {
        next({
          status: mapStatus(agentManager.agentStatus)
        });
        agentManager.agentStatusChanged.on(ctx, (newStatus) => {
          next({
            status: mapStatus(newStatus)
          });
        });
      });
    });
  }
};
var mapStatus = (agentStatus) => {
  switch (agentStatus) {
    case EdgeAgentStatus2.ACTIVE:
      return QueryAgentStatusResponse.AgentStatus.ACTIVE;
    case EdgeAgentStatus2.INACTIVE:
      return QueryAgentStatusResponse.AgentStatus.INACTIVE;
    case EdgeAgentStatus2.NOT_FOUND:
      return QueryAgentStatusResponse.AgentStatus.NOT_FOUND;
    case void 0:
      return QueryAgentStatusResponse.AgentStatus.UNKNOWN;
  }
};

// src/packlets/services/service-context.ts
import * as Effect3 from "effect/Effect";
import { Mutex as Mutex5, Trigger as Trigger6 } from "@dxos/async";
import { Resource as Resource9 } from "@dxos/context";
import { getCredentialAssertion as getCredentialAssertion5 } from "@dxos/credentials";
import { failUndefined as failUndefined2, warnAfterTimeout as warnAfterTimeout2 } from "@dxos/debug";
import { EchoEdgeReplicator, EchoEdgeSubductionReplicator, EchoHost, MeshEchoReplicator, SpaceManager, SqliteMetadataStore, runSqliteHealthCheck, valueEncoding } from "@dxos/echo-host";
import { createChainEdgeIdentity, createEphemeralEdgeIdentity } from "@dxos/edge-client";
import { RuntimeProvider as RuntimeProvider3 } from "@dxos/effect";
import { FeedFactory, FeedStore } from "@dxos/feed-store";
import { invariant as invariant24 } from "@dxos/invariant";
import { SqliteKeyring } from "@dxos/keyring";
import { log as log24 } from "@dxos/log";
import { InvalidStorageVersionError, STORAGE_VERSION as STORAGE_VERSION2 } from "@dxos/protocols";
import { FeedProtocol as FeedProtocol4 } from "@dxos/protocols";
import { Invitation as Invitation10 } from "@dxos/protocols/proto/dxos/client/services";
import { SqliteBlobStore } from "@dxos/teleport-extension-object-sync";
import { trace as Trace2 } from "@dxos/tracing";
import { safeInstanceof } from "@dxos/util";

// src/packlets/identity/identity-recovery-manager.ts
import { generateSeedPhrase, keyPairFromSeedPhrase } from "@dxos/credentials";
import { sign as sign3 } from "@dxos/crypto";
import { invariant as invariant23 } from "@dxos/invariant";
import { PublicKey as PublicKey12 } from "@dxos/keys";
import { log as log22 } from "@dxos/log";
import { EdgeAuthChallengeError as EdgeAuthChallengeError2 } from "@dxos/protocols";
import { schema as schema6 } from "@dxos/protocols/proto";
import { Timeframe as Timeframe4 } from "@dxos/timeframe";
var __dxlog_file28 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/identity/identity-recovery-manager.ts";
var EdgeIdentityRecoveryManager = class {
  _keyring;
  _edgeClient;
  _identityProvider;
  _acceptRecoveredIdentity;
  constructor(_keyring, _edgeClient, _identityProvider, _acceptRecoveredIdentity) {
    this._keyring = _keyring;
    this._edgeClient = _edgeClient;
    this._identityProvider = _identityProvider;
    this._acceptRecoveredIdentity = _acceptRecoveredIdentity;
  }
  async createRecoveryCredential({ data }) {
    const identity = this._identityProvider();
    invariant23(identity, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file28, L: 25, S: this, A: ["identity", ""] });
    let recoveryKey;
    let lookupKey;
    let algorithm;
    let recoveryCode;
    if (data) {
      recoveryKey = data.recoveryKey;
      lookupKey = data.lookupKey;
      algorithm = data.algorithm;
    } else {
      recoveryCode = generateSeedPhrase();
      const keypair = keyPairFromSeedPhrase(recoveryCode);
      recoveryKey = PublicKey12.from(keypair.publicKey);
      lookupKey = PublicKey12.from(keypair.publicKey);
      algorithm = "ED25519";
    }
    const identityKey = identity.identityKey;
    const credential = await identity.getIdentityCredentialSigner().createCredential({
      subject: identityKey,
      assertion: {
        "@type": "dxos.halo.credentials.IdentityRecovery",
        recoveryKey,
        identityKey,
        algorithm,
        lookupKey
      }
    });
    const receipt = await identity.controlPipeline.writer.write({
      credential: {
        credential
      }
    });
    await identity.controlPipeline.state.waitUntilTimeframe(new Timeframe4([
      [
        receipt.feedKey,
        receipt.seq
      ]
    ]));
    return {
      recoveryCode
    };
  }
  async requestRecoveryChallenge(ctx) {
    invariant23(this._edgeClient, "Not connected to EDGE.", { "~LogMeta": "~LogMeta", F: __dxlog_file28, L: 68, S: this, A: ["this._edgeClient", "'Not connected to EDGE.'"] });
    const deviceKey = await this._keyring.createKey();
    const controlFeedKey = await this._keyring.createKey();
    const request = {
      deviceKey: deviceKey.toHex(),
      controlFeedKey: controlFeedKey.toHex()
    };
    try {
      await this._edgeClient.recoverIdentity(ctx, request);
      throw new Error("No challenge received.");
    } catch (error) {
      if (!(error instanceof EdgeAuthChallengeError2)) {
        throw error;
      }
      return {
        deviceKey,
        controlFeedKey,
        challenge: error.challenge
      };
    }
  }
  async recoverIdentityWithExternalSignature(ctx, { lookupKey, deviceKey, controlFeedKey, signature, clientDataJson, authenticatorData }) {
    invariant23(this._edgeClient, "Not connected to EDGE.", { "~LogMeta": "~LogMeta", F: __dxlog_file28, L: 90, S: this, A: ["this._edgeClient", "'Not connected to EDGE.'"] });
    const request = {
      lookupKey: lookupKey.toHex(),
      deviceKey: deviceKey.toHex(),
      controlFeedKey: controlFeedKey.toHex(),
      signature: clientDataJson && authenticatorData ? {
        signature: Buffer.from(signature).toString("base64"),
        clientDataJson: Buffer.from(clientDataJson).toString("base64"),
        authenticatorData: Buffer.from(authenticatorData).toString("base64")
      } : Buffer.from(signature).toString("base64")
    };
    const response = await this._edgeClient.recoverIdentity(ctx, request);
    await this._acceptRecoveredIdentity({
      authorizedDeviceCredential: decodeCredential(response.deviceAuthCredential),
      haloGenesisFeedKey: PublicKey12.fromHex(response.genesisFeedKey),
      haloSpaceKey: PublicKey12.fromHex(response.haloSpaceKey),
      identityKey: PublicKey12.fromHex(response.identityKey),
      deviceKey,
      controlFeedKey,
      dataFeedKey: await this._keyring.createKey()
    });
  }
  /**
  * Recover an identity using an opaque one-time token. Accepts either an email magic-link
  * `token` (validated by hub-service) or an OAuth `recoveryProof` (redeemed by kms-service).
  * The two fields are routed to different backends by db-service and must not be conflated.
  */
  async recoverIdentityWithToken(ctx, fields) {
    invariant23(this._edgeClient, "Not connected to EDGE.", { "~LogMeta": "~LogMeta", F: __dxlog_file28, L: 117, S: this, A: ["this._edgeClient", "'Not connected to EDGE.'"] });
    const deviceKey = await this._keyring.createKey();
    const controlFeedKey = await this._keyring.createKey();
    const request = {
      deviceKey: deviceKey.toHex(),
      controlFeedKey: controlFeedKey.toHex(),
      ...fields
    };
    const response = await this._edgeClient.recoverIdentity(ctx, request);
    await this._acceptRecoveredIdentity({
      authorizedDeviceCredential: decodeCredential(response.deviceAuthCredential),
      haloGenesisFeedKey: PublicKey12.fromHex(response.genesisFeedKey),
      haloSpaceKey: PublicKey12.fromHex(response.haloSpaceKey),
      identityKey: PublicKey12.fromHex(response.identityKey),
      deviceKey,
      controlFeedKey,
      dataFeedKey: await this._keyring.createKey()
    });
  }
  async recoverIdentity(ctx, { recoveryCode }) {
    invariant23(this._edgeClient, "Not connected to EDGE.", { "~LogMeta": "~LogMeta", F: __dxlog_file28, L: 137, S: this, A: ["this._edgeClient", "'Not connected to EDGE.'"] });
    const recoveryKeypair = keyPairFromSeedPhrase(recoveryCode);
    const recoveryKey = PublicKey12.from(recoveryKeypair.publicKey);
    const deviceKey = await this._keyring.createKey();
    const controlFeedKey = await this._keyring.createKey();
    const request = {
      lookupKey: recoveryKey.toHex(),
      deviceKey: deviceKey.toHex(),
      controlFeedKey: controlFeedKey.toHex()
    };
    let response;
    try {
      response = await this._edgeClient.recoverIdentity(ctx, request);
    } catch (error) {
      if (!(error instanceof EdgeAuthChallengeError2)) {
        throw error;
      }
      const signature = sign3(Buffer.from(error.challenge, "base64"), recoveryKeypair.secretKey);
      response = await this._edgeClient.recoverIdentity(ctx, {
        ...request,
        signature: Buffer.from(signature).toString("base64")
      });
    }
    log22.info("recovering identity", response, { "~LogMeta": "~LogMeta", F: __dxlog_file28, L: 160, S: this });
    await this._acceptRecoveredIdentity({
      authorizedDeviceCredential: decodeCredential(response.deviceAuthCredential),
      haloGenesisFeedKey: PublicKey12.fromHex(response.genesisFeedKey),
      haloSpaceKey: PublicKey12.fromHex(response.haloSpaceKey),
      identityKey: PublicKey12.fromHex(response.identityKey),
      deviceKey,
      controlFeedKey,
      dataFeedKey: await this._keyring.createKey()
    });
  }
};
var decodeCredential = (credentialBase64) => {
  const credentialBytes = Buffer.from(credentialBase64, "base64");
  const codec = schema6.getCodecForType("dxos.halo.credentials.Credential");
  return codec.decode(credentialBytes);
};

// src/packlets/services/sqlite-storage.ts
import * as SqlClient from "@effect/sql/SqlClient";
import * as Effect2 from "effect/Effect";
import { RuntimeProvider as RuntimeProvider2 } from "@dxos/effect";
import { log as log23 } from "@dxos/log";
import { Directory, StorageType, wrapFile } from "@dxos/random-access-storage";
var __dxlog_file29 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/services/sqlite-storage.ts";
var BaseEventEmitter = class {
  #events = /* @__PURE__ */ Object.create(null);
  on(event, listener) {
    (this.#events[event] ??= []).push(listener);
    return this;
  }
  off(event, listener) {
    const list = this.#events[event];
    if (list) {
      const idx = list.indexOf(listener);
      if (idx !== -1) {
        list.splice(idx, 1);
      }
    }
    return this;
  }
  emit(event, ...args) {
    const list = this.#events[event];
    if (!list?.length) {
      return false;
    }
    for (const fn2 of [
      ...list
    ]) {
      fn2(...args);
    }
    return true;
  }
  addListener = this.on;
  removeListener = this.off;
  once(event, listener) {
    const wrapper = (...args) => {
      this.off(event, wrapper);
      listener(...args);
    };
    return this.on(event, wrapper);
  }
  removeAllListeners(event) {
    if (event) {
      delete this.#events[event];
    } else {
      for (const key of Object.keys(this.#events)) {
        delete this.#events[key];
      }
    }
    return this;
  }
  listeners(event) {
    return this.#events[event] ?? [];
  }
  rawListeners(event) {
    return this.#events[event] ?? [];
  }
  listenerCount(event) {
    return this.#events[event]?.length ?? 0;
  }
  prependListener(event, listener) {
    (this.#events[event] ??= []).unshift(listener);
    return this;
  }
  prependOnceListener(event, listener) {
    const wrapper = (...args) => {
      this.off(event, wrapper);
      listener(...args);
    };
    return this.prependListener(event, wrapper);
  }
  eventNames() {
    return Object.keys(this.#events);
  }
  getMaxListeners() {
    return 0;
  }
  setMaxListeners() {
    return this;
  }
};
var SqliteRandomAccessFile = class extends BaseEventEmitter {
  filePath;
  runtime;
  opened = false;
  suspended = false;
  writing = false;
  readable = true;
  writable = true;
  deletable = true;
  truncatable = false;
  // Setting statable=false prevents hypercore's statAndReadAll() from computing a negative
  // read-size (stat.size - offset) for new empty files, which causes an infinite async loop.
  statable = false;
  #closed = false;
  #unlinked = false;
  #destroyed = false;
  get closed() {
    return this.#closed;
  }
  get unlinked() {
    return this.#unlinked;
  }
  get destroyed() {
    return this.#destroyed;
  }
  // Filesystem-like metadata for compatibility with wrapFile.
  directory;
  filename;
  #buffer = Buffer.alloc(0);
  #loaded = false;
  #loading = null;
  constructor(filePath, runtime) {
    super(), this.filePath = filePath, this.runtime = runtime;
    const parts = filePath.split("/");
    this.filename = parts.pop() ?? "";
    this.directory = parts.join("/");
  }
  _ensureLoaded() {
    if (this.#loaded) {
      return Promise.resolve();
    }
    if (this.#closed) {
      return Promise.resolve();
    }
    if (!this.#loading) {
      this.#loading = this._loadFromDb();
    }
    return this.#loading;
  }
  async _loadFromDb() {
    const filePath = this.filePath;
    try {
      const rows = await RuntimeProvider2.runPromise(this.runtime)(Effect2.gen(function* () {
        const sql = yield* SqlClient.SqlClient;
        return yield* sql`SELECT data FROM hypercore_files WHERE path = ${filePath}`;
      }));
      this.#buffer = rows.length > 0 ? Buffer.from(rows[0].data) : Buffer.alloc(0);
    } catch (err) {
      if (!this.#closed) {
        throw err;
      }
    }
    this.#loaded = true;
  }
  async _saveToDb() {
    const filePath = this.filePath;
    const data = this.#buffer;
    await RuntimeProvider2.runPromise(this.runtime)(Effect2.gen(function* () {
      const sql = yield* SqlClient.SqlClient;
      yield* sql`INSERT OR REPLACE INTO hypercore_files (path, data) VALUES (${filePath}, ${data})`;
    }));
  }
  write(offset, data, cb) {
    this._ensureLoaded().then(() => {
      const end = offset + data.length;
      if (end > this.#buffer.length) {
        const enlarged = Buffer.alloc(end);
        this.#buffer.copy(enlarged);
        this.#buffer = enlarged;
      }
      data.copy(this.#buffer, offset);
      return this._saveToDb();
    }).then(() => cb(null)).catch((err) => cb(err));
  }
  read(offset, size, cb) {
    this._ensureLoaded().then(() => {
      if (size === 0) {
        cb(null, Buffer.alloc(0));
        return;
      }
      if (offset + size > this.#buffer.length) {
        cb(new Error(`Could not satisfy length ${size} at offset ${offset} (file size: ${this.#buffer.length})`));
        return;
      }
      cb(null, Buffer.from(this.#buffer.subarray(offset, offset + size)));
    }).catch((err) => cb(err));
  }
  del(offset, size, cb) {
    this._ensureLoaded().then(() => {
      const end = Math.min(offset + size, this.#buffer.length);
      if (offset < end) {
        this.#buffer.fill(0, offset, end);
      }
      return this._saveToDb();
    }).then(() => cb(null)).catch((err) => cb(err));
  }
  stat(cb) {
    this._ensureLoaded().then(() => {
      cb(null, {
        size: this.#buffer.length
      });
    }).catch((err) => cb(err));
  }
  close(cb) {
    this.#closed = true;
    (this.#loading ?? Promise.resolve()).then(() => cb(null), () => cb(null));
  }
  destroy(cb) {
    const filePath = this.filePath;
    RuntimeProvider2.runPromise(this.runtime)(Effect2.gen(function* () {
      const sql = yield* SqlClient.SqlClient;
      yield* sql`DELETE FROM hypercore_files WHERE path = ${filePath}`;
    })).then(() => {
      this.#buffer = Buffer.alloc(0);
      this.#destroyed = true;
      this.#unlinked = true;
      this.#closed = true;
      cb(null);
    }).catch((err) => cb(err));
  }
};
var SqliteStorage = class {
  path;
  type = StorageType.NODE;
  #runtime;
  #files = /* @__PURE__ */ new Map();
  #nativeFiles = /* @__PURE__ */ new Map();
  #closed = false;
  constructor({ runtime }, path = "/sqlite-feeds") {
    this.#runtime = runtime;
    this.path = path;
  }
  migrate = Effect2.fn("SqliteStorage.migrate")(() => Effect2.gen(function* () {
    const sql = yield* SqlClient.SqlClient;
    yield* sql`CREATE TABLE IF NOT EXISTS hypercore_files (
          path TEXT PRIMARY KEY,
          data BLOB NOT NULL DEFAULT x''
        )`;
    log23("hypercore_files table ready", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file29, L: 251, S: this });
  }).pipe(Effect2.withSpan("SqliteStorage.migrate")))();
  get size() {
    return this.#files.size;
  }
  createDirectory(sub = "") {
    const dirPath = sub ? `${this.path}/${sub}` : this.path;
    const runtime = this.#runtime;
    const files = this.#files;
    const nativeFiles = this.#nativeFiles;
    const getOrCreateFile = (path, filename) => {
      const fullPath = `${path}/${filename}`;
      const existingNative = nativeFiles.get(fullPath);
      if (existingNative && !existingNative.destroyed) {
        return files.get(fullPath);
      }
      const native = new SqliteRandomAccessFile(fullPath, runtime);
      const file = wrapFile(native, StorageType.NODE);
      files.set(fullPath, file);
      nativeFiles.set(fullPath, native);
      return file;
    };
    const list = async (path) => {
      const prefix = path.endsWith("/") ? path : path + "/";
      const rows = await RuntimeProvider2.runPromise(runtime)(Effect2.gen(function* () {
        const sql = yield* SqlClient.SqlClient;
        return yield* sql`SELECT path FROM hypercore_files WHERE path = ${path} OR path LIKE ${prefix + "%"}`;
      }));
      return rows.map((row) => row.path.replace(path + "/", "").replace(path, ""));
    };
    const remove = async () => {
      const prefix = dirPath.endsWith("/") ? dirPath : dirPath + "/";
      await RuntimeProvider2.runPromise(runtime)(Effect2.gen(function* () {
        const sql = yield* SqlClient.SqlClient;
        yield* sql`DELETE FROM hypercore_files WHERE path = ${dirPath} OR path LIKE ${prefix + "%"}`;
      }));
      for (const [filePath] of files) {
        if (filePath === dirPath || filePath.startsWith(prefix)) {
          files.delete(filePath);
        }
      }
    };
    return new Directory({
      type: StorageType.NODE,
      path: dirPath,
      list,
      getOrCreateFile,
      remove
    });
  }
  async reset() {
    log23("SqliteStorage: clearing all hypercore files", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file29, L: 303, S: this });
    await RuntimeProvider2.runPromise(this.#runtime)(Effect2.gen(function* () {
      const sql = yield* SqlClient.SqlClient;
      yield* sql`DELETE FROM hypercore_files`;
    }));
    this.#files.clear();
  }
  async close() {
    if (this.#closed) {
      return;
    }
    this.#closed = true;
    const natives = [
      ...this.#nativeFiles.values()
    ];
    await Promise.all(natives.map((native) => new Promise((resolve) => native.close(() => resolve()))));
    this.#files.clear();
    this.#nativeFiles.clear();
  }
};

// src/packlets/services/service-context.ts
var __dxlog_file30 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/services/service-context.ts";
function _ts_decorate11(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_add_disposable_resource5(env, value, async) {
  if (value !== null && value !== void 0) {
    if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
    var dispose, inner;
    if (async) {
      if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
      dispose = value[Symbol.asyncDispose];
    }
    if (dispose === void 0) {
      if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
      dispose = value[Symbol.dispose];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    env.stack.push({
      value,
      dispose,
      async
    });
  } else if (async) {
    env.stack.push({
      async: true
    });
  }
  return value;
}
function _ts_dispose_resources5(env) {
  var _SuppressedError = typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
  };
  return (_ts_dispose_resources5 = function _ts_dispose_resources6(env2) {
    function fail(e) {
      env2.error = env2.hasError ? new _SuppressedError(e, env2.error, "An error was suppressed during disposal.") : e;
      env2.hasError = true;
    }
    var r, s = 0;
    function next() {
      while (r = env2.stack.pop()) {
        try {
          if (!r.async && s === 1) return s = 0, env2.stack.push(r), Promise.resolve().then(next);
          if (r.dispose) {
            var result = r.dispose.call(r.value);
            if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) {
              fail(e);
              return next();
            });
          } else s |= 1;
        } catch (e) {
          fail(e);
        }
      }
      if (s === 1) return env2.hasError ? Promise.reject(env2.error) : Promise.resolve();
      if (env2.hasError) throw env2.error;
    }
    return next();
  })(env);
}
var ServiceContext = class extends Resource9 {
  networkManager;
  signalManager;
  _edgeConnection;
  _edgeHttpClient;
  _runtime;
  _runtimeProps;
  _edgeFeatures;
  _edgeIdentityUpdateMutex = new Mutex5();
  initialized = new Trigger6();
  metadataStore;
  blobStore;
  feedStore;
  keyring;
  spaceManager;
  identityManager;
  recoveryManager;
  invitations;
  invitationsManager;
  echoHost;
  _meshReplicator = void 0;
  _echoEdgeReplicator = void 0;
  _feedSyncer = void 0;
  _feedStorage;
  // Initialized after identity is initialized.
  dataSpaceManager;
  edgeAgentManager;
  _handlerFactories = /* @__PURE__ */ new Map();
  _deviceSpaceSync;
  constructor(networkManager, signalManager, _edgeConnection, _edgeHttpClient, _runtime, _runtimeProps, _edgeFeatures) {
    super(), this.networkManager = networkManager, this.signalManager = signalManager, this._edgeConnection = _edgeConnection, this._edgeHttpClient = _edgeHttpClient, this._runtime = _runtime, this._runtimeProps = _runtimeProps, this._edgeFeatures = _edgeFeatures;
    log24("runtimeProps", this._runtimeProps, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 131, S: this });
    log24("edgeFeatures", this._edgeFeatures, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 132, S: this });
    this.metadataStore = new SqliteMetadataStore({
      runtime: this._runtime
    });
    this.blobStore = new SqliteBlobStore({
      runtime: this._runtime
    });
    this.keyring = new SqliteKeyring({
      runtime: this._runtime
    });
    this._feedStorage = new SqliteStorage({
      runtime: this._runtime
    });
    const feedStorage = this._feedStorage;
    this.feedStore = new FeedStore({
      factory: new FeedFactory({
        root: feedStorage.createDirectory("feeds"),
        signer: this.keyring,
        hypercore: {
          valueEncoding,
          stats: true
        }
      })
    });
    this.spaceManager = new SpaceManager({
      feedStore: this.feedStore,
      networkManager: this.networkManager,
      blobStore: this.blobStore,
      metadataStore: this.metadataStore,
      disableP2pReplication: this._runtimeProps?.disableP2pReplication
    });
    this.identityManager = new IdentityManager({
      metadataStore: this.metadataStore,
      keyring: this.keyring,
      feedStore: this.feedStore,
      spaceManager: this.spaceManager,
      devicePresenceOfflineTimeout: this._runtimeProps?.devicePresenceOfflineTimeout,
      devicePresenceAnnounceInterval: this._runtimeProps?.devicePresenceAnnounceInterval,
      edgeConnection: this._edgeConnection,
      edgeFeatures: this._edgeFeatures
    });
    this.recoveryManager = new EdgeIdentityRecoveryManager(this.keyring, this._edgeHttpClient, () => this.identityManager.identity, this._acceptIdentity.bind(this));
    this.echoHost = new EchoHost({
      peerIdProvider: () => this.identityManager.identity?.deviceKey?.toHex(),
      getSpaceKeyByRootDocumentId: (documentId) => this.spaceManager.findSpaceByRootDocumentId(documentId)?.key,
      runtime: this._runtime,
      useSubduction: this._edgeFeatures?.subductionReplicator,
      syncQueue: async (ctx, request) => {
        return this._feedSyncer?.syncBlocking(ctx, {
          spaceId: request.spaceId,
          subspaceTag: request.subspaceTag,
          shouldPush: request.shouldPush,
          shouldPull: request.shouldPull
        });
      },
      getSyncState: async (ctx, request) => {
        if (!this._feedSyncer) {
          return {
            namespaces: []
          };
        }
        return this._feedSyncer.getSyncState(ctx, request);
      }
    });
    this.invitations = new InvitationsHandler(this.networkManager, this._edgeHttpClient, _runtimeProps?.invitationConnectionDefaultProps);
    this.invitationsManager = new InvitationsManager(this.invitations, (invitation) => this.getInvitationHandler(invitation), this.metadataStore);
    this._handlerFactories.set(Invitation10.Kind.DEVICE, () => new DeviceInvitationProtocol(this.keyring, () => this.identityManager.identity ?? failUndefined2(), this._acceptIdentity.bind(this)));
    if (!this._runtimeProps?.disableP2pReplication) {
      this._meshReplicator = new MeshEchoReplicator();
    }
    if (this._edgeConnection && this._edgeHttpClient) {
      if (this._edgeFeatures?.subductionReplicator) {
        this._echoEdgeReplicator = new EchoEdgeSubductionReplicator({
          edgeConnection: this._edgeConnection,
          edgeHttpClient: this._edgeHttpClient
        });
      } else if (this._edgeFeatures?.echoReplicator) {
        this._echoEdgeReplicator = new EchoEdgeReplicator({
          edgeConnection: this._edgeConnection,
          edgeHttpClient: this._edgeHttpClient
        });
      }
    }
    if (this.echoHost.feedStore && this._edgeConnection) {
      this._feedSyncer = new FeedSyncer({
        runtime: this._runtime,
        feedStore: this.echoHost.feedStore,
        edgeClient: this._edgeConnection,
        peerId: this.identityManager.identity?.deviceKey?.toHex() ?? "",
        getSpaceIds: () => this.echoHost.spaceIds,
        syncNamespaces: [
          FeedProtocol4.WellKnownNamespaces.data,
          FeedProtocol4.WellKnownNamespaces.trace
        ]
      });
    }
  }
  async _open(ctx) {
    await RuntimeProvider3.runPromise(this._runtime)(Effect3.all([
      this.metadataStore.migrate,
      this.blobStore.migrate,
      this.keyring.migrate,
      this._feedStorage.migrate
    ]));
    await this._checkStorageVersion();
    log24("running sqlite health check...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 242, S: this });
    await runSqliteHealthCheck(this._runtime);
    log24("sqlite health check passed", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 244, S: this });
    log24("opening...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 245, S: this });
    log24("opening identityManager...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 246, S: this });
    await this.identityManager.open(ctx);
    log24("identityManager opened", {
      hasIdentity: !!this.identityManager.identity
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 248, S: this });
    log24("setting network identity...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 251, S: this });
    await this._setNetworkIdentity({
      identity: this.identityManager.identity
    });
    log24("network identity set", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 255, S: this });
    log24("opening edge connection...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 256, S: this });
    await this._edgeConnection?.open(ctx);
    log24("edge connection opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 258, S: this });
    log24("opening signal manager...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 259, S: this });
    await this.signalManager.open(ctx);
    log24("signal manager opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 261, S: this });
    log24("opening network manager...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 262, S: this });
    await this.networkManager.open();
    log24("network manager opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 264, S: this });
    log24("opening echo host...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 265, S: this });
    await this.echoHost.open(ctx);
    log24("echo host opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 267, S: this });
    if (this._meshReplicator) {
      log24("adding mesh replicator...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 269, S: this });
      await this.echoHost.addReplicator(ctx, this._meshReplicator);
      log24("mesh replicator added", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 271, S: this });
    }
    if (this._echoEdgeReplicator) {
      log24("adding edge replicator...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 274, S: this });
      await this.echoHost.addReplicator(ctx, this._echoEdgeReplicator);
      log24("edge replicator added", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 276, S: this });
    }
    log24("loading metadata store...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 278, S: this });
    await this.metadataStore.load();
    log24("metadata store loaded", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 280, S: this });
    log24("opening space manager...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 281, S: this });
    await this.spaceManager.open();
    log24("space manager opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 283, S: this });
    if (this.identityManager.identity) {
      log24("joining network...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 285, S: this });
      await this.identityManager.identity.joinNetwork(ctx);
      log24("network joined", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 287, S: this });
      log24("initializing spaces...(calling _initialize)", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 288, S: this });
      await this._initialize(ctx);
      log24("spaces initialized", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 290, S: this });
    } else {
      log24("no identity, skipping network join and space initialization", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 292, S: this });
    }
    log24("opening feed syncer...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 294, S: this });
    await this._feedSyncer?.open(ctx);
    log24("feed syncer opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 296, S: this });
    log24("loading persistent invitations...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 297, S: this });
    const loadedInvitations = await this.invitationsManager.loadPersistentInvitations(ctx);
    log24("loaded persistent invitations", {
      count: loadedInvitations.invitations?.length
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 299, S: this });
    log24("opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 302, S: this });
  }
  async _close(ctx) {
    log24("closing...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 305, S: this });
    await this._feedSyncer?.close();
    if (this._deviceSpaceSync && this.identityManager.identity) {
      await this.identityManager.identity.space.spaceState.removeCredentialProcessor(this._deviceSpaceSync);
    }
    await this.dataSpaceManager?.close(ctx);
    await this.edgeAgentManager?.close();
    await this.identityManager.close(ctx);
    await this.spaceManager.close();
    await this.echoHost.close(ctx);
    await this.networkManager.close(ctx);
    await this.signalManager.close();
    await this._edgeConnection?.close();
    await this.feedStore.close();
    await this.metadataStore.close();
    log24("closed", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 320, S: this });
  }
  async createIdentity(params = {}, ctx) {
    ctx ??= this._ctx;
    const identity = await this.identityManager.createIdentity(params, ctx);
    await this._setNetworkIdentity({
      identity
    });
    await identity.joinNetwork(ctx);
    await this._initialize(ctx);
    return identity;
  }
  getInvitationHandler(invitation) {
    if (this.identityManager.identity == null && invitation.kind === Invitation10.Kind.SPACE) {
      throw new Error("Identity must be created before joining a space.");
    }
    const factory = this._handlerFactories.get(invitation.kind);
    invariant24(factory, `Unknown invitation kind: ${invitation.kind}`, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 337, S: this, A: ["factory", "`Unknown invitation kind: ${invitation.kind}`"] });
    return factory(invitation);
  }
  async broadcastProfileUpdate(profile) {
    if (!profile || !this.dataSpaceManager) {
      return;
    }
    for (const space of this.dataSpaceManager.spaces.values()) {
      await space.updateOwnProfile(profile);
    }
  }
  async _acceptIdentity(params) {
    const { identity, identityRecord } = await this.identityManager.prepareIdentity(params, this._ctx);
    await this._setNetworkIdentity({
      deviceCredential: params.authorizedDeviceCredential,
      identity
    });
    await identity.joinNetwork(this._ctx);
    await this.identityManager.acceptIdentity(identity, identityRecord, params.deviceProfile);
    await this._initialize(this._ctx);
    return identity;
  }
  async _checkStorageVersion() {
    await this.metadataStore.load();
    if (this.metadataStore.version !== STORAGE_VERSION2) {
      throw new InvalidStorageVersionError(STORAGE_VERSION2, this.metadataStore.version);
    }
  }
  // Called when identity is created.
  async _initialize(ctx) {
    log24("_initialize: start", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 368, S: this });
    const identity = this.identityManager.identity ?? failUndefined2();
    const signingContext = {
      credentialSigner: identity.getIdentityCredentialSigner(),
      identityKey: identity.identityKey,
      deviceKey: identity.deviceKey,
      getProfile: () => identity.profileDocument,
      recordCredential: async (credential) => {
        await identity.controlPipeline.writer.write({
          credential: {
            credential
          }
        });
      }
    };
    log24("_initialize: creating DataSpaceManager", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 383, S: this });
    this.dataSpaceManager = new DataSpaceManager({
      spaceManager: this.spaceManager,
      metadataStore: this.metadataStore,
      keyring: this.keyring,
      signingContext,
      feedStore: this.feedStore,
      echoHost: this.echoHost,
      invitationsManager: this.invitationsManager,
      edgeConnection: this._edgeConnection,
      edgeHttpClient: this._edgeHttpClient,
      echoEdgeReplicator: this._echoEdgeReplicator,
      meshReplicator: this._meshReplicator,
      runtimeProps: this._runtimeProps,
      edgeFeatures: this._edgeFeatures
    });
    log24("_initialize: opening DataSpaceManager...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 399, S: this });
    await this.dataSpaceManager.open(ctx);
    log24("_initialize: DataSpaceManager opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 401, S: this });
    this.edgeAgentManager = new EdgeAgentManager(this._edgeFeatures, this._edgeHttpClient, this.dataSpaceManager, identity);
    log24("_initialize: opening EdgeAgentManager...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 403, S: this });
    await this.edgeAgentManager.open(ctx);
    log24("_initialize: EdgeAgentManager opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 405, S: this });
    this._handlerFactories.set(Invitation10.Kind.SPACE, (invitation) => {
      invariant24(this.dataSpaceManager, "dataSpaceManager not initialized yet", { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 407, S: this, A: ["this.dataSpaceManager", "'dataSpaceManager not initialized yet'"] });
      return new SpaceInvitationProtocol(this.dataSpaceManager, signingContext, this.keyring, invitation.spaceKey);
    });
    this.initialized.wake();
    log24("_initialize: initialized.wake() called", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 411, S: this });
    this._deviceSpaceSync = {
      processCredential: async (credential) => {
        const assertion = getCredentialAssertion5(credential);
        if (assertion["@type"] === "dxos.halo.credentials.SpaceDeleted") {
          if (assertion.spaceKey.equals(identity.space.key)) {
            return;
          }
          if (!this.dataSpaceManager) {
            log24("dataSpaceManager not initialized yet, ignoring space deletion", {
              details: assertion
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 422, S: this });
            return;
          }
          try {
            log24("tombstoning space recorded in halo", {
              details: assertion
            }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 428, S: this });
            await this.dataSpaceManager.handleRemoteSpaceDeleted(this._ctx, assertion.spaceKey);
          } catch (err) {
            log24.catch(err, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 433, S: this });
          }
          return;
        }
        if (assertion["@type"] !== "dxos.halo.credentials.SpaceMember") {
          return;
        }
        if (assertion.spaceKey.equals(identity.space.key)) {
          return;
        }
        if (!this.dataSpaceManager) {
          log24("dataSpaceManager not initialized yet, ignoring space admission", {
            details: assertion
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 445, S: this });
          return;
        }
        if (this.dataSpaceManager.isSpaceDeleted(assertion.spaceKey)) {
          log24("space is deleted, ignoring space admission", {
            details: assertion
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 452, S: this });
          return;
        }
        if (this.dataSpaceManager.spaces.has(assertion.spaceKey)) {
          log24("space already exists, ignoring space admission", {
            details: assertion
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 458, S: this });
          return;
        }
        try {
          log24("accepting space recorded in halo", {
            details: assertion
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 464, S: this });
          await this.dataSpaceManager.acceptSpace(this._ctx, {
            spaceKey: assertion.spaceKey,
            genesisFeedKey: assertion.genesisFeedKey,
            tags: assertion.tags
          });
        } catch (err) {
          log24.catch(err, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 473, S: this });
        }
      }
    };
    await identity.space.spaceState.addCredentialProcessor(this._deviceSpaceSync);
  }
  async _setNetworkIdentity(params) {
    const env = {
      stack: [],
      error: void 0,
      hasError: false
    };
    try {
      log24("_setNetworkIdentity: acquiring mutex...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 486, S: this });
      const _ = _ts_add_disposable_resource5(env, await this._edgeIdentityUpdateMutex.acquire(), false);
      log24("_setNetworkIdentity: mutex acquired", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 488, S: this });
      let edgeIdentity;
      const identity = params?.identity;
      if (identity) {
        log24("_setNetworkIdentity: has identity", {
          identity: identity.identityKey.toHex(),
          hasDeviceCredential: !!params?.deviceCredential
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 492, S: this });
        if (params?.deviceCredential) {
          log24("_setNetworkIdentity: creating chain edge identity with device credential...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 497, S: this });
          edgeIdentity = await createChainEdgeIdentity(identity.signer, identity.identityKey, identity.deviceKey, {
            credential: params.deviceCredential
          }, []);
          log24("_setNetworkIdentity: chain edge identity created", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 501, S: this });
        } else {
          log24("_setNetworkIdentity: waiting for identity.ready()...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 503, S: this });
          await warnAfterTimeout2(1e4, "Waiting for identity to be ready for edge connection", async () => {
            await identity.ready();
          });
          log24("_setNetworkIdentity: identity.ready() resolved", {
            hasDeviceCredentialChain: !!identity.deviceCredentialChain
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 508, S: this });
          invariant24(identity.deviceCredentialChain, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 511, S: this, A: ["identity.deviceCredentialChain", ""] });
          log24("_setNetworkIdentity: creating chain edge identity...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 512, S: this });
          edgeIdentity = await createChainEdgeIdentity(identity.signer, identity.identityKey, identity.deviceKey, identity.deviceCredentialChain, []);
          log24("_setNetworkIdentity: chain edge identity created", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 514, S: this });
        }
      } else {
        log24("_setNetworkIdentity: no identity, creating ephemeral edge identity...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 517, S: this });
        edgeIdentity = await createEphemeralEdgeIdentity();
        log24("_setNetworkIdentity: ephemeral edge identity created", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 519, S: this });
      }
      this._edgeConnection?.setIdentity(edgeIdentity);
      this._edgeHttpClient?.setIdentity(edgeIdentity);
      this.networkManager.setPeerInfo({
        identityDid: edgeIdentity.identityDid,
        peerKey: edgeIdentity.peerKey
      });
      log24("_setNetworkIdentity: done", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file30, L: 527, S: this });
    } catch (e) {
      env.error = e;
      env.hasError = true;
    } finally {
      _ts_dispose_resources5(env);
    }
  }
};
_ts_decorate11([
  Trace2.span({
    op: "lifecycle"
  })
], ServiceContext.prototype, "_open", null);
_ts_decorate11([
  Trace2.span()
], ServiceContext.prototype, "_initialize", null);
ServiceContext = _ts_decorate11([
  safeInstanceof("dxos.client-services.ServiceContext"),
  Trace2.resource({
    lifecycle: true
  })
], ServiceContext);

// src/packlets/services/service-registry.ts
var ServiceRegistry = class {
  _serviceBundle;
  _handlers;
  // prettier-ignore
  constructor(_serviceBundle, _handlers = {}) {
    this._serviceBundle = _serviceBundle;
    this._handlers = _handlers;
  }
  get descriptors() {
    return this._serviceBundle;
  }
  get services() {
    return this._handlers;
  }
  setServices(services) {
    this._handlers = services;
  }
  addService(name, service) {
    this._handlers[name] = service;
  }
  removeService(name) {
    delete this._handlers[name];
  }
};

// src/packlets/services/service-host.ts
import * as SqlClient3 from "@effect/sql/SqlClient";
import * as Effect4 from "effect/Effect";
import { Event as Event11, synchronized as synchronized4 } from "@dxos/async";
import { clientServiceBundle } from "@dxos/client-protocol";
import { resolveTelemetryTag } from "@dxos/config";
import { Context as Context13 } from "@dxos/context";
import { EdgeClient, EdgeHttpClient, createStubEdgeIdentity } from "@dxos/edge-client";
import { RuntimeProvider as RuntimeProvider4 } from "@dxos/effect";
import { invariant as invariant26 } from "@dxos/invariant";
import { log as log26 } from "@dxos/log";
import { EdgeSignalManager, WebsocketSignalManager } from "@dxos/messaging";
import { SwarmNetworkManager, createIceProvider, createRtcTransportFactory } from "@dxos/network-manager";
import { SystemStatus } from "@dxos/protocols/proto/dxos/client/services";
import * as SqlExport from "@dxos/sql-sqlite/SqlExport";
import { trace as Trace3 } from "@dxos/tracing";
import { WebsocketRpcClient } from "@dxos/websocket-rpc";

// src/packlets/devices/devices-service.ts
import { SubscriptionList as SubscriptionList3 } from "@dxos/async";
import { Stream as Stream11 } from "@dxos/codec-protobuf/stream";
import { invariant as invariant25 } from "@dxos/invariant";
import { Device as Device2, DeviceKind as DeviceKind2, EdgeStatus as EdgeStatus3 } from "@dxos/protocols/proto/dxos/client/services";
var __dxlog_file31 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/devices/devices-service.ts";
var DevicesServiceImpl = class {
  _identityManager;
  _edgeConnection;
  constructor(_identityManager, _edgeConnection) {
    this._identityManager = _identityManager;
    this._edgeConnection = _edgeConnection;
  }
  async updateDevice(profile) {
    return this._identityManager.updateDeviceProfile(profile);
  }
  queryDevices() {
    return new Stream11(({ next }) => {
      const update = () => {
        const deviceKeys = this._identityManager.identity?.authorizedDeviceKeys;
        if (!deviceKeys) {
          next({
            devices: []
          });
        } else {
          invariant25(this._identityManager.identity?.presence, "presence not present", { "~LogMeta": "~LogMeta", F: __dxlog_file31, L: 27, S: this, A: ["this._identityManager.identity?.presence", "'presence not present'"] });
          const peers = this._identityManager.identity.presence.getPeersOnline();
          next({
            devices: Array.from(deviceKeys.entries()).map(([key, profile]) => {
              const isMe = this._identityManager.identity?.deviceKey.equals(key);
              let presence;
              if (isMe) {
                presence = Device2.PresenceState.ONLINE;
              } else if (profile.os?.toUpperCase() === "EDGE") {
                presence = this._edgeConnection?.status.state === EdgeStatus3.ConnectionState.CONNECTED ? Device2.PresenceState.ONLINE : Device2.PresenceState.OFFLINE;
              } else {
                presence = peers.some((peer) => peer.identityKey.equals(key)) ? Device2.PresenceState.ONLINE : Device2.PresenceState.OFFLINE;
              }
              return {
                deviceKey: key,
                kind: this._identityManager.identity?.deviceKey.equals(key) ? DeviceKind2.CURRENT : DeviceKind2.TRUSTED,
                profile,
                presence
              };
            })
          });
        }
      };
      let identitySubscribed = false;
      let presenceSubscribed = false;
      const subscribeIdentity = () => {
        if (!identitySubscribed) {
          this._identityManager.identity?.stateUpdate.on(() => {
            update();
          });
          identitySubscribed = true;
        }
      };
      const subscribePresence = () => {
        if (!presenceSubscribed) {
          this._identityManager.identity?.presence?.updated.on(() => {
            update();
          });
          presenceSubscribed = true;
        }
      };
      const subscriptions = new SubscriptionList3();
      if (this._identityManager.identity) {
        subscribeIdentity();
        subscribePresence();
      }
      subscriptions.add(this._identityManager.stateUpdate.on(() => {
        update();
        if (this._identityManager.identity) {
          subscribeIdentity();
          subscribePresence();
        }
      }));
      update();
      return () => subscriptions.clear();
    });
  }
};

// src/packlets/identity/contacts-service.ts
import { SubscriptionList as SubscriptionList4, UpdateScheduler as UpdateScheduler2, scheduleTask as scheduleTask10 } from "@dxos/async";
import { Stream as Stream12 } from "@dxos/codec-protobuf/stream";
import { PublicKey as PublicKey13 } from "@dxos/keys";
import { ComplexMap as ComplexMap5, ComplexSet as ComplexSet6 } from "@dxos/util";
var ContactsServiceImpl = class {
  _identityManager;
  _spaceManager;
  _dataSpaceManagerProvider;
  constructor(_identityManager, _spaceManager, _dataSpaceManagerProvider) {
    this._identityManager = _identityManager;
    this._spaceManager = _spaceManager;
    this._dataSpaceManagerProvider = _dataSpaceManagerProvider;
  }
  async getContacts() {
    const identity = this._identityManager.identity;
    if (identity == null) {
      return {
        contacts: []
      };
    }
    const contacts = [
      ...this._spaceManager.spaces.values()
    ].flatMap((s) => [
      ...s.spaceState.members.values()
    ].map((m) => [
      s.key,
      m
    ])).reduce((acc, v) => {
      const [spaceKey, memberInfo] = v;
      if (memberInfo.key.equals(identity.identityKey)) {
        return acc;
      }
      const existing = acc.get(memberInfo.key);
      if (existing != null) {
        existing.profile ??= memberInfo.profile;
        existing.commonSpaces?.push(spaceKey);
      } else {
        acc.set(memberInfo.key, {
          identityKey: memberInfo.key,
          profile: memberInfo.profile,
          commonSpaces: [
            spaceKey
          ]
        });
      }
      return acc;
    }, new ComplexMap5(PublicKey13.hash));
    return {
      contacts: [
        ...contacts.values()
      ]
    };
  }
  queryContacts() {
    const subscribedSpaceKeySet = new ComplexSet6(PublicKey13.hash);
    return new Stream12(({ next, ctx }) => {
      const pushUpdateTask = new UpdateScheduler2(ctx, async () => {
        const contacts = await this.getContacts();
        next(contacts);
      }, {
        maxFrequency: 2
      });
      scheduleTask10(ctx, async () => {
        const subscriptions = new SubscriptionList4();
        ctx.onDispose(() => subscriptions.clear());
        const subscribeToSpaceAndUpdate = () => {
          const oldSetSize = subscribedSpaceKeySet.size;
          for (const space of this._spaceManager.spaces.values()) {
            if (!subscribedSpaceKeySet.has(space.key)) {
              subscriptions.add(space.stateUpdate.on(ctx, () => pushUpdateTask.trigger()));
              subscribedSpaceKeySet.add(space.key);
            }
          }
          if (oldSetSize !== subscribedSpaceKeySet.size) {
            pushUpdateTask.trigger();
          }
        };
        const unsubscribe = (await this._dataSpaceManagerProvider()).updated.on(ctx, subscribeToSpaceAndUpdate);
        ctx.onDispose(unsubscribe);
        subscribeToSpaceAndUpdate();
      });
    });
  }
};

// src/packlets/logging/logging-service.ts
import { Event as Event10 } from "@dxos/async";
import { Stream as Stream13 } from "@dxos/codec-protobuf/stream";
import { PublicKey as PublicKey14 } from "@dxos/keys";
import { log as log25 } from "@dxos/log";
import { QueryLogsRequest } from "@dxos/protocols/proto/dxos/client/services";
import { numericalValues, tracer } from "@dxos/util";
var LoggingServiceImpl = class {
  _logs = new Event10();
  _started = Date.now();
  _sessionId = PublicKey14.random().toHex();
  async open() {
    log25.runtimeConfig.processors.push(this._logProcessor);
  }
  async close() {
    const index = log25.runtimeConfig.processors.findIndex((processor) => processor === this._logProcessor);
    log25.runtimeConfig.processors.splice(index, 1);
  }
  async controlMetrics({ reset, record }) {
    if (reset) {
      tracer.clear();
    }
    if (record === true) {
      tracer.start();
    } else if (record === false) {
      tracer.stop();
    }
    return {
      recording: tracer.recording
    };
  }
  /**
  * @deprecated (Move to diagnostics).
  */
  queryMetrics({ interval = 5e3 }) {
    const getNumericalValues = (key) => {
      const events = tracer.get(key) ?? [];
      return {
        key,
        stats: numericalValues(events, "duration")
      };
    };
    return new Stream13(({ next }) => {
      const update = () => {
        const metrics2 = {
          timestamp: /* @__PURE__ */ new Date(),
          values: [
            getNumericalValues("dxos.echo.pipeline.control"),
            getNumericalValues("dxos.echo.pipeline.data")
          ].filter(Boolean)
        };
        next({
          timestamp: /* @__PURE__ */ new Date(),
          metrics: metrics2
        });
      };
      update();
      const i = setInterval(update, Math.max(interval, 1e3));
      return () => {
        clearInterval(i);
      };
    });
  }
  queryLogs(request) {
    return new Stream13(({ ctx, next }) => {
      const handler = (entry2) => {
        if (LOG_PROCESSING > 0) {
          return;
        }
        if (entry2.meta?.F.includes("logging-service") || entry2.context && Object.values(entry2.context).some((value) => typeof value === "string" && value.includes("LoggingService"))) {
          return;
        }
        if (!shouldLog(entry2, request)) {
          return;
        }
        const { filename, line, context: scopeName } = entry2.computedMeta;
        const recordContext = {
          ...entry2.computedContext
        };
        if (entry2.computedError !== void 0) {
          recordContext.error = entry2.computedError;
        }
        const record = {
          level: entry2.level,
          message: entry2.message ?? entry2.computedError ?? "",
          context: recordContext,
          timestamp: new Date(entry2.timestamp),
          meta: {
            // TODO(dmaretskyi): Fix proto.
            file: filename ?? "",
            line: line ?? 0,
            scope: {
              hostSessionId: this._sessionId,
              uptimeSeconds: (Date.now() - this._started) / 1e3,
              name: scopeName ?? ""
            }
          }
        };
        try {
          LOG_PROCESSING++;
          next(record);
        } finally {
          LOG_PROCESSING--;
        }
      };
      this._logs.on(ctx, handler);
    });
  }
  _logProcessor = (_config, entry2) => {
    this._logs.emit(entry2);
  };
};
var matchFilter = (filter, level, path, options) => {
  switch (options) {
    case QueryLogsRequest.MatchingOptions.INCLUSIVE:
      return level >= filter.level && (!filter.pattern || path.includes(filter.pattern));
    case QueryLogsRequest.MatchingOptions.EXPLICIT:
      return level === filter.level && (!filter.pattern || path.includes(filter.pattern));
  }
};
var shouldLog = (entry2, request) => {
  const options = request.options ?? QueryLogsRequest.MatchingOptions.INCLUSIVE;
  if (request.filters === void 0) {
    return options === QueryLogsRequest.MatchingOptions.INCLUSIVE;
  } else {
    return request.filters.some((filter) => matchFilter(filter, entry2.level, entry2.meta?.F ?? "", options));
  }
};
var LOG_PROCESSING = 0;

// src/packlets/network/network-service.ts
import { Stream as Stream14 } from "@dxos/codec-protobuf/stream";
import { Context as Context12 } from "@dxos/context";
var __dxlog_file32 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/network/network-service.ts";
var NetworkServiceImpl = class {
  networkManager;
  signalManager;
  edgeConnection;
  constructor(networkManager, signalManager, edgeConnection) {
    this.networkManager = networkManager;
    this.signalManager = signalManager;
    this.edgeConnection = edgeConnection;
  }
  queryStatus() {
    return new Stream14(({ ctx, next }) => {
      const update = () => {
        next({
          swarm: this.networkManager.connectionState,
          connectionInfo: this.networkManager.connectionLog?.swarms,
          signaling: this.signalManager.getStatus?.().map(({ host, state }) => ({
            server: host,
            state
          }))
        });
      };
      this.networkManager.connectionStateChanged.on(ctx, () => update());
      this.signalManager.statusChanged?.on(ctx, () => update());
      update();
    });
  }
  async updateConfig(request) {
    await this.networkManager.setConnectionState(request.swarm);
  }
  async joinSwarm(request, options) {
    return this.signalManager.join(options?.ctx ?? Context12.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file32, L: 36 }), request);
  }
  async leaveSwarm(request, options) {
    return this.signalManager.leave(options?.ctx ?? Context12.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file32, L: 39 }), request);
  }
  async querySwarm(request, options) {
    return this.signalManager.query(options?.ctx ?? Context12.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file32, L: 42 }), request);
  }
  subscribeSwarmState(request) {
    return new Stream14(({ ctx, next }) => {
      this.signalManager.swarmState?.on(ctx, (state) => {
        if (request.topic.equals(state.swarmKey)) {
          next(state);
        }
      });
    });
  }
  async sendMessage(message, options) {
    return this.signalManager.sendMessage(options?.ctx ?? Context12.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file32, L: 54 }), message);
  }
  subscribeMessages(peer) {
    return new Stream14(({ ctx, next }) => {
      this.signalManager.onMessage.on(ctx, (message) => {
        if (message.recipient.peerKey === peer.peerKey) {
          next(message);
        }
      });
    });
  }
};

// src/packlets/system/system-service.ts
import { Stream as Stream15 } from "@dxos/codec-protobuf/stream";
import { GetDiagnosticsRequest as GetDiagnosticsRequest2 } from "@dxos/protocols/proto/dxos/client/services";
import { jsonKeyReplacer as jsonKeyReplacer2 } from "@dxos/util";
var SystemServiceImpl = class {
  _config;
  _statusUpdate;
  _getCurrentStatus;
  _onUpdateStatus;
  _onReset;
  _getDiagnostics;
  constructor({ config, statusUpdate, getDiagnostics, onUpdateStatus, getCurrentStatus, onReset }) {
    this._config = config;
    this._statusUpdate = statusUpdate;
    this._getCurrentStatus = getCurrentStatus;
    this._getDiagnostics = getDiagnostics;
    this._onUpdateStatus = onUpdateStatus;
    this._onReset = onReset;
  }
  async getConfig() {
    return (await this._config?.())?.values ?? {};
  }
  /**
  * NOTE: Since this is serialized as a JSON object, we allow the option to serialize keys.
  */
  async getDiagnostics({ keys } = {}) {
    const diagnostics = await this._getDiagnostics();
    return {
      timestamp: /* @__PURE__ */ new Date(),
      diagnostics: JSON.parse(JSON.stringify(diagnostics, jsonKeyReplacer2({
        truncate: keys === GetDiagnosticsRequest2.KEY_OPTION.TRUNCATE,
        humanize: keys === GetDiagnosticsRequest2.KEY_OPTION.HUMANIZE
      })))
    };
  }
  async getPlatform() {
    return getPlatform();
  }
  async updateStatus({ status }) {
    await this._onUpdateStatus(status);
  }
  // TODO(burdon): Standardize interval option in stream request?
  queryStatus({ interval = 3e3 } = {}) {
    return new Stream15(({ next }) => {
      const update = () => {
        next({
          status: this._getCurrentStatus()
        });
      };
      update();
      const unsubscribe = this._statusUpdate.on(() => update());
      const i = setInterval(update, interval);
      return () => {
        clearInterval(i);
        unsubscribe();
      };
    });
  }
  async reset() {
    await this._onReset();
  }
};

// src/packlets/services/service-host.ts
var __dxlog_file33 = "/__w/dxos/dxos/packages/sdk/client-services/src/packlets/services/service-host.ts";
function _ts_decorate12(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var ClientServicesHost = class {
  _resourceLock;
  _serviceRegistry;
  _systemService;
  _loggingService;
  _statusUpdate = new Event11();
  _config;
  _signalManager;
  _networkManager;
  _callbacks;
  _devtoolsProxy;
  _edgeConnection = void 0;
  _edgeHttpClient = void 0;
  _serviceContext;
  _runtime;
  _runtimeProps;
  diagnosticsBroadcastHandler;
  _opening = false;
  _open = false;
  _resetting = false;
  constructor({
    config,
    transportFactory,
    signalManager,
    // TODO(wittjosiah): Turn this on by default.
    lockKey,
    callbacks,
    runtime,
    runtimeProps
  }) {
    this._callbacks = callbacks;
    this._runtime = runtime;
    this._runtimeProps = runtimeProps ?? {};
    if (config) {
      this.initialize({
        config,
        transportFactory,
        signalManager
      });
    }
    if (lockKey) {
      this._resourceLock = new locks_exports.Lock({
        lockKey,
        onAcquire: () => {
          if (!this._opening) {
            void this.open(new Context13(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 77 }));
          }
        },
        onRelease: () => this.close(Context13.default(void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 80 }))
      });
    }
    this._systemService = new SystemServiceImpl({
      config: () => this._config,
      statusUpdate: this._statusUpdate,
      getCurrentStatus: () => this.isOpen && !this._resetting ? SystemStatus.ACTIVE : SystemStatus.INACTIVE,
      getDiagnostics: () => {
        return createDiagnostics(this._serviceRegistry.services, this._serviceContext, this._config);
      },
      onUpdateStatus: async (status) => {
        if (!this.isOpen && status === SystemStatus.ACTIVE) {
          await this._resourceLock?.acquire();
        } else if (this.isOpen && status === SystemStatus.INACTIVE) {
          await this._resourceLock?.release();
        }
      },
      onReset: async () => {
        await this.reset();
      }
    });
    this.diagnosticsBroadcastHandler = (0, diagnostics_exports.createCollectDiagnosticsBroadcastHandler)(this._systemService);
    this._loggingService = new LoggingServiceImpl();
    this._serviceRegistry = new ServiceRegistry(clientServiceBundle, {
      SystemService: this._systemService
    });
  }
  get isOpen() {
    return this._open;
  }
  get config() {
    return this._config;
  }
  get context() {
    return this._serviceContext;
  }
  get serviceRegistry() {
    return this._serviceRegistry;
  }
  get descriptors() {
    return this._serviceRegistry.descriptors;
  }
  get services() {
    return this._serviceRegistry.services;
  }
  /**
  * Debugging util.
  */
  async exportSqliteDatabase() {
    return await RuntimeProvider4.runPromise(this._runtime)(Effect4.gen(function* () {
      const sql = yield* SqlExport.SqlExport;
      return yield* sql.export;
    }));
  }
  /**
  * Debugging util.
  */
  async runSqliteQuery(query, params) {
    return await RuntimeProvider4.runPromise(this._runtime)(Effect4.gen(function* () {
      const sql = yield* SqlClient3.SqlClient;
      return yield* sql`${sql.unsafe(query, params)}`;
    }));
  }
  /**
  * Initialize the service host with the config.
  * Config can also be provided in the constructor.
  * Can only be called once.
  */
  initialize({ config, ...options }) {
    invariant26(!this._open, "service host is open", { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 147, S: this, A: ["!this._open", "'service host is open'"] });
    log26("initializing...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 148, S: this });
    if (config) {
      if (this._runtimeProps.disableP2pReplication === void 0) {
        this._runtimeProps.disableP2pReplication = config?.get("runtime.client.disableP2pReplication", false);
      }
      if (this._runtimeProps.enableVectorIndexing === void 0) {
        this._runtimeProps.enableVectorIndexing = config?.get("runtime.client.enableVectorIndexing", false);
      }
      invariant26(!this._config, "config already set", { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 156, S: this, A: ["!this._config", "'config already set'"] });
      this._config = config;
    }
    if (!options.signalManager) {
    }
    const endpoint = config?.get("runtime.services.edge.url");
    if (endpoint) {
      const clientTag = resolveTelemetryTag(config);
      this._edgeConnection = new EdgeClient(createStubEdgeIdentity(), {
        socketEndpoint: endpoint,
        clientTag
      });
      this._edgeHttpClient = new EdgeHttpClient(endpoint, {
        clientTag
      });
    }
    const { connectionLog = true, transportFactory = createRtcTransportFactory({
      iceServers: this._config?.get("runtime.services.ice")
    }, this._config?.get("runtime.services.iceProviders") && createIceProvider(this._config.get("runtime.services.iceProviders"))), signalManager = this._edgeConnection && this._config?.get("runtime.client.edgeFeatures")?.signaling ? new EdgeSignalManager({
      edgeConnection: this._edgeConnection
    }) : new WebsocketSignalManager(this._config?.get("runtime.services.signaling") ?? []) } = options;
    this._signalManager = signalManager;
    invariant26(!this._networkManager, "network manager already set", { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 180, S: this, A: ["!this._networkManager", "'network manager already set'"] });
    this._networkManager = new SwarmNetworkManager({
      enableDevtoolsLogging: connectionLog,
      transportFactory,
      signalManager,
      peerInfo: this._edgeConnection ? {
        identityDid: this._edgeConnection.identityDid,
        peerKey: this._edgeConnection.peerKey
      } : void 0
    });
    log26("initialized", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 190, S: this });
  }
  async open(ctx) {
    if (this._open) {
      return;
    }
    log26("opening service host", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 196, S: this });
    invariant26(this._config, "config not set", { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 197, S: this, A: ["this._config", "'config not set'"] });
    invariant26(this._signalManager, "signal manager not set", { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 198, S: this, A: ["this._signalManager", "'signal manager not set'"] });
    invariant26(this._networkManager, "network manager not set", { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 199, S: this, A: ["this._networkManager", "'network manager not set'"] });
    this._opening = true;
    log26("opening...", {
      lockKey: this._resourceLock?.lockKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 201, S: this });
    await this._resourceLock?.acquire();
    await this._loggingService.open();
    this._serviceContext = new ServiceContext(this._networkManager, this._signalManager, this._edgeConnection, this._edgeHttpClient, this._runtime, this._runtimeProps, this._config.get("runtime.client.edgeFeatures"));
    const dataSpaceManagerProvider = async () => {
      await this._serviceContext.initialized.wait();
      return this._serviceContext.dataSpaceManager;
    };
    const agentManagerProvider = async () => {
      await this._serviceContext.initialized.wait();
      return this._serviceContext.edgeAgentManager;
    };
    const identityService = new IdentityServiceImpl(this._serviceContext.identityManager, this._serviceContext.recoveryManager, this._serviceContext.keyring, (params, ctx2) => this._createIdentity(params, ctx2), (profile) => this._serviceContext.broadcastProfileUpdate(profile));
    this._serviceRegistry.setServices({
      SystemService: this._systemService,
      IdentityService: identityService,
      ContactsService: new ContactsServiceImpl(this._serviceContext.identityManager, this._serviceContext.spaceManager, dataSpaceManagerProvider),
      InvitationsService: new InvitationsServiceImpl(this._serviceContext.invitationsManager),
      DevicesService: new DevicesServiceImpl(this._serviceContext.identityManager, this._edgeConnection),
      SpacesService: new SpacesServiceImpl(this._serviceContext.identityManager, this._serviceContext.spaceManager, this._serviceContext.echoHost, dataSpaceManagerProvider),
      DataService: this._serviceContext.echoHost.dataService,
      QueryService: this._serviceContext.echoHost.queryService,
      QueueService: this._serviceContext.echoHost.queuesService,
      NetworkService: new NetworkServiceImpl(this._serviceContext.networkManager, this._serviceContext.signalManager, this._edgeConnection),
      LoggingService: this._loggingService,
      // TODO(burdon): Move to new protobuf definitions.
      DevtoolsHost: new DevtoolsServiceImpl({
        events: new DevtoolsHostEvents(),
        config: this._config,
        context: this._serviceContext,
        exportSqliteDatabase: () => this.exportSqliteDatabase(),
        runSqliteQuery: (query, params) => this.runSqliteQuery(query, params)
      }),
      EdgeAgentService: new EdgeAgentServiceImpl(agentManagerProvider, this._edgeConnection)
    });
    log26("service-host: opening service context...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 238, S: this });
    await this._serviceContext.open(ctx);
    log26("service-host: service context opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 240, S: this });
    log26("service-host: opening identity service...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 241, S: this });
    await identityService.open();
    log26("service-host: identity service opened", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 243, S: this });
    const devtoolsProxy = this._config?.get("runtime.client.devtoolsProxy");
    if (devtoolsProxy) {
      this._devtoolsProxy = new WebsocketRpcClient({
        url: devtoolsProxy,
        requested: {},
        exposed: clientServiceBundle,
        handlers: this.services
      });
      void this._devtoolsProxy.open();
    }
    this.diagnosticsBroadcastHandler.start();
    this._opening = false;
    this._open = true;
    this._statusUpdate.emit();
    const deviceKey = this._serviceContext.identityManager.identity?.deviceKey;
    log26("opened", {
      deviceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 259, S: this });
  }
  async close(ctx) {
    if (!this._open) {
      return;
    }
    const deviceKey = this._serviceContext.identityManager.identity?.deviceKey;
    log26("closing...", {
      deviceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 268, S: this });
    this.diagnosticsBroadcastHandler.stop();
    await this._devtoolsProxy?.close();
    this._serviceRegistry.setServices({
      SystemService: this._systemService
    });
    await this._loggingService.close();
    await this._serviceContext.close();
    this._open = false;
    this._statusUpdate.emit();
    log26("closed", {
      deviceKey
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 280, S: this });
  }
  async reset() {
    log26.info("resetting...", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 285, S: this });
    this._resetting = true;
    this._statusUpdate.emit();
    await this._serviceContext?.close();
    await RuntimeProvider4.runPromise(this._runtime)(Effect4.gen(function* () {
      const sql = yield* SqlClient3.SqlClient;
      yield* sql`DELETE FROM space_metadata`;
      yield* sql`DELETE FROM space_large`;
      yield* sql`DELETE FROM blobs_meta`;
      yield* sql`DELETE FROM blobs_data`;
      yield* sql`DELETE FROM keyring`;
      yield* sql`DELETE FROM automerge_chunks`;
      yield* sql`DELETE FROM automerge_heads`;
      yield* sql`DELETE FROM hypercore_files`;
      yield* sql`DELETE FROM feeds`;
      yield* sql`DELETE FROM blocks`;
      yield* sql`DELETE FROM subscriptions`;
      yield* sql`DELETE FROM cursor_tokens`;
      yield* sql`DELETE FROM sync_state`;
      yield* sql`DELETE FROM indexCursor`;
      yield* sql`DELETE FROM objectMeta`;
      yield* sql`DELETE FROM reverseRef`;
      yield* sql`DELETE FROM ftsIndex`;
    }));
    log26.info("reset", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file33, L: 319, S: this });
    await this._callbacks?.onReset?.();
  }
  async _createIdentity(params, ctx) {
    const identity = await this._serviceContext.createIdentity(params, ctx);
    await this._serviceContext.initialized.wait();
    return identity;
  }
};
_ts_decorate12([
  Trace3.info()
], ClientServicesHost.prototype, "_opening", void 0);
_ts_decorate12([
  Trace3.info()
], ClientServicesHost.prototype, "_open", void 0);
_ts_decorate12([
  Trace3.info()
], ClientServicesHost.prototype, "_resetting", void 0);
_ts_decorate12([
  synchronized4,
  Trace3.span()
], ClientServicesHost.prototype, "open", null);
_ts_decorate12([
  synchronized4,
  Trace3.span()
], ClientServicesHost.prototype, "close", null);
ClientServicesHost = _ts_decorate12([
  Trace3.resource()
], ClientServicesHost);

export {
  subscribeToFeeds,
  subscribeToFeedBlocks,
  subscribeToNetworkStatus,
  subscribeToSignal,
  subscribeToNetworkTopics,
  subscribeToSwarmInfo,
  getNetworkPeers,
  subscribeToSpaces,
  DevtoolsHostEvents,
  DevtoolsServiceImpl,
  createDiagnostics,
  DiagnosticsCollector,
  diagnostics_exports,
  createAuthProvider,
  TrustedKeySetAuthVerifier,
  EdgeFeedReplicator,
  DataSpace,
  DataSpaceManager,
  detectSpaceArchiveFormat,
  readSerializedSpaceArchive,
  objJsonToObjectStructure,
  buildDatabaseDirectoryFromObjects,
  orderObjJsonFields,
  writeSerializedSpaceArchive,
  objectStructureToObjJson,
  extractSpaceArchive,
  SpaceArchiveWriter,
  SpacesServiceImpl,
  Identity,
  IdentityManager,
  IdentityServiceImpl,
  DeviceInvitationProtocol,
  InvitationsHandler,
  createAdmissionKeypair,
  InvitationsServiceImpl,
  SpaceInvitationProtocol,
  InvitationsManager,
  locks_exports,
  FeedSyncer,
  EdgeAgentManager,
  EdgeAgentServiceImpl,
  SqliteStorage,
  ServiceContext,
  ServiceRegistry,
  ClientServicesHost
};
//# sourceMappingURL=chunk-XU5R2BMF.mjs.map
